import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { USER_JOURNEY_EVENT_TYPES, EVENT_ENUM_TO_DISPLAY } from './userJourneyEventTypes';
import { reconcileSubscriptionFields, isCouponTrialActive } from './userProfile';
import './ReelsAdminPage.css';

/** True if session includes Generate Shorts button (enum key or display name from API). */
const sessionHasGenerateShortsClick = (session) => {
  if (!session) return false;
  const types = session.event_types || session.eventTypes || [];
  const target = EVENT_ENUM_TO_DISPLAY.GENERATE_SHORTS_BUTTON;
  return types.some((name) => {
    if (name == null || String(name).trim() === '') return false;
    const raw = String(name).trim();
    const normalized = EVENT_ENUM_TO_DISPLAY[raw] || raw;
    return normalized === target;
  });
};

/** Task has per-clip Lambda render metadata (same condition as task-lambda-inline in details). */
const hasTaskLambdaMeta = (task) =>
  task &&
  task.remotion &&
  typeof task.remotion === 'object' &&
  Object.keys(task.remotion).length > 0;

/** Parse one Remotion cost line, e.g. "12 λ's used, 34.74sec, Estimated cost $0.005" */
/** Razorpay webhook `acquirer_data` — short display + full JSON for title/tooltip. */
const summarizeWebhookAcquirerData = (data) => {
  if (data == null || typeof data !== 'object') return { short: '—', full: '' };
  try {
    const full = JSON.stringify(data);
    const short = full.length > 88 ? `${full.slice(0, 88)}…` : full;
    return { short, full };
  } catch (_) {
    return { short: '—', full: '' };
  }
};

/** Client-side search: concatenate all row values (objects JSON-stringified) for substring match. */
const buildRazorpayWebhookRowSearchText = (row) => {
  if (!row || typeof row !== 'object') return '';
  const parts = [];
  for (const key of Object.keys(row)) {
    const v = row[key];
    if (v == null || v === '') continue;
    if (typeof v === 'object') {
      try {
        parts.push(JSON.stringify(v));
      } catch (_) {
        parts.push(String(v));
      }
    } else {
      parts.push(String(v));
    }
  }
  return parts.join(' \n ').toLowerCase();
};

const parseLambdaClipSummary = (summary) => {
  if (summary == null || typeof summary !== 'string') {
    return { lambdas: null, seconds: null, costDollars: null };
  }
  const s = summary.trim();
  let lambdas = null;
  const mL = s.match(/(\d+)\s*\u03bb['\u2019]s\s+used/i);
  if (mL) lambdas = parseInt(mL[1], 10);
  let seconds = null;
  const mS = s.match(/([\d.]+)\s*sec\b/i);
  if (mS) {
    const v = parseFloat(mS[1]);
    if (!Number.isNaN(v)) seconds = v;
  }
  let costDollars = null;
  const mC = s.match(/Estimated\s+cost\s*\$([\d.]+)/i);
  if (mC) {
    const v = parseFloat(mC[1]);
    if (!Number.isNaN(v)) costDollars = v;
  }
  return { lambdas, seconds, costDollars };
};

const formatLambdaCostUSD = (n) => {
  if (n == null || Number.isNaN(n)) return '';
  const t = n.toFixed(4).replace(/\.?0+$/, '');
  return t || '0';
};

/** Display streak count (align with app /api/auth/user: min 1 if last_date exists). */
const getDisplayStreakCount = (user) => {
  if (!user) return 0;
  let count = parseInt(user.creator_streak_count, 10);
  if (Number.isNaN(count)) count = 0;
  if (count < 1 && user.creator_streak_last_date) return 1;
  return Math.max(0, count);
};

/** User journey: "Streak Overlay Shown" streak_from / streak_to → "4 to 5". */
const formatStreakOverlayRange = (event) => {
  if (!event) return null;
  const fromNum = Number(event.streak_from);
  const toNum = Number(event.streak_to);
  const hasFrom = event.streak_from != null && event.streak_from !== '' && Number.isFinite(fromNum);
  const hasTo = event.streak_to != null && event.streak_to !== '' && Number.isFinite(toNum);
  if (!hasFrom && !hasTo) return null;
  if (hasFrom && hasTo) return `${fromNum} to ${toNum}`;
  if (hasFrom) return `${fromNum} to ?`;
  return `? to ${toNum}`;
};

const hasPushSubscription = (user) => {
  if (user?.push_status === 'granted') return true;
  const sub = user?.push_subscription;
  return Boolean(sub && typeof sub === 'object' && sub.endpoint);
};

const SCHEDULED_POST_STATUSES = ['pending', 'scheduled', 'publishing', 'published', 'failed', 'cancelled'];
const SCHEDULED_POST_PLATFORMS = ['youtube', 'instagram', 'facebook', 'linkedin'];

const getScheduledPostPlatformUrl = (post) => {
  if (!post) return '';
  const platform = post.platform || 'youtube';
  const payload = post[platform];
  if (!payload || typeof payload !== 'object') return '';
  return payload.permalink || payload.url || '';
};

const canCancelScheduledPost = (post) =>
  post?.status === 'pending' || post?.status === 'failed';

const hasSocialHandle = (user) =>
  Boolean(
    String(user?.instagram_handle || '').trim() ||
    String(user?.youtube_handle || '').trim()
  );

const CONNECTED_PLATFORM_META = {
  youtube: { short: 'YT', label: 'YouTube' },
  instagram: { short: 'IG', label: 'Instagram' },
  facebook: { short: 'FB', label: 'Facebook' },
  linkedin: { short: 'LI', label: 'LinkedIn' },
};

const getUserConnectedPlatforms = (userId, connectedAccountsByUser) => {
  const entry = connectedAccountsByUser?.[userId];
  if (!entry) return [];
  return Array.isArray(entry.platforms) ? entry.platforms : [];
};

const hasConnectedAccount = (userId, connectedAccountsByUser) =>
  getUserConnectedPlatforms(userId, connectedAccountsByUser).length > 0;

const ADMIN_VIDEO_EXT = ['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi'];
const ADMIN_IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];

const getAdminFileKind = (fileName) => {
  if (!fileName) return 'other';
  const lower = String(fileName).toLowerCase();
  if (ADMIN_VIDEO_EXT.some((ext) => lower.endsWith(ext))) return 'video';
  if (ADMIN_IMAGE_EXT.some((ext) => lower.endsWith(ext))) return 'image';
  return 'other';
};

const makeFileDownloadKey = (fileType, filePath) => `${fileType}:${filePath}`;

/** Stream admin file download and report progress when Content-Length is available. */
async function fetchAdminFileBlobWithProgress(url, headers, onProgress) {
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = new Error('Download failed');
    err.status = response.status;
    throw err;
  }

  const contentLength = response.headers.get('Content-Length');
  const total = contentLength ? Number(contentLength) : 0;
  const hasTotal = Number.isFinite(total) && total > 0;

  if (!response.body) {
    const blob = await response.blob();
    onProgress({
      percent: 100,
      loaded: blob.size,
      total: hasTotal ? total : blob.size,
      indeterminate: false,
      fileName: '',
    });
    return blob;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    if (hasTotal) {
      onProgress({
        percent: Math.min(100, Math.round((loaded / total) * 100)),
        loaded,
        total,
        indeterminate: false,
        fileName: '',
      });
    } else {
      onProgress({
        percent: null,
        loaded,
        total: 0,
        indeterminate: true,
        fileName: '',
      });
    }
  }

  onProgress({
    percent: 100,
    loaded,
    total: hasTotal ? total : loaded,
    indeterminate: false,
    fileName: '',
  });
  return new Blob(chunks);
}

function AdminFileDownloadButton({ filePath, fileType, fileName, onDownload, fileDownloadProgress }) {
  const key = makeFileDownloadKey(fileType, filePath);
  const prog = fileDownloadProgress[key];
  const isDownloading = Boolean(prog);
  const title = isDownloading
    ? prog.indeterminate
      ? `Downloading ${fileName || 'file'}…`
      : `Downloading ${fileName || 'file'} — ${prog.percent}%`
    : 'Download';

  return (
    <button
      type="button"
      onClick={() => onDownload(filePath, fileType, fileName)}
      className={`btn-action btn-download${isDownloading ? ' btn-download--progress' : ''}`}
      disabled={isDownloading}
      title={title}
      aria-label={title}
    >
      {isDownloading ? (
        <span className="btn-download-progress-label">
          {prog.indeterminate ? '…' : `${prog.percent}%`}
        </span>
      ) : (
        <span>⬇️</span>
      )}
    </button>
  );
}

function AdminFileDownloadProgressOverlay({ fileType, filePath, fileDownloadProgress }) {
  const prog = fileDownloadProgress[makeFileDownloadKey(fileType, filePath)];
  if (!prog) return null;
  return (
    <div className="admin-file-download-overlay" aria-live="polite">
      <span className="admin-file-download-overlay-label">
        {prog.indeterminate ? 'Downloading…' : `${prog.percent}%`}
      </span>
      {!prog.indeterminate && (
        <div className="admin-file-download-overlay-bar">
          <div className="admin-file-download-overlay-bar-fill" style={{ width: `${prog.percent}%` }} />
        </div>
      )}
    </div>
  );
}

/** Per-customer file section: all files as thumbnail tiles (Input / Output / Archive). */
function CustomerFileThumbnailGrid({
  files,
  fileType,
  streamToken,
  userId,
  selectedFileKeys,
  makeFileSelectionKey,
  toggleFileSelection,
  batchDeletingFiles,
  openStreamPreview,
  handleDownload,
  formatFileSize,
  fileDownloadProgress,
}) {
  if (!files?.length) return null;
  return (
    <div className="customer-files-thumb-grid">
      {files.map((file, idx) => {
        const kind = getAdminFileKind(file.name);
        const thumbUrl =
          streamToken && file.path && kind !== 'other'
            ? `/api/admin/file/thumbnail?${new URLSearchParams({
                file_path: file.path,
                file_type: fileType,
                token: streamToken,
              }).toString()}`
            : '';
        const selKey = makeFileSelectionKey(fileType, file.path);
        const isDownloading = Boolean(fileDownloadProgress[makeFileDownloadKey(fileType, file.path)]);
        const onPick = () => {
          if (isDownloading) return;
          if (kind === 'video') {
            openStreamPreview(file.path, fileType, file.name, 'video', userId);
          } else if (kind === 'image') {
            openStreamPreview(file.path, fileType, file.name, 'image', userId);
          } else {
            handleDownload(file.path, fileType, file.name);
          }
        };
        return (
          <div key={`thumb-${fileType}-${file.path}-${idx}`} className="customer-file-thumb-tile">
            <label
              className="customer-file-thumb-checkbox"
              onClick={(e) => e.stopPropagation()}
              title={`Select ${file.name}`}
            >
              <input
                type="checkbox"
                className="admin-file-checkbox admin-file-checkbox--thumb"
                checked={selectedFileKeys.has(selKey)}
                onChange={() => toggleFileSelection(fileType, file.path)}
                disabled={batchDeletingFiles}
                aria-label={`Select ${file.name}`}
              />
            </label>
            <button
              type="button"
              className="customer-file-thumb-btn"
              onClick={onPick}
              title={file.name}
              disabled={isDownloading}
            >
              <div className="customer-file-thumb-image-wrap">
                {thumbUrl ? (
                  <img
                    src={thumbUrl}
                    alt=""
                    loading="lazy"
                    className="customer-file-thumb-image"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="customer-file-thumb-fallback">
                    {kind === 'other' ? file.name?.slice(0, 12) || 'File' : '…'}
                  </span>
                )}
                <AdminFileDownloadProgressOverlay
                  fileType={fileType}
                  filePath={file.path}
                  fileDownloadProgress={fileDownloadProgress}
                />
              </div>
              <div className="customer-file-thumb-meta">
                <div className="customer-file-thumb-name">{file.name}</div>
                <div className="customer-file-thumb-size">{formatFileSize(file.size)}</div>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

const formatSocialHandleTooltip = (user) => {
  const parts = [];
  const ig = String(user?.instagram_handle || '').trim();
  const yt = String(user?.youtube_handle || '').trim();
  if (ig) parts.push(`Instagram: ${ig}`);
  if (yt) parts.push(`YouTube: ${yt}`);
  return parts.length ? parts.join(' · ') : 'Social handle on file';
};

/** Merge admin scheduled posts lists (recent page + load remaining). */
const mergeScheduledPostsLists = (base, extra) => {
  if (!Array.isArray(base)) return Array.isArray(extra) ? [...extra] : [];
  if (!Array.isArray(extra) || extra.length === 0) return base;
  const seen = new Set(base.map((post) => post.id).filter(Boolean));
  const merged = [...base];
  extra.forEach((post) => {
    if (post?.id && !seen.has(post.id)) {
      merged.push(post);
      seen.add(post.id);
    }
  });
  return merged;
};

/** Merge admin tasks API payloads (recent page + load remaining). */
const mergeAdminTasksPayload = (base, extra) => {
  if (!base) return extra;
  if (!extra) return base;
  const tasks_by_user = { ...(base.tasks_by_user || {}) };
  Object.entries(extra.tasks_by_user || {}).forEach(([uid, list]) => {
    const existing = tasks_by_user[uid] ? [...tasks_by_user[uid]] : [];
    const seen = new Set(existing.map((t) => t.task_id));
    list.forEach((t) => {
      if (!seen.has(t.task_id)) {
        existing.push(t);
        seen.add(t.task_id);
      }
    });
    tasks_by_user[uid] = existing;
  });
  const total_tasks = Object.values(tasks_by_user).reduce((n, arr) => n + arr.length, 0);
  return {
    ...base,
    tasks_by_user,
    total_tasks,
    total_users: Object.keys(tasks_by_user).length,
    has_more: false,
    partial: false,
  };
};

const collapsedTaskIdsFromPayload = (tasksData) => {
  const allTaskIds = new Set();
  Object.values(tasksData?.tasks_by_user || {}).forEach((userTasks) => {
    userTasks.forEach((task) => allTaskIds.add(task.task_id));
  });
  return allTaskIds;
};

/** Map login user cache row to marketing list shape. */
const normalizeMarketingUserFromCache = (user) => {
  const phone = (user.phone_number || '').trim();
  return {
    ...user,
    has_phone_number: !!phone,
    last_marketing_time: user.last_marketing_time ?? user.last_message_template_sent,
  };
};

/** Same rules as GET /api/admin/marketing/users?channel=… */
const filterUsersForMarketingChannel = (users, channel) => {
  const ch = channel === 'web_push' ? 'web_push' : 'whatsapp';
  return (users || [])
    .map(normalizeMarketingUserFromCache)
    .filter((user) => {
      if (ch === 'web_push') {
        return user.push_status === 'granted' || hasPushSubscription(user);
      }
      return !!(user.phone_number || '').trim();
    });
};

const collectMarketingTemplateNames = (users, channel) => {
  const ch = channel === 'web_push' ? 'web_push' : 'whatsapp';
  const uniqueTemplates = new Set();
  (users || []).forEach((user) => {
    if (ch === 'web_push') {
      (user.web_push_marketing_sent || []).forEach((t) => uniqueTemplates.add(t));
    } else {
      (user.whatsapp_template_sent || []).forEach((t) => uniqueTemplates.add(t));
    }
  });
  return Array.from(uniqueTemplates).sort();
};

/** Short label for push endpoint (avoid dumping full subscription in admin UI). */
const summarizePushSubscription = (user) => {
  if (!hasPushSubscription(user)) return 'No';
  const endpoint = user.push_subscription?.endpoint;
  if (!endpoint || typeof endpoint !== 'string') return 'Yes';
  const trimmed = endpoint.trim();
  if (trimmed.length <= 56) return trimmed;
  return `${trimmed.slice(0, 28)}…${trimmed.slice(-20)}`;
};

/** True when user has coupon metadata but trial period has ended (DB may still say premium_source=coupon). */
const isCouponTrialExpiredMetadata = (user) => {
  if (!user?.coupon_code_applied) return false;
  if (user.coupon_trial_expired_at_iso) return false;
  return !isCouponTrialActive(user);
};

/** Wall-clock effective subscription_status for admin display/filters (does not write Firestore). */
const getEffectiveSubscriptionStatus = (user) => {
  if (!user) return '';
  return reconcileSubscriptionFields(user).subscription_status || '';
};

/** Razorpay cancel webhook: auto-renew off, premium until subscription_current_end_iso. */
const isCancelAtPeriodEndUser = (user) => {
  if (user?.subscription_cancel_at_period_end !== true) return false;
  const effective = getEffectiveSubscriptionStatus(user);
  return effective === 'active' || effective === 'cancelled';
};

/** Sum λ count, seconds, and estimated $ across clips (uses each clip's summary string). */
const aggregateLambdaRemotionTotals = (remotion) => {
  let lambdas = 0;
  let seconds = 0;
  let cost = 0;
  let nL = 0;
  let nS = 0;
  let nC = 0;
  if (!remotion || typeof remotion !== 'object') {
    return { lambdas: null, seconds: null, cost: null };
  }
  Object.values(remotion).forEach((clip) => {
    const p = parseLambdaClipSummary(clip && clip.summary);
    if (p.lambdas != null) {
      lambdas += p.lambdas;
      nL += 1;
    }
    if (p.seconds != null) {
      seconds += p.seconds;
      nS += 1;
    }
    if (p.costDollars != null) {
      cost += p.costDollars;
      nC += 1;
    }
  });
  return {
    lambdas: nL > 0 ? lambdas : null,
    seconds: nS > 0 ? seconds : null,
    cost: nC > 0 ? cost : null,
  };
};

const toYmdLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const enumerateYmdDatesInclusive = (fromYmd, toYmd) => {
  if (!fromYmd || !toYmd) return [];
  const [fy, fm, fd] = fromYmd.split('-').map(Number);
  const [ty, tm, td] = toYmd.split('-').map(Number);
  if (
    [fy, fm, fd, ty, tm, td].some((n) => Number.isNaN(n)) ||
    fm < 1 || fm > 12 || tm < 1 || tm > 12
  ) {
    return [];
  }
  const start = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
  const end = new Date(ty, tm - 1, td, 0, 0, 0, 0);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [];
  }
  const out = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    out.push(toYmdLocal(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

/**
 * Find video processing sessions in raw log text (matches video_service.py session lines).
 * startLine is 0-based inclusive; endLine is exclusive, for scrolling/highlight in the viewer.
 */
const parseLogProcessingTasks = (content) => {
  if (content == null || typeof content !== 'string' || !content.trim()) return [];
  const lines = content.split('\n');
  const sessionRe = /Session started - Task:\s*([^,]+),\s*File:\s*(.+?),\s*User:/;
  const bgRe = /Background processing started for task\s+([^:]+):\s*(.+)$/;
  const raw = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const ms = line.match(sessionRe);
    if (ms) {
      raw.push({ taskId: ms[1].trim(), label: ms[2].trim(), startLine: i });
      continue;
    }
    const mb = line.match(bgRe);
    if (mb) {
      const taskId = mb[1].trim();
      const label = mb[2].trim();
      if (raw.some((t) => t.taskId === taskId && Math.abs(t.startLine - i) <= 3)) continue;
      raw.push({ taskId, label, startLine: i });
    }
  }
  for (let j = 0; j < raw.length; j++) {
    raw[j].endLine = j + 1 < raw.length ? raw[j + 1].startLine : lines.length;
  }
  return raw.map((t, idx) => ({
    ...t,
    key: `${t.taskId}-${t.startLine}-${idx}`,
    displayLabel: t.label.length > 80 ? `${t.label.slice(0, 77)}…` : t.label,
  }));
};

/** Firestore Tasks tab: OS family filter tokens (not stored on tasks). */
const TASK_DEVICE_OS_FAMILY = {
  android: '__family:android__',
  ios: '__family:ios__',
  windows: '__family:windows__',
  mac: '__family:mac__',
};

const taskDeviceOsString = (task) => String(task?.metadata?.device_os ?? '').trim();

const taskDeviceTypeString = (task) => String(task?.metadata?.device_type ?? '').trim();

const matchesTaskDeviceOsFamily = (osLower, family) => {
  if (!osLower) return false;
  switch (family) {
    case 'android':
      return osLower.startsWith('android');
    case 'ios':
      return osLower.startsWith('ios');
    case 'windows':
      return osLower.startsWith('windows');
    case 'mac':
      return osLower.startsWith('mac') || osLower.startsWith('macos');
    default:
      return false;
  }
};

const matchesTaskDeviceOsFilter = (task, filterValue) => {
  if (!filterValue) return true;
  const os = taskDeviceOsString(task).toLowerCase();
  if (!os) return false;
  if (filterValue === TASK_DEVICE_OS_FAMILY.android) return matchesTaskDeviceOsFamily(os, 'android');
  if (filterValue === TASK_DEVICE_OS_FAMILY.ios) return matchesTaskDeviceOsFamily(os, 'ios');
  if (filterValue === TASK_DEVICE_OS_FAMILY.windows) return matchesTaskDeviceOsFamily(os, 'windows');
  if (filterValue === TASK_DEVICE_OS_FAMILY.mac) return matchesTaskDeviceOsFamily(os, 'mac');
  return os === filterValue.trim().toLowerCase();
};

const flattenTasksByUser = (tasksState) => {
  if (!tasksState?.tasks_by_user) return [];
  const out = [];
  Object.values(tasksState.tasks_by_user).forEach((userTasks) => {
    userTasks.forEach((task) => out.push(task));
  });
  return out;
};

const REELS_ADMIN_PAGE_BODY_CLASS = 'reels-admin-page-plain-bg';
const REELS_ADMIN_PAGE_STATE_KEY = 'reels_admin_page_state_v1';

const getSavedAdminPageState = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(REELS_ADMIN_PAGE_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

const ReelsAdminPage = () => {
  useEffect(() => {
    document.body.classList.add(REELS_ADMIN_PAGE_BODY_CLASS);
    return () => document.body.classList.remove(REELS_ADMIN_PAGE_BODY_CLASS);
  }, []);

  const { idToken, isAuthenticated, loading: authLoading, user, getFreshToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = getSavedAdminPageState()?.activeTab;
    return typeof tab === 'string' && tab.trim() ? tab : 'files';
  }); // 'files', 'controls', 'coupons', 'firestore', 'logs', 'razorpay-webhooks', 'survey', 'clip-feedback', 'user-journey', 'youtube-cookies', 'marketing', 'task-queue', 'scheduled-posts'
  
  // YouTube Cookies state
  const [cookies, setCookies] = useState([]);
  const [loadingCookies, setLoadingCookies] = useState(false);
  const [editingCookie, setEditingCookie] = useState(null);
  const [newCookieName, setNewCookieName] = useState('');
  const [newCookieContent, setNewCookieContent] = useState('');
  const [youtubeUrlEnabled, setYoutubeUrlEnabled] = useState(true);
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [chatwootTemplatesEnabled, setChatwootTemplatesEnabled] = useState({
    welcome: true,
    finished: true
  });
  const [loadingChatwootToggle, setLoadingChatwootToggle] = useState(false);
  const [lambdaEnabled, setLambdaEnabled] = useState(false);
  const [loadingLambdaToggle, setLoadingLambdaToggle] = useState(false);
  const [lambdaEnvConfigured, setLambdaEnvConfigured] = useState(true);
  const [lambdaMissingEnvFields, setLambdaMissingEnvFields] = useState([]);
  const [razorpayCheckoutEnabled, setRazorpayCheckoutEnabled] = useState(true);
  const [razorpayEnvEnabled, setRazorpayEnvEnabled] = useState(true);
  const [razorpayMode, setRazorpayMode] = useState('test');
  const [razorpayModeReady, setRazorpayModeReady] = useState(true);
  const [loadingRazorpayToggle, setLoadingRazorpayToggle] = useState(false);
  const EMPTY_PRICING_DISPLAY = {
    in: { price_old: '', price_new: '', period: '' },
    intl: { price_old: '', price_new: '', period: '' },
  };
  const EMPTY_RAZORPAY_PLANS = {
    test: { in: '', intl: '' },
    live: { in: '', intl: '' },
  };
  const [pricingDisplayForm, setPricingDisplayForm] = useState(EMPTY_PRICING_DISPLAY);
  const [pricingDisplayLoaded, setPricingDisplayLoaded] = useState(false);
  const [loadingPricingDisplay, setLoadingPricingDisplay] = useState(false);
  const [savingPricingDisplay, setSavingPricingDisplay] = useState(false);
  const [razorpayPlansForm, setRazorpayPlansForm] = useState(EMPTY_RAZORPAY_PLANS);
  const [razorpayPlansLoaded, setRazorpayPlansLoaded] = useState(false);
  const [loadingRazorpayPlans, setLoadingRazorpayPlans] = useState(false);
  const [savingRazorpayPlans, setSavingRazorpayPlans] = useState(false);
  const [loadingRazorpayMode, setLoadingRazorpayMode] = useState(false);
  const [requirePhoneForDownload, setRequirePhoneForDownload] = useState(true);
  const [loadingPhoneDownloadToggle, setLoadingPhoneDownloadToggle] = useState(false);
  const [pricingInviteCardEnabled, setPricingInviteCardEnabled] = useState(true);
  const [loadingPricingInviteToggle, setLoadingPricingInviteToggle] = useState(false);
  const [trimSilenceFeatureEnabled, setTrimSilenceFeatureEnabled] = useState(true);
  const [loadingTrimSilenceToggle, setLoadingTrimSilenceToggle] = useState(false);
  const [includeBrollDefaultEnabled, setIncludeBrollDefaultEnabled] = useState(true);
  const [loadingBrollDefaultToggle, setLoadingBrollDefaultToggle] = useState(false);
  const [streakPushSchedulerEnabled, setStreakPushSchedulerEnabled] = useState(true);
  const [loadingStreakPushToggle, setLoadingStreakPushToggle] = useState(false);
  const [subscriptionReconcilePreview, setSubscriptionReconcilePreview] = useState(null);
  const [subscriptionReconcileLoading, setSubscriptionReconcileLoading] = useState(false);
  const [subscriptionReconcileApplying, setSubscriptionReconcileApplying] = useState(false);
  const [couponReconcilePreview, setCouponReconcilePreview] = useState(null);
  const [couponReconcileLoading, setCouponReconcileLoading] = useState(false);
  const [couponReconcileApplying, setCouponReconcileApplying] = useState(false);

  // Coupons (Admin)
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponsError, setCouponsError] = useState('');
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    type: 'trial',
    trial_days: 7,
    max_uses: 100,
    expires_at_iso: '',
    expires_at_date: null,
    razorpay_plan_id_in: '',
    razorpay_plan_id_intl: '',
    display_price_old: '',
    display_price_new: '',
    display_period: 'per month',
    display_price_old_intl: '',
    display_price_new_intl: '',
    display_period_intl: '',
  });
  const [editingCouponCode, setEditingCouponCode] = useState('');
  const [editCouponPatch, setEditCouponPatch] = useState({
    is_active: true,
    type: 'trial',
    trial_days: 7,
    max_uses: 100,
    expires_at_iso: '',
    expires_at_date: null,
    razorpay_plan_id_in: '',
    razorpay_plan_id_intl: '',
    display_price_old: '',
    display_price_new: '',
    display_period: '',
    display_price_old_intl: '',
    display_price_new_intl: '',
    display_period_intl: '',
  });

  // Daily content prompts (Admin)
  const [dailyContentPrompts, setDailyContentPrompts] = useState([]);
  const [loadingDailyContentPrompts, setLoadingDailyContentPrompts] = useState(false);
  const [dailyContentPromptsError, setDailyContentPromptsError] = useState('');
  const [creatingDailyContentPrompt, setCreatingDailyContentPrompt] = useState(false);
  const [dailyContentForm, setDailyContentForm] = useState({
    email: '',
    system_prompt: '',
    enabled: true,
    send_hour_local: 8,
  });
  const [editingDailyContentUserId, setEditingDailyContentUserId] = useState('');
  const [editDailyContentPatch, setEditDailyContentPatch] = useState({
    system_prompt: '',
    enabled: true,
    send_hour_local: 8,
  });
  const [previewDailyContent, setPreviewDailyContent] = useState(null);
  const [previewingDailyContentUserId, setPreviewingDailyContentUserId] = useState('');
  const [dailyContentLlmConfig, setDailyContentLlmConfig] = useState({
    provider: 'nvidia_nim',
    model: 'meta/llama-3.1-8b-instruct',
    fallback_provider: 'openrouter',
    fallback_model: 'google/gemma-3-27b-it:free',
    temperature: 0.7,
    max_tokens: 800,
  });
  const [loadingDailyContentLlmConfig, setLoadingDailyContentLlmConfig] = useState(false);
  const [savingDailyContentLlmConfig, setSavingDailyContentLlmConfig] = useState(false);
  const [dailyContentLlmConfigError, setDailyContentLlmConfigError] = useState('');
  const [dailyContentSchedulerEnabled, setDailyContentSchedulerEnabled] = useState(true);
  const [loadingDailyContentSchedulerToggle, setLoadingDailyContentSchedulerToggle] = useState(false);
  const [scriptFeedbackRows, setScriptFeedbackRows] = useState([]);
  const [loadingScriptFeedback, setLoadingScriptFeedback] = useState(false);
  const [scriptFeedbackError, setScriptFeedbackError] = useState('');
  const [deletingScriptFeedbackId, setDeletingScriptFeedbackId] = useState('');

  // Daily content Get now requests (Admin)
  const [getNowRequestRows, setGetNowRequestRows] = useState([]);
  const [loadingGetNowRequests, setLoadingGetNowRequests] = useState(false);
  const [getNowRequestsError, setGetNowRequestsError] = useState('');
  const [deletingGetNowRequestId, setDeletingGetNowRequestId] = useState('');
  
  // Marketing Dashboard state
  const [marketingChannel, setMarketingChannel] = useState('whatsapp'); // 'whatsapp' | 'web_push'
  const [allMarketingUsers, setAllMarketingUsers] = useState([]); // All users from API
  const [marketingUsers, setMarketingUsers] = useState([]); // Filtered users for display
  const [loadingMarketingUsers, setLoadingMarketingUsers] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [marketingTemplates, setMarketingTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sendingMessages, setSendingMessages] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  useEffect(() => {
    const key = `marketing_selected_template_id_${marketingChannel}`;
    const saved = localStorage.getItem(key);
    setSelectedTemplateId(saved || '');
    setSelectedUserIds(new Set());
  }, [marketingChannel]);

  useEffect(() => {
    const key = `marketing_selected_template_id_${marketingChannel}`;
    if (selectedTemplateId) {
      localStorage.setItem(key, selectedTemplateId);
    } else {
      localStorage.removeItem(key);
    }
  }, [selectedTemplateId, marketingChannel]);

  // Marketing filters
  const [marketingFilters, setMarketingFilters] = useState({
    last_active: { operator: 'none', days: '' },
    last_marketing: { operator: 'none', days: '' },
    last_template: [],
    message_count: { operator: 'none', value: '' },
    name: '',
    email: '',
    created_range: [null, null], // [startDate, endDate] for created_at date range
    premiumFilter: 'all',
  });
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: 'asc' // 'asc' or 'desc'
  });
  
  // Template management
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const emptyWhatsappTemplateForm = () => ({
    channel: 'whatsapp',
    template_name: '',
    whatsapp_template_name: '',
    send_first_name: false,
  });
  const emptyWebPushTemplateForm = () => ({
    channel: 'web_push',
    template_name: '',
    push_title: '',
    push_body: '',
    push_url: '/',
    push_campaign: 'marketing',
  });
  const [templateForm, setTemplateForm] = useState(emptyWhatsappTemplateForm);
  
  // File management state
  const [customers, setCustomers] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [filenameSearch, setFilenameSearch] = useState(''); // File mgmt: email, user id, or filename substring
  /** 'none' | 'largest' | 'smallest' — client-side sort within each file list */
  const [fileSortBySize, setFileSortBySize] = useState('none');
  /** Selected admin files for batch delete: JSON.stringify({ t: fileType, p: path }) */
  const [selectedFileKeys, setSelectedFileKeys] = useState(() => new Set());
  const [batchDeletingFiles, setBatchDeletingFiles] = useState(false);
  const [previewVideo, setPreviewVideo] = useState({
    isOpen: false,
    isLoading: false,
    error: '',
    url: '',
    name: '',
    userEmail: '',
    isBlobUrl: true,
    previewKind: 'video',
  });
  /** Global latest files (file management); token used for thumb/stream query params */
  const [recentFiles, setRecentFiles] = useState([]);
  const [streamTokenForAssets, setStreamTokenForAssets] = useState('');
  /** Customer Files: user_ids with thumbnail view (per-email toggle). */
  const [thumbnailViewUserIds, setThumbnailViewUserIds] = useState(() => new Set());
  /** Customer Files: active downloads keyed by makeFileDownloadKey → { percent, fileName, indeterminate, loaded, total } */
  const [fileDownloadProgress, setFileDownloadProgress] = useState({});
  const downloadingFileKeysRef = useRef(new Set());
  
  // Firestore state
  const [tasks, setTasks] = useState(null);
  const [users, setUsers] = useState(null);
  const [loadingFirestore, setLoadingFirestore] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState('users'); // 'users', 'tasks'
  /** Firestore task row details: task_ids currently re-fetching single doc */
  const [taskSingleRefreshIds, setTaskSingleRefreshIds] = useState(() => new Set());
  
  // Logs state
  const [logFile, setLogFile] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Today's date in YYYY-MM-DD
  const [showErrorLogsOnly, setShowErrorLogsOnly] = useState(true); // Default ON
  const [logViewerSearchInput, setLogViewerSearchInput] = useState('');
  const [logViewerSearchDebounced, setLogViewerSearchDebounced] = useState('');
  const [logSearchMatchCursor, setLogSearchMatchCursor] = useState(0);
  const [logAnalyzedTasks, setLogAnalyzedTasks] = useState([]);
  const [selectedLogTaskKey, setSelectedLogTaskKey] = useState('');
  const [logTaskCopyFlash, setLogTaskCopyFlash] = useState(false);
  const logTaskCopyFlashTimerRef = useRef(null);

  // Razorpay webhook events (admin)
  const [razorpayWebhookEvents, setRazorpayWebhookEvents] = useState([]);
  const [loadingRazorpayWebhooks, setLoadingRazorpayWebhooks] = useState(false);
  const [razorpayWebhooksError, setRazorpayWebhooksError] = useState('');
  const [razorpayWebhooksNextCursor, setRazorpayWebhooksNextCursor] = useState(null);
  const [razorpayWebhooksHasMore, setRazorpayWebhooksHasMore] = useState(false);
  const [webhookEventTypeFilter, setWebhookEventTypeFilter] = useState('all');
  const [webhookEventFilterOpen, setWebhookEventFilterOpen] = useState(false);
  const [razorpayWebhookSearch, setRazorpayWebhookSearch] = useState('');
  const [selectedRazorpayWebhookDocIds, setSelectedRazorpayWebhookDocIds] = useState([]);
  const [deletingRazorpayWebhooks, setDeletingRazorpayWebhooks] = useState(false);
  const webhooksFilterMenuRef = useRef(null);
  const razorpayWebhookSelectAllRef = useRef(null);

  // Payment abandonment surveys (admin)
  const [paymentSurveys, setPaymentSurveys] = useState([]);
  const [loadingPaymentSurveys, setLoadingPaymentSurveys] = useState(false);
  const [paymentSurveysError, setPaymentSurveysError] = useState('');
  const [paymentSurveysNextCursor, setPaymentSurveysNextCursor] = useState(null);
  const [paymentSurveysHasMore, setPaymentSurveysHasMore] = useState(false);
  const [clipFeedbackRows, setClipFeedbackRows] = useState([]);
  const [loadingClipFeedback, setLoadingClipFeedback] = useState(false);
  const [clipFeedbackError, setClipFeedbackError] = useState('');
  const [clipFeedbackNextCursor, setClipFeedbackNextCursor] = useState(null);
  const [clipFeedbackHasMore, setClipFeedbackHasMore] = useState(false);
  const [deletingClipFeedbackId, setDeletingClipFeedbackId] = useState(null);

  const webhookEventTypesInData = useMemo(() => {
    const s = new Set();
    razorpayWebhookEvents.forEach((r) => {
      const t = String(r.event_type || '').trim();
      if (t) s.add(t);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [razorpayWebhookEvents]);

  const filteredRazorpayWebhookEvents = useMemo(() => {
    let list = razorpayWebhookEvents;
    if (webhookEventTypeFilter !== 'all') {
      list = list.filter((r) => String(r.event_type || '') === webhookEventTypeFilter);
    }
    const q = razorpayWebhookSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => buildRazorpayWebhookRowSearchText(r).includes(q));
  }, [razorpayWebhookEvents, webhookEventTypeFilter, razorpayWebhookSearch]);

  const filteredRazorpayVisibleDocIds = useMemo(
    () =>
      filteredRazorpayWebhookEvents
        .map((r) => (r.doc_id || r.event_id ? String(r.doc_id || r.event_id) : ''))
        .filter(Boolean),
    [filteredRazorpayWebhookEvents]
  );

  const selectedLogTask = useMemo(
    () => logAnalyzedTasks.find((t) => t.key === selectedLogTaskKey) || null,
    [logAnalyzedTasks, selectedLogTaskKey]
  );

  const logLinesForViewer = useMemo(
    () => (logFile?.content ? logFile.content.split('\n') : []),
    [logFile?.content]
  );

  /** Original line indices in the loaded log that match the search (respects error-only filter). */
  const logSearchMatchLineIndices = useMemo(() => {
    if (!logViewerSearchDebounced) return [];
    const q = logViewerSearchDebounced.toLowerCase();
    const indices = [];
    for (let i = 0; i < logLinesForViewer.length; i++) {
      const line = logLinesForViewer[i];
      if (!line.toLowerCase().includes(q)) continue;
      if (showErrorLogsOnly && !line.toUpperCase().includes('ERROR')) continue;
      indices.push(i);
    }
    return indices;
  }, [logLinesForViewer, logViewerSearchDebounced, showErrorLogsOnly]);

  const logSearchCurrentLineIndex = useMemo(() => {
    if (logSearchMatchLineIndices.length === 0) return null;
    const i = Math.min(logSearchMatchCursor, logSearchMatchLineIndices.length - 1);
    return logSearchMatchLineIndices[i];
  }, [logSearchMatchLineIndices, logSearchMatchCursor]);

  // User Journey state
  const [userJourneyDateFrom, setUserJourneyDateFrom] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const saved = getSavedAdminPageState()?.userJourneyDateFrom;
    return typeof saved === 'string' && saved.trim() ? saved : today;
  });
  const [userJourneyDateTo, setUserJourneyDateTo] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const saved = getSavedAdminPageState()?.userJourneyDateTo;
    return typeof saved === 'string' && saved.trim() ? saved : today;
  });
  const [userJourneySessions, setUserJourneySessions] = useState([]);
  const [loadingUserJourney, setLoadingUserJourney] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(() => {
    const saved = getSavedAdminPageState()?.selectedSessionId;
    return typeof saved === 'string' && saved.trim() ? saved : null;
  });
  const [selectedSessionDate, setSelectedSessionDate] = useState(() => {
    const saved = getSavedAdminPageState()?.selectedSessionDate;
    return typeof saved === 'string' && saved.trim() ? saved : null;
  });
  const selectedSessionIdRef = useRef(selectedSessionId);
  const selectedSessionDateRef = useRef(selectedSessionDate);
  const [userJourneyTimeline, setUserJourneyTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [userJourneySessionFilter, setUserJourneySessionFilter] = useState([]); // selected event types; session shown if it CONTAINS any of these
  const [userJourneyFilterDropdownOpen, setUserJourneyFilterDropdownOpen] = useState(false);
  const [userJourneyEmailScope, setUserJourneyEmailScope] = useState(''); // filter sessions by email (substring, case-insensitive) in loaded range
  const [userJourneyCountryScope, setUserJourneyCountryScope] = useState(''); // filter sessions by country code (ISO-2)
  const [userJourneyLandingScope, setUserJourneyLandingScope] = useState(''); // raw substring search for landing URL
  /** Min event count threshold: keep sessions where event_count > this value (default 4 → 5+ events). */
  const [userJourneyMinEventCount, setUserJourneyMinEventCount] = useState(4);
  const [deletingSessionId, setDeletingSessionId] = useState(null); // session_id being deleted
  const [selectedJourneySessionKeys, setSelectedJourneySessionKeys] = useState(() => new Set());
  const [batchDeletingJourneySessions, setBatchDeletingJourneySessions] = useState(false);
  const [exportingUserJourney, setExportingUserJourney] = useState(false);
  
  // User email cache (user_id -> email mapping)
  const [userEmailCache, setUserEmailCache] = useState({});
  /** user_id -> { created_at, last_active, updated_at } for Tasks tab "Returning user" tag */
  const [userMetaById, setUserMetaById] = useState({});
  const usersLoadedRef = useRef(false);
  const pushStatsLoadedRef = useRef(false);
  const connectedAccountsLoadedRef = useRef(false);
  /** Full /api/admin/firestore/users response — shared by login + Users tab */
  const cachedUsersRef = useRef(null);
  const loadAllUsersRef = useRef(null);
  /** Cursor for tasks load_remaining (25th doc id) */
  const tasksPageCursorRef = useRef(null);
  const [tasksHasMore, setTasksHasMore] = useState(false);
  const [loadingTasksRemaining, setLoadingTasksRemaining] = useState(false);
  const [pushStats, setPushStats] = useState(null);
  const [connectedAccountsByUser, setConnectedAccountsByUser] = useState({});
  const [connectedAccountsLoaded, setConnectedAccountsLoaded] = useState(false);

  // Sorting state
  const [sortLatestFirst, setSortLatestFirst] = useState({
    tasks: true,
    users: true
  });
  
  // Sort type state for users (created or active)
  const [userSortType, setUserSortType] = useState('created'); // 'created' or 'active'
  
  // Collapsed state for rows
  const [collapsedItems, setCollapsedItems] = useState({
    tasks: new Set(),
    users: new Set()
  });

  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const [batchDeletingTasks, setBatchDeletingTasks] = useState(false);

  // Task queue state
  const [queueData, setQueueData] = useState(null);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [taskQueueSearch, setTaskQueueSearch] = useState('');
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loadingScheduledPosts, setLoadingScheduledPosts] = useState(false);
  const [loadingScheduledPostsRemaining, setLoadingScheduledPostsRemaining] = useState(false);
  const [scheduledPostsHasMore, setScheduledPostsHasMore] = useState(false);
  const scheduledPostsPageCursorRef = useRef(null);
  const [scheduledPostsError, setScheduledPostsError] = useState('');
  const [scheduledPostsSearch, setScheduledPostsSearch] = useState('');
  const [scheduledPostsStatusFilter, setScheduledPostsStatusFilter] = useState('');
  const [scheduledPostsPlatformFilter, setScheduledPostsPlatformFilter] = useState('');
  const [deletingScheduledPostId, setDeletingScheduledPostId] = useState('');
  const [selectedScheduledPostIds, setSelectedScheduledPostIds] = useState(() => new Set());
  const [batchDeletingScheduledPosts, setBatchDeletingScheduledPosts] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    tasks: {
      status: '', // Filter by lifecycle_state/status
      userEmail: '', // Filter by user email (search)
      searchText: '', // General search across task fields
      dateRange: [null, null], // [startDate, endDate] for date range picker
      lambdaOnly: false, // UI: tasks with Remotion/Lambda metadata only
      returningUsersOnly: false, // UI: tasks whose user is a returning user
      deviceType: '', // metadata.device_type (mobile, tablet, desktop)
      deviceOs: '', // metadata.device_os or OS family token
    },
    users: {
      email: '', // Filter by email
      name: '', // Filter by name
      phone: '', // Filter by phone number
      country: '', // Filter by signup country code (ISO-2)
      paidCount: 'all', // 'all' | '1' | 'gt1' (subscription_paid_count)
      streakFilter: 'all', // 'all' | '1' | 'gt1' (creator streak display count)
      hasVideos: null, // true = has videos, false = no videos, null = all
      hasPhone: 'none', // 'none' = all, 'true' = has phone, 'false' = no phone
      premiumFilter: 'all', // 'all' | 'premium' | 'coupon' | 'any-subscription' | 'cancel-at-period-end' | 'active'|... (legacy 'subscription' → active)
      createdRange: [null, null], // [startDate, endDate] for created_at
      lastActiveRange: [null, null], // [startDate, endDate] for last_active
      remindersOnly: false, // UI: users with push reminders enabled
      socialHandleOnly: false, // UI: users with instagram_handle and/or youtube_handle
      connectedAccountOnly: false, // UI: users with OAuth connected social account(s)
    }
  });
  
  // Navigation state for task -> user/files navigation and user journey -> firestore
  const [navigationState, setNavigationState] = useState({
    fromTask: null, // Store task info when navigating to user or files
    previousCollection: null, // Store previous collection tab
    previousTab: null, // Store previous tab (for files navigation)
    fromUserJourney: null, // { date, sessionId } when opening Tasks/Users from User Journey tab
    fromFileManagement: null, // { email, userId, collection: 'users'|'tasks' } from File Management
    fromFirestoreUsers: null, // { email, userId, source: 'tasks'|'files' } when drilling from Firestore Users
  });
  
  // Use ref to track if admin validation has been done to prevent loops
  const adminValidatedRef = useRef(false);
  
  // Store getFreshToken in a ref to avoid dependency issues
  const getFreshTokenRef = useRef(getFreshToken);
  useEffect(() => {
    getFreshTokenRef.current = getFreshToken;
  }, [getFreshToken]);
  useEffect(() => {
    selectedSessionIdRef.current = selectedSessionId;
  }, [selectedSessionId]);
  useEffect(() => {
    selectedSessionDateRef.current = selectedSessionDate;
  }, [selectedSessionDate]);
  
  // YouTube Cookies functions
  const loadCookies = useCallback(async () => {
    try {
      setLoadingCookies(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/youtube/cookies', {
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCookies(data.cookies || []);
      }
    } catch (err) {
      console.error('Error loading cookies:', err);
    } finally {
      setLoadingCookies(false);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      setLoadingQueue(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/task-queue', {
        headers: { 'Authorization': `Bearer ${freshToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setQueueData(data);
      } else {
        setQueueData(null);
      }
    } catch (err) {
      console.error('Error loading queue:', err);
      setQueueData(null);
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  const removeFromQueue = useCallback(async (taskId) => {
    if (!taskId || !window.confirm(`Remove task ${taskId} from the task queue?`)) return;
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch(`/api/admin/task-queue/${encodeURIComponent(taskId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${freshToken}` },
      });
      if (response.ok) {
        await loadQueue();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to remove from task queue');
      }
    } catch (err) {
      console.error('Error removing from task queue:', err);
      alert('Failed to remove from task queue');
    }
  }, [loadQueue]);

  const filteredTaskQueueItems = useMemo(() => {
    const items = queueData?.items;
    if (!Array.isArray(items)) return [];
    const q = taskQueueSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const blob = [item.task_id, item.user_id, item.user_email, item.filename, item.type, item.added_at]
        .map((x) => (x == null ? '' : String(x).toLowerCase()))
        .join(' ');
      return blob.includes(q);
    });
  }, [queueData, taskQueueSearch]);

  const applyScheduledPostsPayload = useCallback((data, { append = false } = {}) => {
    const posts = Array.isArray(data?.posts) ? data.posts : [];
    if (append) {
      setScheduledPosts((prev) => mergeScheduledPostsLists(prev, posts));
    } else {
      setScheduledPosts(posts);
    }
    setScheduledPostsHasMore(Boolean(data?.has_more));
    scheduledPostsPageCursorRef.current = data?.page_cursor || null;
  }, []);

  const fetchScheduledPostsRecent = useCallback(async ({ force = false } = {}) => {
    if (!force && scheduledPosts.length > 0) {
      return;
    }
    try {
      setLoadingScheduledPosts(true);
      setScheduledPostsError('');
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/scheduled-posts?limit=25', {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load scheduled posts');
      }
      applyScheduledPostsPayload(data);
    } catch (err) {
      console.error('Error loading scheduled posts:', err);
      setScheduledPosts([]);
      setScheduledPostsHasMore(false);
      scheduledPostsPageCursorRef.current = null;
      setScheduledPostsError(err.message || 'Failed to load scheduled posts');
    } finally {
      setLoadingScheduledPosts(false);
    }
  }, [scheduledPosts.length, applyScheduledPostsPayload]);

  const loadScheduledPostsRemaining = useCallback(async () => {
    const cursor = scheduledPostsPageCursorRef.current;
    if (!cursor || !scheduledPostsHasMore) return;
    try {
      setLoadingScheduledPostsRemaining(true);
      const freshToken = await getFreshTokenRef.current();
      const qs = new URLSearchParams({
        load_remaining: '1',
        start_after_post_id: cursor,
      });
      const response = await fetch(`/api/admin/scheduled-posts?${qs}`, {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load remaining scheduled posts');
      }
      applyScheduledPostsPayload(data, { append: true });
      setScheduledPostsHasMore(false);
    } catch (err) {
      console.error('Error loading remaining scheduled posts:', err);
      alert(err.message || 'Failed to load remaining scheduled posts');
    } finally {
      setLoadingScheduledPostsRemaining(false);
    }
  }, [scheduledPostsHasMore, applyScheduledPostsPayload]);

  const refreshScheduledPosts = useCallback(async () => {
    scheduledPostsPageCursorRef.current = null;
    setScheduledPostsHasMore(false);
    setScheduledPosts([]);
    setSelectedScheduledPostIds(new Set());
    await fetchScheduledPostsRecent({ force: true });
  }, [fetchScheduledPostsRecent]);

  const deleteScheduledPost = useCallback(async (postId) => {
    if (!postId || !window.confirm('Delete this scheduled post from Firestore? This cannot be undone.')) return;
    try {
      setDeletingScheduledPostId(postId);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch(`/api/admin/scheduled-posts/${encodeURIComponent(postId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete scheduled post');
      }
      setScheduledPosts((prev) => prev.filter((post) => post.id !== postId));
      setSelectedScheduledPostIds((prev) => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    } catch (err) {
      console.error('Error deleting scheduled post:', err);
      alert(err.message || 'Failed to delete scheduled post');
    } finally {
      setDeletingScheduledPostId('');
    }
  }, []);

  const toggleScheduledPostSelection = useCallback((postId) => {
    if (!postId) return;
    setSelectedScheduledPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  }, []);

  const handleDeleteSelectedScheduledPosts = useCallback(async () => {
    const ids = [...selectedScheduledPostIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} scheduled post(s) from Firestore? This cannot be undone.`)) {
      return;
    }
    setBatchDeletingScheduledPosts(true);
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/scheduled-posts/batch-delete', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ post_ids: ids }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Batch delete failed');
      }
      const failed = Array.isArray(data.failed) ? data.failed : [];
      const deletedIds = new Set(ids.filter((id) => !failed.includes(id)));
      setScheduledPosts((prev) => prev.filter((post) => !deletedIds.has(post.id)));
      setSelectedScheduledPostIds(new Set(failed));
      if (failed.length > 0) {
        alert(`Failed to delete ${failed.length} post(s). Others were removed.`);
      }
    } catch (err) {
      alert(err.message || 'Batch delete failed');
    } finally {
      setBatchDeletingScheduledPosts(false);
    }
  }, [selectedScheduledPostIds]);

  const filteredScheduledPosts = useMemo(() => {
    const q = scheduledPostsSearch.trim().toLowerCase();

    return scheduledPosts.filter((post) => {
      if (scheduledPostsStatusFilter && post.status !== scheduledPostsStatusFilter) {
        return false;
      }
      if (scheduledPostsPlatformFilter && (post.platform || 'youtube') !== scheduledPostsPlatformFilter) {
        return false;
      }
      if (!q) return true;
      const blob = [
        post.id,
        post.user_id,
        post.user_email,
        post.platform,
        post.status,
        post.file_stem,
        post.clip_filename,
        post.metadata?.title,
        post.error,
        getScheduledPostPlatformUrl(post),
      ]
        .map((x) => (x == null ? '' : String(x).toLowerCase()))
        .join(' ');
      return blob.includes(q);
    });
  }, [scheduledPosts, scheduledPostsSearch, scheduledPostsStatusFilter, scheduledPostsPlatformFilter]);

  const scheduledPostsHasActiveFilters = Boolean(
    scheduledPostsSearch.trim() || scheduledPostsStatusFilter || scheduledPostsPlatformFilter
  );
  
  const loadYoutubeUrlToggle = useCallback(async () => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/youtube-url-toggle', {
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setYoutubeUrlEnabled(data.enabled !== false);
      }
    } catch (err) {
      console.error('Error loading YouTube URL toggle:', err);
    }
  }, []);

  const loadTrimSilenceFeatureToggle = useCallback(async () => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/trim-silence-toggle', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTrimSilenceFeatureEnabled(data.enabled !== false);
      }
    } catch (err) {
      console.error('Error loading trim silence feature toggle:', err);
    }
  }, []);

  const loadBrollDefaultToggle = useCallback(async () => {
    try {
      setLoadingBrollDefaultToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/broll-default-toggle', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setIncludeBrollDefaultEnabled(data.enabled !== false);
      }
    } catch (err) {
      console.error('Error loading B-roll default toggle:', err);
    } finally {
      setLoadingBrollDefaultToggle(false);
    }
  }, []);

  const loadStreakPushSchedulerToggle = useCallback(async () => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/streak-push-scheduler-toggle', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStreakPushSchedulerEnabled(data.enabled !== false);
        setPushStats((prev) => (prev ? { ...prev, scheduler_enabled: data.enabled !== false } : prev));
      }
    } catch (err) {
      console.error('Error loading streak push scheduler toggle:', err);
    }
  }, []);

  const loadChatwootTemplatesToggle = useCallback(async () => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/chatwoot-templates-toggle', {
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatwootTemplatesEnabled({
          welcome: data.welcome !== false,
          finished: data.finished !== false
        });
      }
    } catch (err) {
      console.error('Error loading Chatwoot templates toggle:', err);
    }
  }, []);

  const loadLambdaToggle = useCallback(async () => {
    try {
      setLoadingLambdaToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/lambda-toggle', {
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setLambdaEnabled(data.enabled === true);
        setLambdaEnvConfigured(data.env_configured !== false);
        setLambdaMissingEnvFields(Array.isArray(data.missing_env_fields) ? data.missing_env_fields : []);
      }
    } catch (err) {
      console.error('Error loading Lambda toggle:', err);
    } finally {
      setLoadingLambdaToggle(false);
    }
  }, []);

  const loadRazorpaySubscriptionToggle = useCallback(async () => {
    try {
      setLoadingRazorpayToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/razorpay-subscription-toggle', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRazorpayCheckoutEnabled(data.checkout_enabled !== false);
        setRazorpayEnvEnabled(data.env_enabled !== false);
        setRazorpayMode(data.mode === 'live' ? 'live' : 'test');
        setRazorpayModeReady(data.mode_ready !== false);
      }
    } catch (err) {
      console.error('Error loading Razorpay subscription toggle:', err);
    } finally {
      setLoadingRazorpayToggle(false);
    }
  }, []);

  const loadRazorpayMode = useCallback(async () => {
    try {
      setLoadingRazorpayMode(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/razorpay-mode', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRazorpayMode(data.mode === 'live' ? 'live' : 'test');
        setRazorpayModeReady(data.mode_ready !== false);
        setRazorpayEnvEnabled(data.env_enabled !== false);
      }
    } catch (err) {
      console.error('Error loading Razorpay mode:', err);
    } finally {
      setLoadingRazorpayMode(false);
    }
  }, []);

  const loadPricingDisplay = useCallback(async () => {
    try {
      setLoadingPricingDisplay(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/billing/pricing-display', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPricingDisplayForm({
          in: {
            price_old: data?.in?.price_old || '',
            price_new: data?.in?.price_new || '',
            period: data?.in?.period || '',
          },
          intl: {
            price_old: data?.intl?.price_old || '',
            price_new: data?.intl?.price_new || '',
            period: data?.intl?.period || '',
          },
        });
        setPricingDisplayLoaded(true);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to load pricing display');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to load pricing display'}`);
    } finally {
      setLoadingPricingDisplay(false);
    }
  }, []);

  const savePricingDisplay = async () => {
    try {
      setSavingPricingDisplay(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/billing/pricing-display', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pricingDisplayForm),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        alert(data.message || 'Pricing display updated');
      } else {
        alert(data.error || 'Failed to update pricing display');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to update pricing display'}`);
    } finally {
      setSavingPricingDisplay(false);
    }
  };

  const loadRazorpayPlans = useCallback(async () => {
    try {
      setLoadingRazorpayPlans(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/billing/razorpay-plans', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRazorpayPlansForm({
          test: {
            in: data?.test?.in || '',
            intl: data?.test?.intl || '',
          },
          live: {
            in: data?.live?.in || '',
            intl: data?.live?.intl || '',
          },
        });
        setRazorpayPlansLoaded(true);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to load Razorpay plan IDs');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to load Razorpay plan IDs'}`);
    } finally {
      setLoadingRazorpayPlans(false);
    }
  }, []);

  const saveRazorpayPlans = async () => {
    try {
      setSavingRazorpayPlans(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/billing/razorpay-plans', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(razorpayPlansForm),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        if (typeof data.mode_ready === 'boolean') {
          setRazorpayModeReady(data.mode_ready);
        }
        alert(data.message || 'Razorpay plan IDs updated');
      } else {
        alert(data.error || 'Failed to update Razorpay plan IDs');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to update Razorpay plan IDs'}`);
    } finally {
      setSavingRazorpayPlans(false);
    }
  };

  const loadPhoneDownloadToggle = useCallback(async () => {
    try {
      setLoadingPhoneDownloadToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/phone-download-toggle', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRequirePhoneForDownload(data.enabled !== false);
      }
    } catch (err) {
      console.error('Error loading phone download toggle:', err);
    } finally {
      setLoadingPhoneDownloadToggle(false);
    }
  }, []);

  const loadPricingInviteCardToggle = useCallback(async () => {
    try {
      setLoadingPricingInviteToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/pricing-invite-card-toggle', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPricingInviteCardEnabled(data.enabled !== false);
      }
    } catch (err) {
      console.error('Error loading pricing invite card toggle:', err);
    } finally {
      setLoadingPricingInviteToggle(false);
    }
  }, []);

  const loadCoupons = useCallback(async () => {
    try {
      setCouponsError('');
      setLoadingCoupons(true);
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/coupons', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load coupons');
      }
      setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
    } catch (e) {
      setCouponsError(e?.message || 'Failed to load coupons');
      setCoupons([]);
    } finally {
      setLoadingCoupons(false);
    }
  }, []);

  const loadDailyContentPrompts = useCallback(async () => {
    try {
      setDailyContentPromptsError('');
      setLoadingDailyContentPrompts(true);
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/daily-content-prompts', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load daily content prompts');
      }
      setDailyContentPrompts(Array.isArray(data.prompts) ? data.prompts : []);
    } catch (e) {
      setDailyContentPromptsError(e?.message || 'Failed to load daily content prompts');
      setDailyContentPrompts([]);
    } finally {
      setLoadingDailyContentPrompts(false);
    }
  }, []);

  const loadDailyContentLlmConfig = useCallback(async () => {
    try {
      setDailyContentLlmConfigError('');
      setLoadingDailyContentLlmConfig(true);
      const freshToken = await getFreshTokenRef.current();
      const [cfgRes, toggleRes] = await Promise.all([
        fetch('/api/admin/daily-content/llm-config', {
          headers: { Authorization: `Bearer ${freshToken}` },
        }),
        fetch('/api/admin/features/daily-content-scheduler-toggle', {
          headers: { Authorization: `Bearer ${freshToken}` },
        }),
      ]);
      const cfgData = await cfgRes.json().catch(() => ({}));
      const toggleData = await toggleRes.json().catch(() => ({}));
      if (!cfgRes.ok) {
        throw new Error(cfgData.error || 'Failed to load LLM config');
      }
      if (cfgData.config && typeof cfgData.config === 'object') {
        setDailyContentLlmConfig((prev) => ({ ...prev, ...cfgData.config }));
      }
      if (toggleRes.ok) {
        setDailyContentSchedulerEnabled(Boolean(toggleData.enabled));
      }
    } catch (e) {
      setDailyContentLlmConfigError(e?.message || 'Failed to load LLM config');
    } finally {
      setLoadingDailyContentLlmConfig(false);
    }
  }, []);

  const loadScriptFeedbackSurvey = useCallback(async () => {
    try {
      setScriptFeedbackError('');
      setLoadingScriptFeedback(true);
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/daily-content/script-feedback?limit=200', {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load script feedback');
      }
      setScriptFeedbackRows(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setScriptFeedbackError(e?.message || 'Failed to load script feedback');
      setScriptFeedbackRows([]);
    } finally {
      setLoadingScriptFeedback(false);
    }
  }, []);

  const deleteScriptFeedback = async (feedbackId) => {
    if (!feedbackId || deletingScriptFeedbackId) return;
    if (!window.confirm('Delete this script feedback entry?')) return;
    try {
      setDeletingScriptFeedbackId(feedbackId);
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/daily-content/script-feedback/${encodeURIComponent(feedbackId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete script feedback');
      }
      setScriptFeedbackRows((prev) => prev.filter((row) => row.id !== feedbackId));
    } catch (e) {
      setScriptFeedbackError(e?.message || 'Failed to delete script feedback');
    } finally {
      setDeletingScriptFeedbackId('');
    }
  };

  const loadGetNowRequests = useCallback(async () => {
    try {
      setGetNowRequestsError('');
      setLoadingGetNowRequests(true);
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/daily-content/get-now-requests?limit=200', {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load get-now requests');
      }
      setGetNowRequestRows(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setGetNowRequestsError(e?.message || 'Failed to load get-now requests');
      setGetNowRequestRows([]);
    } finally {
      setLoadingGetNowRequests(false);
    }
  }, []);

  const deleteGetNowRequest = async (requestId) => {
    if (!requestId || deletingGetNowRequestId) return;
    if (!window.confirm('Delete this get-now request?')) return;
    try {
      setDeletingGetNowRequestId(requestId);
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/daily-content/get-now-requests/${encodeURIComponent(requestId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete get-now request');
      }
      setGetNowRequestRows((prev) => prev.filter((row) => row.id !== requestId));
    } catch (e) {
      setGetNowRequestsError(e?.message || 'Failed to delete get-now request');
    } finally {
      setDeletingGetNowRequestId('');
    }
  };

  const saveDailyContentLlmConfig = async () => {
    setSavingDailyContentLlmConfig(true);
    setDailyContentLlmConfigError('');
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/daily-content/llm-config', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dailyContentLlmConfig),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save LLM config');
      }
      if (data.config) {
        setDailyContentLlmConfig((prev) => ({ ...prev, ...data.config }));
      }
    } catch (e) {
      setDailyContentLlmConfigError(e?.message || 'Failed to save LLM config');
    } finally {
      setSavingDailyContentLlmConfig(false);
    }
  };

  const toggleDailyContentScheduler = async () => {
    setLoadingDailyContentSchedulerToggle(true);
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/features/daily-content-scheduler-toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !dailyContentSchedulerEnabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to toggle scheduler');
      }
      setDailyContentSchedulerEnabled(Boolean(data.enabled));
    } catch (e) {
      setDailyContentLlmConfigError(e?.message || 'Failed to toggle scheduler');
    } finally {
      setLoadingDailyContentSchedulerToggle(false);
    }
  };

  const createDailyContentPrompt = async () => {
    const email = String(dailyContentForm.email || '').trim();
    const systemPrompt = String(dailyContentForm.system_prompt || '').trim();
    if (!email) {
      setDailyContentPromptsError('User email is required');
      return;
    }
    if (!systemPrompt) {
      setDailyContentPromptsError('System prompt is required');
      return;
    }
    setCreatingDailyContentPrompt(true);
    setDailyContentPromptsError('');
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/daily-content-prompts', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          system_prompt: systemPrompt,
          enabled: Boolean(dailyContentForm.enabled),
          send_hour_local: Number(dailyContentForm.send_hour_local || 8),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create prompt assignment');
      }
      setDailyContentForm({
        email: '',
        system_prompt: '',
        enabled: true,
        send_hour_local: 8,
      });
      await loadDailyContentPrompts();
    } catch (e) {
      setDailyContentPromptsError(e?.message || 'Failed to create prompt assignment');
    } finally {
      setCreatingDailyContentPrompt(false);
    }
  };

  const startEditDailyContentPrompt = (prompt) => {
    setEditingDailyContentUserId(prompt.user_id || '');
    setEditDailyContentPatch({
      system_prompt: prompt.system_prompt || '',
      enabled: Boolean(prompt.enabled),
      send_hour_local: Number(prompt.send_hour_local ?? 8),
    });
  };

  const saveEditDailyContentPrompt = async () => {
    const uid = editingDailyContentUserId;
    if (!uid) return;
    setDailyContentPromptsError('');
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/daily-content-prompts/${encodeURIComponent(uid)}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editDailyContentPatch),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update prompt assignment');
      }
      setEditingDailyContentUserId('');
      await loadDailyContentPrompts();
    } catch (e) {
      setDailyContentPromptsError(e?.message || 'Failed to update prompt assignment');
    }
  };

  const deleteDailyContentPrompt = async (userId) => {
    if (!userId) return;
    if (!window.confirm('Delete this daily content prompt assignment?')) return;
    setDailyContentPromptsError('');
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/daily-content-prompts/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete prompt assignment');
      }
      await loadDailyContentPrompts();
    } catch (e) {
      setDailyContentPromptsError(e?.message || 'Failed to delete prompt assignment');
    }
  };

  const previewDailyContentPrompt = async (userId) => {
    if (!userId) return;
    setPreviewingDailyContentUserId(userId);
    setPreviewDailyContent(null);
    setDailyContentPromptsError('');
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/daily-content-prompts/${encodeURIComponent(userId)}/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Preview failed');
      }
      setPreviewDailyContent(data);
    } catch (e) {
      setDailyContentPromptsError(e?.message || 'Preview failed');
    } finally {
      setPreviewingDailyContentUserId('');
    }
  };

  const loadRazorpayWebhookEvents = useCallback(async ({ append = false, cursor = null } = {}) => {
    try {
      setRazorpayWebhooksError('');
      setLoadingRazorpayWebhooks(true);
      const freshToken = await getFreshTokenRef.current();
      const params = new URLSearchParams({ limit: '15' });
      if (append && cursor) {
        params.set('cursor', cursor);
      }
      const res = await fetch(`/api/admin/razorpay-webhook-events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load webhook events');
      }
      const ev = Array.isArray(data.events) ? data.events : [];
      setRazorpayWebhookEvents((prev) => (append ? [...prev, ...ev] : ev));
      setRazorpayWebhooksHasMore(Boolean(data.has_more));
      setRazorpayWebhooksNextCursor(data.next_cursor || null);
    } catch (e) {
      setRazorpayWebhooksError(e?.message || 'Failed to load webhook events');
      if (!append) {
        setRazorpayWebhookEvents([]);
        setRazorpayWebhooksHasMore(false);
        setRazorpayWebhooksNextCursor(null);
      }
    } finally {
      setLoadingRazorpayWebhooks(false);
    }
  }, []);

  const deleteRazorpayWebhookEventsBatch = useCallback(async (ids) => {
    const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (!list.length) return;
    if (list.length > 500) {
      setRazorpayWebhooksError('Select at most 500 events per delete.');
      return;
    }
    setDeletingRazorpayWebhooks(true);
    setRazorpayWebhooksError('');
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch('/api/admin/razorpay-webhook-events/delete', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: list }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Delete failed');
      }
      const idSet = new Set(list.map((id) => String(id)));
      setRazorpayWebhookEvents((prev) =>
        prev.filter((r) => {
          const key = String(r.doc_id || r.event_id || '');
          return !idSet.has(key);
        })
      );
      setSelectedRazorpayWebhookDocIds((prev) => prev.filter((id) => !idSet.has(String(id))));
    } catch (e) {
      setRazorpayWebhooksError(e?.message || 'Failed to delete webhook events');
    } finally {
      setDeletingRazorpayWebhooks(false);
    }
  }, []);

  const loadPaymentAbandonmentSurveys = useCallback(async ({ append = false, cursor = null } = {}) => {
    try {
      setPaymentSurveysError('');
      setLoadingPaymentSurveys(true);
      const freshToken = await getFreshTokenRef.current();
      const params = new URLSearchParams({ limit: '25' });
      if (append && cursor) {
        params.set('cursor', cursor);
      }
      const res = await fetch(`/api/admin/payment-abandonment-surveys?${params.toString()}`, {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load survey submissions');
      }
      const rows = Array.isArray(data.surveys) ? data.surveys : [];
      setPaymentSurveys((prev) => (append ? [...prev, ...rows] : rows));
      setPaymentSurveysHasMore(Boolean(data.has_more));
      setPaymentSurveysNextCursor(data.next_cursor || null);
    } catch (e) {
      setPaymentSurveysError(e?.message || 'Failed to load survey submissions');
      if (!append) {
        setPaymentSurveys([]);
        setPaymentSurveysHasMore(false);
        setPaymentSurveysNextCursor(null);
      }
    } finally {
      setLoadingPaymentSurveys(false);
    }
  }, []);

  const loadClipFeedback = useCallback(async ({ append = false, cursor = null } = {}) => {
    try {
      setClipFeedbackError('');
      setLoadingClipFeedback(true);
      const freshToken = await getFreshTokenRef.current();
      const params = new URLSearchParams({ limit: '25' });
      if (append && cursor) {
        params.set('cursor', cursor);
      }
      const res = await fetch(`/api/admin/clip-feedback?${params.toString()}`, {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load clip feedback');
      }
      const rows = Array.isArray(data.feedback) ? data.feedback : [];
      setClipFeedbackRows((prev) => (append ? [...prev, ...rows] : rows));
      setClipFeedbackHasMore(Boolean(data.has_more));
      setClipFeedbackNextCursor(data.next_cursor || null);
    } catch (e) {
      setClipFeedbackError(e?.message || 'Failed to load clip feedback');
      if (!append) {
        setClipFeedbackRows([]);
        setClipFeedbackHasMore(false);
        setClipFeedbackNextCursor(null);
      }
    } finally {
      setLoadingClipFeedback(false);
    }
  }, []);

  const deleteClipFeedback = useCallback(async (docId) => {
    const id = String(docId || '').trim();
    if (!id) return;
    if (!window.confirm('Delete this clip feedback entry?')) return;
    try {
      setDeletingClipFeedbackId(id);
      setClipFeedbackError('');
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/clip-feedback/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete clip feedback');
      }
      setClipFeedbackRows((prev) => prev.filter((row) => String(row?.doc_id || '') !== id));
    } catch (e) {
      setClipFeedbackError(e?.message || 'Failed to delete clip feedback');
    } finally {
      setDeletingClipFeedbackId(null);
    }
  }, []);

  const createCoupon = async () => {
    const code = String(couponForm.code || '').trim().toUpperCase();
    if (!code) {
      alert('Coupon code is required');
      return;
    }
    if (!couponForm.expires_at_iso) {
      alert('Expiry is required');
      return;
    }
    const couponType = couponForm.type === 'paid' ? 'paid' : 'trial';
    if (couponType === 'trial' && Number(couponForm.trial_days || 0) <= 0) {
      alert('Trial days must be > 0 for trial coupons');
      return;
    }
    if (couponType === 'paid') {
      if (!String(couponForm.razorpay_plan_id_in || '').trim()) {
        alert('Razorpay plan ID (India) is required for paid coupons');
        return;
      }
      if (!String(couponForm.display_price_new || '').trim()) {
        alert('Display price is required for paid coupons');
        return;
      }
      if (!String(couponForm.display_period || '').trim()) {
        alert('Display period is required for paid coupons');
        return;
      }
    }
    try {
      setCreatingCoupon(true);
      const freshToken = await getFreshTokenRef.current();
      const payload = {
        code,
        type: couponType,
        trial_days: Number(couponForm.trial_days || 0),
        max_uses: Number(couponForm.max_uses || 0),
        expires_at_iso: String(couponForm.expires_at_iso || '').trim(),
        is_active: true,
      };
      if (couponType === 'paid') {
        Object.assign(payload, {
          razorpay_plan_id_in: String(couponForm.razorpay_plan_id_in || '').trim(),
          razorpay_plan_id_intl: String(couponForm.razorpay_plan_id_intl || '').trim(),
          display_price_new: String(couponForm.display_price_new || '').trim(),
          display_period: String(couponForm.display_period || '').trim(),
          display_price_new_intl: String(couponForm.display_price_new_intl || '').trim(),
          display_period_intl: String(couponForm.display_period_intl || '').trim(),
        });
      }
      const res = await fetch('/api/admin/coupons', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create coupon');
      }
      setCouponForm((p) => ({ ...p, code: '' }));
      await loadCoupons();
      alert('Coupon created');
    } catch (e) {
      alert(e?.message || 'Failed to create coupon');
    } finally {
      setCreatingCoupon(false);
    }
  };

  const startEditCoupon = (c) => {
    const code = String(c?.code || '').trim().toUpperCase();
    if (!code) return;
    setEditingCouponCode(code);
    const iso = String(c?.expires_at_iso || c?.expires_at || '').trim();
    let dt = null;
    if (iso) {
      try {
        dt = new Date(iso);
        if (Number.isNaN(dt.getTime())) dt = null;
      } catch (_) {
        dt = null;
      }
    }
    setEditCouponPatch({
      is_active: c?.is_active !== false,
      type: c?.type === 'paid' ? 'paid' : 'trial',
      trial_days: Number(c?.trial_days || 0) || 7,
      max_uses: Number(c?.max_uses || 0) || 0,
      expires_at_iso: iso,
      expires_at_date: dt,
      razorpay_plan_id_in: String(c?.razorpay_plan_id_in || ''),
      razorpay_plan_id_intl: String(c?.razorpay_plan_id_intl || ''),
      display_price_old: String(c?.display_price_old || ''),
      display_price_new: String(c?.display_price_new || ''),
      display_period: String(c?.display_period || ''),
      display_price_old_intl: String(c?.display_price_old_intl || ''),
      display_price_new_intl: String(c?.display_price_new_intl || ''),
      display_period_intl: String(c?.display_period_intl || ''),
    });
  };

  const saveEditCoupon = async () => {
    const code = String(editingCouponCode || '').trim().toUpperCase();
    if (!code) return;
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/coupons/${encodeURIComponent(code)}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_active: Boolean(editCouponPatch.is_active),
          type: editCouponPatch.type === 'paid' ? 'paid' : 'trial',
          trial_days: Number(editCouponPatch.trial_days || 0),
          max_uses: Number(editCouponPatch.max_uses || 0),
          expires_at_iso: String(editCouponPatch.expires_at_iso || '').trim(),
          razorpay_plan_id_in: String(editCouponPatch.razorpay_plan_id_in || '').trim(),
          razorpay_plan_id_intl: String(editCouponPatch.razorpay_plan_id_intl || '').trim(),
          display_price_new: String(editCouponPatch.display_price_new || '').trim(),
          display_period: String(editCouponPatch.display_period || '').trim(),
          display_price_new_intl: String(editCouponPatch.display_price_new_intl || '').trim(),
          display_period_intl: String(editCouponPatch.display_period_intl || '').trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update coupon');
      }
      setEditingCouponCode('');
      await loadCoupons();
      alert('Coupon updated');
    } catch (e) {
      alert(e?.message || 'Failed to update coupon');
    }
  };

  const deleteCoupon = async (code) => {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return;
    if (!window.confirm(`Delete coupon ${normalized}? This cannot be undone.`)) return;
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/coupons/${encodeURIComponent(normalized)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete coupon');
      }
      await loadCoupons();
    } catch (e) {
      alert(e?.message || 'Failed to delete coupon');
    }
  };

  const toggleCouponActive = async (code, nextActive) => {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return;
    try {
      const freshToken = await getFreshTokenRef.current();
      const res = await fetch(`/api/admin/coupons/${encodeURIComponent(normalized)}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: Boolean(nextActive) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update coupon');
      }
      await loadCoupons();
    } catch (e) {
      alert(e?.message || 'Failed to update coupon');
    }
  };

  const toggleRazorpaySubscriptionCheckout = async () => {
    try {
      setLoadingRazorpayToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/razorpay-subscription-toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !razorpayCheckoutEnabled }),
      });
      const data = await response.json();
      if (response.ok) {
        setRazorpayCheckoutEnabled(data.checkout_enabled !== false);
        setRazorpayEnvEnabled(data.env_enabled !== false);
        alert(data.message || `Razorpay subscription checkout ${data.checkout_enabled ? 'enabled' : 'disabled'}`);
      } else {
        alert(data.error || 'Failed to toggle Razorpay subscription checkout');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to toggle'}`);
    } finally {
      setLoadingRazorpayToggle(false);
    }
  };

  const setRazorpayBillingMode = async (nextMode) => {
    const normalizedMode = nextMode === 'live' ? 'live' : 'test';
    try {
      setLoadingRazorpayMode(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/razorpay-mode', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode: normalizedMode }),
      });
      const data = await response.json();
      if (response.ok) {
        setRazorpayMode(data.mode === 'live' ? 'live' : 'test');
        setRazorpayModeReady(data.mode_ready !== false);
        setRazorpayEnvEnabled(data.env_enabled !== false);
        alert(data.message || `Razorpay mode switched to ${normalizedMode}`);
      } else {
        alert(data.error || 'Failed to switch Razorpay mode');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to switch mode'}`);
    } finally {
      setLoadingRazorpayMode(false);
    }
  };

  const toggleLambda = async () => {
    try {
      setLoadingLambdaToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/lambda-toggle', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !lambdaEnabled }),
      });
      const data = await response.json();
      if (response.ok) {
        setLambdaEnabled(data.enabled === true);
        setLambdaEnvConfigured(data.env_configured !== false);
        setLambdaMissingEnvFields(Array.isArray(data.missing_env_fields) ? data.missing_env_fields : []);
        alert(data.message || `Remotion Lambda ${data.enabled ? 'enabled' : 'disabled'}`);
      } else {
        alert(data.error || 'Failed to toggle Remotion Lambda');
      }
    } catch (err) {
      alert('Error: ' + (err.message || 'Failed to toggle'));
    } finally {
      setLoadingLambdaToggle(false);
    }
  };
  
  const addCookie = async () => {
    if (!newCookieName.trim() || !newCookieContent.trim()) {
      alert('Please provide both name and content');
      return;
    }
    
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/youtube/cookies', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newCookieName.trim(),
          content: newCookieContent.trim(),
        }),
      });
      
      const data = await response.json();
      if (response.ok) {
        alert('Cookie added successfully');
        setNewCookieName('');
        setNewCookieContent('');
        loadCookies();
      } else {
        alert(`Error: ${data.error || 'Failed to add cookie'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };
  
  const updateCookie = async (cookieName, content) => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch(`/api/admin/youtube/cookies/${encodeURIComponent(cookieName)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });
      
      const data = await response.json();
      if (response.ok) {
        alert('Cookie updated successfully');
        setEditingCookie(null);
        // Reload cookies to get updated usage count
        loadCookies();
      } else {
        alert(`Error: ${data.error || 'Failed to update cookie'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };
  
  const deleteCookie = async (cookieName) => {
    if (!confirm(`Are you sure you want to delete cookie "${cookieName}"?`)) {
      return;
    }
    
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch(`/api/admin/youtube/cookies/${encodeURIComponent(cookieName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      if (response.ok) {
        alert('Cookie deleted successfully');
        loadCookies();
      } else {
        alert(`Error: ${data.error || 'Failed to delete cookie'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };
  
  const checkCookie = async (cookieName) => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch(`/api/admin/youtube/cookies/${encodeURIComponent(cookieName)}/check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      if (data.valid) {
        alert('Cookie is valid');
      } else {
        alert(`Cookie is expired or invalid: ${data.error || 'Unknown error'}`);
      }
      // Reload cookies to get updated usage count
      loadCookies();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };
  
  const toggleYoutubeUrl = async () => {
    try {
      setLoadingToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/youtube-url-toggle', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !youtubeUrlEnabled }),
      });
      
      const data = await response.json();
      if (response.ok) {
        setYoutubeUrlEnabled(data.enabled);
        alert(data.message || `YouTube URL feature ${data.enabled ? 'enabled' : 'disabled'}`);
      } else {
        alert(`Error: ${data.error || 'Failed to toggle'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingToggle(false);
    }
  };

  const toggleTrimSilenceFeature = async () => {
    try {
      setLoadingTrimSilenceToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/trim-silence-toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !trimSilenceFeatureEnabled }),
      });
      const data = await response.json();
      if (response.ok) {
        setTrimSilenceFeatureEnabled(data.enabled !== false);
        alert(data.message || `Trim silence processing ${data.enabled ? 'enabled' : 'disabled'}`);
      } else {
        alert(`Error: ${data.error || 'Failed to toggle'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingTrimSilenceToggle(false);
    }
  };

  const toggleBrollDefault = async () => {
    try {
      setLoadingBrollDefaultToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/broll-default-toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !includeBrollDefaultEnabled }),
      });
      const data = await response.json();
      if (response.ok) {
        setIncludeBrollDefaultEnabled(data.enabled !== false);
        alert(data.message || `Add B-roll default ${data.enabled ? 'on' : 'off'}`);
      } else {
        alert(`Error: ${data.error || 'Failed to toggle'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingBrollDefaultToggle(false);
    }
  };

  const toggleStreakPushScheduler = async () => {
    try {
      setLoadingStreakPushToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/streak-push-scheduler-toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !streakPushSchedulerEnabled }),
      });
      const data = await response.json();
      if (response.ok) {
        const enabled = data.enabled !== false;
        setStreakPushSchedulerEnabled(enabled);
        setPushStats((prev) => (prev ? { ...prev, scheduler_enabled: enabled } : prev));
        alert(data.message || `Streak push scheduler ${enabled ? 'enabled' : 'disabled'}`);
      } else {
        alert(`Error: ${data.error || 'Failed to toggle'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingStreakPushToggle(false);
    }
  };

  const previewSubscriptionReconcile = async () => {
    try {
      setSubscriptionReconcileLoading(true);
      setSubscriptionReconcilePreview(null);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/subscriptions/reconcile-expired', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dry_run: true }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error || 'Preview failed'}`);
        return;
      }
      setSubscriptionReconcilePreview(data);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubscriptionReconcileLoading(false);
    }
  };

  const applySubscriptionReconcile = async () => {
    if (!subscriptionReconcilePreview?.candidate_count) return;
    const n = subscriptionReconcilePreview.candidate_count;
    const ok = window.confirm(
      `Update subscription_status to expired for ${n} user${n === 1 ? '' : 's'}? This writes to Firestore.`
    );
    if (!ok) return;

    try {
      setSubscriptionReconcileApplying(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/subscriptions/reconcile-expired', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dry_run: false }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error || 'Apply failed'}`);
        return;
      }
      setSubscriptionReconcilePreview(data);
      alert(
        `Done. Updated ${data.updated ?? 0} user${(data.updated ?? 0) === 1 ? '' : 's'}` +
          ((data.skipped ?? 0) > 0 ? ` (${data.skipped} skipped — changed since preview)` : '')
      );
      usersLoadedRef.current = false;
      cachedUsersRef.current = null;
      await loadAllUsersRef.current?.();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubscriptionReconcileApplying(false);
    }
  };

  const previewCouponReconcile = async () => {
    try {
      setCouponReconcileLoading(true);
      setCouponReconcilePreview(null);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/coupons/reconcile-expired-trials', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dry_run: true }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error || 'Preview failed'}`);
        return;
      }
      setCouponReconcilePreview(data);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setCouponReconcileLoading(false);
    }
  };

  const applyCouponReconcile = async () => {
    if (!couponReconcilePreview?.candidate_count) return;
    const n = couponReconcilePreview.candidate_count;
    const ok = window.confirm(
      `Clear stale coupon premium metadata for ${n} user${n === 1 ? '' : 's'}? ` +
        'coupon_code_applied is kept; premium_source will be cleared.'
    );
    if (!ok) return;

    try {
      setCouponReconcileApplying(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/coupons/reconcile-expired-trials', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dry_run: false }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error || 'Apply failed'}`);
        return;
      }
      setCouponReconcilePreview(data);
      alert(
        `Done. Updated ${data.updated ?? 0} user${(data.updated ?? 0) === 1 ? '' : 's'}` +
          ((data.skipped ?? 0) > 0 ? ` (${data.skipped} skipped — changed since preview)` : '')
      );
      usersLoadedRef.current = false;
      cachedUsersRef.current = null;
      await loadAllUsersRef.current?.();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setCouponReconcileApplying(false);
    }
  };

  const toggleChatwootTemplate = async (templateName) => {
    try {
      setLoadingChatwootToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const currentEnabled = chatwootTemplatesEnabled[templateName];
      const response = await fetch('/api/admin/features/chatwoot-templates-toggle', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          template_name: templateName,
          enabled: !currentEnabled 
        }),
      });
      
      const data = await response.json();
      if (response.ok) {
        setChatwootTemplatesEnabled(prev => ({
          ...prev,
          [templateName]: data.enabled
        }));
        alert(data.message || `Chatwoot ${templateName} template ${data.enabled ? 'enabled' : 'disabled'}`);
      } else {
        alert(`Error: ${data.error || 'Failed to toggle'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingChatwootToggle(false);
    }
  };

  const togglePhoneDownloadRequirement = async () => {
    try {
      setLoadingPhoneDownloadToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/phone-download-toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !requirePhoneForDownload }),
      });

      const data = await response.json();
      if (response.ok) {
        setRequirePhoneForDownload(data.enabled !== false);
        alert(data.message || `Phone requirement for download ${data.enabled ? 'enabled' : 'disabled'}`);
      } else {
        alert(data.error || 'Failed to toggle phone download requirement');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to toggle'}`);
    } finally {
      setLoadingPhoneDownloadToggle(false);
    }
  };

  const togglePricingInviteCard = async () => {
    try {
      setLoadingPricingInviteToggle(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/features/pricing-invite-card-toggle', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !pricingInviteCardEnabled }),
      });
      const data = await response.json();
      if (response.ok) {
        setPricingInviteCardEnabled(data.enabled !== false);
        alert(data.message || `Pricing invite card ${data.enabled ? 'shown' : 'hidden'}`);
      } else {
        alert(data.error || 'Failed to toggle pricing invite card');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to toggle'}`);
    } finally {
      setLoadingPricingInviteToggle(false);
    }
  };

  // Marketing Dashboard functions
  const [allTemplateNames, setAllTemplateNames] = useState([]);

  const applyMarketingUsersList = useCallback((users, channel) => {
    const ch = channel === 'web_push' ? 'web_push' : 'whatsapp';
    setAllTemplateNames(collectMarketingTemplateNames(users, ch));
    setAllMarketingUsers(users);
  }, []);

  const mergeMarketingUsersIntoLoginCache = useCallback((marketingUsers) => {
    if (!cachedUsersRef.current?.users?.length || !marketingUsers?.length) return;
    const byId = new Map(marketingUsers.map((u) => [u.user_id, u]));
    cachedUsersRef.current = {
      ...cachedUsersRef.current,
      users: cachedUsersRef.current.users.map((u) => {
        const patch = byId.get(u.user_id);
        return patch ? { ...u, ...patch } : u;
      }),
    };
  }, []);

  const hydrateMarketingUsersFromCache = useCallback(
    (channel) => {
      const cached = cachedUsersRef.current?.users;
      if (!cached?.length) return false;
      applyMarketingUsersList(filterUsersForMarketingChannel(cached, channel), channel);
      return true;
    },
    [applyMarketingUsersList]
  );

  const fetchMarketingUsersFromServer = useCallback(
    async (channel) => {
      const freshToken = await getFreshTokenRef.current();
      const ch = channel === 'web_push' ? 'web_push' : 'whatsapp';
      const response = await fetch(
        `/api/admin/marketing/users?channel=${encodeURIComponent(ch)}`,
        {
          headers: {
            Authorization: `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        const users = data.users || [];
        mergeMarketingUsersIntoLoginCache(users);
        applyMarketingUsersList(users, ch);
        return true;
      }
      const err = await response.json().catch(() => ({}));
      alert(`Error loading users: ${err.error || response.status}`);
      return false;
    },
    [applyMarketingUsersList, mergeMarketingUsersIntoLoginCache]
  );

  /** Open tab: filter login cache (0 reads). Refresh: channel API only. */
  const loadMarketingUsers = useCallback(
    async (channel = marketingChannel, { forceRefresh = false, clearSelection = false } = {}) => {
      const ch = channel === 'web_push' ? 'web_push' : 'whatsapp';
      if (clearSelection) setSelectedUserIds(new Set());

      if (!forceRefresh && hydrateMarketingUsersFromCache(ch)) {
        return;
      }

      if (!forceRefresh && !cachedUsersRef.current?.users?.length) {
        if (loadAllUsersRef.current) {
          await loadAllUsersRef.current();
        }
        if (hydrateMarketingUsersFromCache(ch)) {
          return;
        }
      }

      try {
        setLoadingMarketingUsers(true);
        await fetchMarketingUsersFromServer(ch);
      } catch (err) {
        console.error('Error loading marketing users:', err);
        alert(`Error: ${err.message}`);
      } finally {
        setLoadingMarketingUsers(false);
      }
    },
    [marketingChannel, hydrateMarketingUsersFromCache, fetchMarketingUsersFromServer]
  );

  // Apply filters to all users (client-side filtering)
  const applyMarketingFilters = useCallback((usersToFilter = allMarketingUsers) => {
    if (usersToFilter.length === 0) {
      setMarketingUsers([]);
      return;
    }

    let filtered = [...usersToFilter];

    // Filter: Last Active
    if (marketingFilters.last_active.operator && marketingFilters.last_active.operator !== 'none' && marketingFilters.last_active.days) {
      try {
        const days = parseInt(marketingFilters.last_active.days);
        const now = new Date();
        filtered = filtered.filter(user => {
          if (!user.last_active) {
            return marketingFilters.last_active.operator === '<'; // Include users with no last_active for "<" (less than X days = inactive)
          }
          const userDate = new Date(user.last_active);
          const diffDays = Math.floor((now - userDate) / (1000 * 60 * 60 * 24));
          
          if (marketingFilters.last_active.operator === '>') {
            return diffDays > days; // More than X days ago
          } else if (marketingFilters.last_active.operator === '<') {
            return diffDays < days; // Less than X days ago
          }
          return true;
        });
      } catch (e) {
        console.error('Error filtering by last_active:', e);
      }
    }

    const isWebPushChannel = marketingChannel === 'web_push';

    // Filter: Last Marketing Message
    if (marketingFilters.last_marketing.operator && marketingFilters.last_marketing.operator !== 'none' && marketingFilters.last_marketing.days) {
      try {
        const days = parseInt(marketingFilters.last_marketing.days);
        const now = new Date();
        filtered = filtered.filter(user => {
          const lastTime = isWebPushChannel
            ? user.last_web_push_marketing_time
            : user.last_marketing_time;
          if (!lastTime) {
            return marketingFilters.last_marketing.operator === '<';
          }
          const userDate = new Date(lastTime);
          const diffDays = Math.floor((now - userDate) / (1000 * 60 * 60 * 24));

          if (marketingFilters.last_marketing.operator === '>') {
            return diffDays > days;
          } else if (marketingFilters.last_marketing.operator === '<') {
            return diffDays < days;
          }
          return true;
        });
      } catch (e) {
        console.error('Error filtering by last_marketing:', e);
      }
    }

    // Filter: Last Template (multi-select)
    if (marketingFilters.last_template && marketingFilters.last_template.length > 0 && !marketingFilters.last_template.includes('all')) {
      filtered = filtered.filter(user => {
        const templatesSent = isWebPushChannel
          ? (user.web_push_marketing_sent || [])
          : (user.whatsapp_template_sent || []);
        return marketingFilters.last_template.some(template => templatesSent.includes(template));
      });
    }

    // Filter: Message Count
    if (marketingFilters.message_count.operator && marketingFilters.message_count.operator !== 'none' && marketingFilters.message_count.value) {
      try {
        const filterCount = parseInt(marketingFilters.message_count.value);
        filtered = filtered.filter(user => {
          const userCount = isWebPushChannel
            ? (user.web_push_marketing_count || 0)
            : (user.message_template_sent_count || 0);
          if (marketingFilters.message_count.operator === '>') {
            return userCount > filterCount;
          } else if (marketingFilters.message_count.operator === '<') {
            return userCount < filterCount;
          }
          return true;
        });
      } catch (e) {
        console.error('Error filtering by message_count:', e);
      }
    }

    // Filter: Name
    if (marketingFilters.name) {
      const searchName = marketingFilters.name.toLowerCase();
      filtered = filtered.filter(user => {
        const userName = (user.name || '').toLowerCase();
        return userName.includes(searchName);
      });
    }

    // Filter: Email
    if (marketingFilters.email) {
      const searchEmail = marketingFilters.email.toLowerCase();
      filtered = filtered.filter(user => {
        const userEmail = (user.email || '').toLowerCase();
        return userEmail.includes(searchEmail);
      });
    }

    // Filter: Premium (aligned with Firestore users tab)
    const rawPremiumFilter = marketingFilters.premiumFilter || 'all';
    const pf = rawPremiumFilter === 'subscription' ? 'active' : rawPremiumFilter;
    if (pf === 'premium') {
      filtered = filtered.filter((user) => {
        const status = getEffectiveSubscriptionStatus(user).toLowerCase();
        const hasActiveSubscription = status === 'active';
        const hasActiveCoupon = isCouponTrialActive(user);
        return hasActiveSubscription || hasActiveCoupon;
      });
    }
    if (pf === 'any-subscription') {
      filtered = filtered.filter((user) => {
        const hasStatus = String(user.subscription_status || '').trim() !== '';
        const hasSubId = String(user.razorpay_subscription_id || '').trim() !== '';
        const hasSubCreated = String(user.subscription_created_at || '').trim() !== '';
        const hasSubStart = user.subscription_current_start != null || String(user.subscription_current_start_iso || '').trim() !== '';
        const hasSubEnd = user.subscription_current_end != null || String(user.subscription_current_end_iso || '').trim() !== '';
        const hasAnyRazorpayEvent = String(user.last_razorpay_event || '').trim() !== '';
        return hasStatus || hasSubId || hasSubCreated || hasSubStart || hasSubEnd || hasAnyRazorpayEvent;
      });
    }
    if (pf === 'coupon') {
      filtered = filtered.filter((user) => {
        const code = user.coupon_code_applied;
        return code != null && String(code).trim() !== '';
      });
    }
    if (pf === 'cancel-at-period-end') {
      filtered = filtered.filter(isCancelAtPeriodEndUser);
    }
    const subscriptionStatusExactFilters = new Set(['active', 'halted', 'pending', 'paused', 'created', 'cancelled', 'expired']);
    if (subscriptionStatusExactFilters.has(pf)) {
      const want = pf;
      filtered = filtered.filter(
        (user) => String(user.subscription_status || '').toLowerCase() === want
      );
    }

    // Filter: Created Date Range
    const [createdFrom, createdTo] = marketingFilters.created_range || [null, null];
    if (createdFrom || createdTo) {
      filtered = filtered.filter(user => {
        if (!user.created_at) return false;
        const createdTimestamp = new Date(user.created_at).getTime();
        const fromTime = createdFrom ? new Date(createdFrom).setHours(0, 0, 0, 0) : null;
        const toTime = createdTo ? new Date(createdTo).setHours(23, 59, 59, 999) : null;
        
        if (fromTime && createdTimestamp < fromTime) return false;
        if (toTime && createdTimestamp > toTime) return false;
        return true;
      });
    }

    setMarketingUsers(filtered);
  }, [marketingFilters, allMarketingUsers, marketingChannel]);

  useEffect(() => {
    if (activeTab === 'marketing' && allMarketingUsers.length > 0) {
      applyMarketingFilters(allMarketingUsers);
    }
  }, [marketingChannel, activeTab, allMarketingUsers, applyMarketingFilters]);

  const loadMarketingTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch(
        `/api/admin/marketing/templates?channel=${encodeURIComponent(marketingChannel)}`,
        {
          headers: {
            'Authorization': `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMarketingTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Error loading marketing templates:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingTemplates(false);
    }
  }, [marketingChannel]);

  const saveMarketingTemplate = async () => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const url = editingTemplate 
        ? `/api/admin/marketing/templates/${editingTemplate.template_id}`
        : '/api/admin/marketing/templates';
      
      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateForm),
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(data.message || 'Template saved successfully');
        setShowTemplateModal(false);
        setEditingTemplate(null);
        setTemplateForm(
          marketingChannel === 'web_push' ? emptyWebPushTemplateForm() : emptyWhatsappTemplateForm()
        );
        loadMarketingTemplates();
      } else {
        alert(`Error: ${data.error || 'Failed to save template'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const deleteMarketingTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch(`/api/admin/marketing/templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(data.message || 'Template deleted successfully');
        loadMarketingTemplates();
      } else {
        alert(`Error: ${data.error || 'Failed to delete template'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const sendMarketingMessages = async (templateId) => {
    if (selectedUserIds.size === 0) {
      alert('Please select at least one user');
      return;
    }
    
    if (!templateId) {
      alert('Please select a template');
      return;
    }
    
    try {
      setSendingMessages(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/marketing/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_ids: Array.from(selectedUserIds),
          template_id: templateId
        }),
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(data.message || `Sent ${data.results.success.length} messages successfully`);
        setSelectedUserIds(new Set());
        setSelectedTemplateId('');
      } else {
        alert(`Error: ${data.error || 'Failed to send messages'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSendingMessages(false);
    }
  };

  const toggleUserSelection = (userId) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUserIds(newSelection);
  };

  const selectAllUsers = () => {
    const sortedUsers = getSortedMarketingUsers();
    if (selectedUserIds.size === sortedUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(sortedUsers.map(u => u.user_id)));
    }
  };

  const clearMarketingFilters = () => {
    setMarketingFilters({
      last_active: { operator: 'none', days: '' },
      last_marketing: { operator: 'none', days: '' },
      last_template: [],
      message_count: { operator: 'none', value: '' },
      name: '',
      email: '',
      created_range: [null, null],
      premiumFilter: 'all',
    });
  };

  const handleMarketingSort = (field) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        // Toggle direction if same field
        return {
          field: field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // New field, default to ascending
        return {
          field: field,
          direction: 'asc'
        };
      }
    });
  };

  const getSortedMarketingUsers = () => {
    if (!sortConfig.field) {
      return marketingUsers;
    }
    
    const sorted = [...marketingUsers].sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];
      
      // Handle dates
      if (sortConfig.field === 'last_active') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (sortConfig.field === 'message_template_sent_count') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      } else if (sortConfig.field === 'subscription_status') {
        aVal = (a.subscription_status || a.plan_tier || '').toLowerCase();
        bVal = (b.subscription_status || b.plan_tier || '').toLowerCase();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };


  const calculateDaysAgo = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (e) {
      return null;
    }
  };

  // Memoize loadCustomers to prevent recreation on every render
  const loadCustomers = useCallback(async () => {
    try {
      setLoadingFiles(true);
      const freshToken = await getFreshTokenRef.current();
      setStreamTokenForAssets(freshToken);
      const [response, recentRes] = await Promise.all([
        fetch('/api/admin/customers', {
          headers: {
            Authorization: `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch('/api/admin/files/recent?limit=25', {
          headers: {
            Authorization: `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
        setSelectedFileKeys(new Set());
      }
      if (recentRes.ok) {
        const recentData = await recentRes.json();
        setRecentFiles(recentData.files || []);
      } else {
        setRecentFiles([]);
      }
    } catch (err) {
      // Error loading customers - silently fail
      setRecentFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, []); // Empty deps - using ref for getFreshToken

  const loadPushStats = useCallback(async () => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/push/stats?days=7', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPushStats(data);
      }
    } catch (err) {
      console.debug('Push stats load failed:', err);
    }
  }, []);

  const loadConnectedAccountsSummary = useCallback(async ({ force = false } = {}) => {
    if (!force && connectedAccountsLoadedRef.current) return;
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/firestore/connected-accounts-summary', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setConnectedAccountsByUser(data.by_user || {});
        connectedAccountsLoadedRef.current = true;
        setConnectedAccountsLoaded(true);
      }
    } catch (err) {
      console.debug('loadConnectedAccountsSummary failed:', err);
    }
  }, []);

  const applyUsersCacheToState = useCallback((data) => {
    if (!data) return;
    cachedUsersRef.current = data;
    const emailMap = {};
    const metaMap = {};
    (data.users || []).forEach((user) => {
      if (!user.user_id) return;
      if (user.email) emailMap[user.user_id] = user.email;
      metaMap[user.user_id] = {
        created_at: user.created_at,
        last_active: user.last_active,
        updated_at: user.updated_at,
      };
    });
    setUserEmailCache(emailMap);
    setUserMetaById(metaMap);
    setUsers(data);
    const allUserIds = new Set((data.users || []).map((u) => u.user_id));
    setCollapsedItems((prev) => ({ ...prev, users: allUserIds }));
    usersLoadedRef.current = true;
  }, []);

  // Login: full users fetch once per session (email cache + Users tab data)
  const loadAllUsers = useCallback(async () => {
    if (usersLoadedRef.current && cachedUsersRef.current) {
      applyUsersCacheToState(cachedUsersRef.current);
      return;
    }
    try {
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/firestore/users', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        applyUsersCacheToState(data);
      }
    } catch (err) {
      console.debug('loadAllUsers failed:', err);
    }
  }, [applyUsersCacheToState]);

  loadAllUsersRef.current = loadAllUsers;

  const refreshFirestoreUsers = useCallback(async () => {
    try {
      setLoadingFirestore(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/firestore/users', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        applyUsersCacheToState(data);
      }
      await loadConnectedAccountsSummary({ force: true });
    } catch (err) {
      console.error('refreshFirestoreUsers failed:', err);
    } finally {
      setLoadingFirestore(false);
    }
  }, [applyUsersCacheToState, loadConnectedAccountsSummary]);

  const hydrateFirestoreUsersFromCache = useCallback(() => {
    if (cachedUsersRef.current) {
      applyUsersCacheToState(cachedUsersRef.current);
      return true;
    }
    if (usersLoadedRef.current) return true;
    return false;
  }, [applyUsersCacheToState]);

  const applyTasksPayload = useCallback((data, { collapseAll = false } = {}) => {
    setTasks(data);
    setTasksHasMore(Boolean(data?.has_more));
    tasksPageCursorRef.current = data?.page_cursor || null;
    if (collapseAll) {
      setCollapsedItems((prev) => ({ ...prev, tasks: collapsedTaskIdsFromPayload(data) }));
    }
  }, []);

  const fetchTasksRecent = useCallback(async ({ force = false } = {}) => {
    if (!force && tasks) {
      return;
    }
    try {
      setLoadingFirestore(true);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/firestore/tasks?limit=25', {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        applyTasksPayload(data, { collapseAll: true });
      }
    } catch (err) {
      console.error('fetchTasksRecent failed:', err);
    } finally {
      setLoadingFirestore(false);
    }
  }, [tasks, applyTasksPayload]);

  const loadTasksRemaining = useCallback(async () => {
    const cursor = tasksPageCursorRef.current;
    if (!cursor || !tasksHasMore) return;
    try {
      setLoadingTasksRemaining(true);
      const freshToken = await getFreshTokenRef.current();
      const qs = new URLSearchParams({
        load_remaining: '1',
        start_after_task_id: cursor,
      });
      const response = await fetch(`/api/admin/firestore/tasks?${qs}`, {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const merged = mergeAdminTasksPayload(tasks, data);
        applyTasksPayload(merged, { collapseAll: false });
      }
    } catch (err) {
      console.error('loadTasksRemaining failed:', err);
    } finally {
      setLoadingTasksRemaining(false);
    }
  }, [tasks, tasksHasMore, applyTasksPayload]);

  const refreshFirestoreTasks = useCallback(async () => {
    tasksPageCursorRef.current = null;
    setTasks(null);
    setTasksHasMore(false);
    await fetchTasksRecent({ force: true });
  }, [fetchTasksRecent]);

  /** Re-fetch one task from Firestore and merge into local tasks state (no full list reload). */
  const refreshSingleFirestoreTask = useCallback(async (taskId) => {
    if (!taskId) return;
    setTaskSingleRefreshIds((prev) => new Set(prev).add(taskId));
    try {
      const freshToken = await getFreshTokenRef.current();
      const encoded = encodeURIComponent(taskId);
      const response = await fetch(`/api/admin/firestore/tasks/${encoded}`, {
        headers: {
          Authorization: `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(data.error || `Failed to refresh task (${response.status})`);
        return;
      }
      const t = data.task;
      if (!t || !t.task_id) {
        alert('Invalid task response');
        return;
      }
      setTasks((prev) => {
        if (!prev || !prev.tasks_by_user) return prev;
        const tasksByUser = { ...prev.tasks_by_user };
        let updated = false;
        for (const userId of Object.keys(tasksByUser)) {
          const userTasks = tasksByUser[userId];
          const idx = userTasks.findIndex((x) => x.task_id === t.task_id);
          if (idx !== -1) {
            const nextArr = [...userTasks];
            nextArr[idx] = { ...t };
            tasksByUser[userId] = nextArr;
            updated = true;
            break;
          }
        }
        if (!updated) return prev;
        return { ...prev, tasks_by_user: tasksByUser };
      });
    } catch (err) {
      alert(err?.message || 'Error refreshing task');
    } finally {
      setTaskSingleRefreshIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  }, []);

  // Load logs for selected date
  const loadLogs = useCallback(async (date) => {
    pageScrollBeforeLogsRefreshRef.current = window.scrollY;
    try {
      setLoadingLogs(true);
      const freshToken = await getFreshTokenRef.current();
      const dateParam = date || selectedDate;
      const response = await fetch(`/api/logs?date=${encodeURIComponent(dateParam)}`, {
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (response.ok) {
        // Check if log_file exists and is a valid object with filename
        if (data && data.log_file && typeof data.log_file === 'object' && data.log_file !== null && data.log_file.filename) {
          setLogFile(data.log_file);
        } else {
          // File doesn't exist or is invalid
          setLogFile(null);
        }
      } else {
        // Error response
        setLogFile(null);
      }
    } catch (err) {
      // Error loading logs - silently fail
      setLogFile(null);
    } finally {
      setLoadingLogs(false);
    }
  }, [selectedDate]); // Include selectedDate in deps

  const handleAnalyzeLogTasks = useCallback(() => {
    if (!logFile?.content) return;
    const tasks = parseLogProcessingTasks(logFile.content);
    setLogAnalyzedTasks(tasks);
    setSelectedLogTaskKey('');
  }, [logFile?.content]);

  const handleLogTaskSelect = useCallback(
    (e) => {
      const key = e.target.value;
      setSelectedLogTaskKey(key);
      if (!key || showErrorLogsOnly) return;
      const task = logAnalyzedTasks.find((t) => t.key === key);
      if (!task) return;
      requestAnimationFrame(() => {
        document
          .getElementById(`admin-log-line-${task.startLine}`)
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    [logAnalyzedTasks, showErrorLogsOnly]
  );

  const handleCopySelectedLogTaskText = useCallback(async () => {
    if (showErrorLogsOnly || !selectedLogTask) return;
    const { startLine, endLine } = selectedLogTask;
    const slice = logLinesForViewer.slice(startLine, endLine);
    const text = slice.join('\n');
    if (!text) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      if (logTaskCopyFlashTimerRef.current) {
        clearTimeout(logTaskCopyFlashTimerRef.current);
      }
      setLogTaskCopyFlash(true);
      logTaskCopyFlashTimerRef.current = window.setTimeout(() => {
        setLogTaskCopyFlash(false);
        logTaskCopyFlashTimerRef.current = null;
      }, 1600);
    } catch (_) {
      setLogTaskCopyFlash(false);
    }
  }, [selectedLogTask, logLinesForViewer, showErrorLogsOnly]);

  const goLogSearchPrev = useCallback(() => {
    setLogSearchMatchCursor((c) => {
      const n = logSearchMatchLineIndices.length;
      if (n <= 0) return 0;
      return (c - 1 + n) % n;
    });
  }, [logSearchMatchLineIndices]);

  const goLogSearchNext = useCallback(() => {
    setLogSearchMatchCursor((c) => {
      const n = logSearchMatchLineIndices.length;
      if (n <= 0) return 0;
      return (c + 1) % n;
    });
  }, [logSearchMatchLineIndices]);

  // Load timeline for selected session (declared before sessions loader so Refresh can refetch timeline)
  const loadUserJourneyTimeline = useCallback(async (sessionId, date) => {
    if (!sessionId) return;

    try {
      setLoadingTimeline(true);
      const freshToken = await getFreshTokenRef.current();
      const dateParam = date || selectedSessionDateRef.current || userJourneyDateTo;
      const response = await fetch(`/api/admin/user-journey/timeline?date=${encodeURIComponent(dateParam)}&session_id=${encodeURIComponent(sessionId)}`, {
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        if (data.email) {
          console.log('Timeline email found:', data.email);
        }
        setUserJourneyTimeline(data);
      } else {
        setUserJourneyTimeline(null);
        alert(data.error || 'Failed to load timeline');
      }
    } catch (err) {
      setUserJourneyTimeline(null);
      alert('Error loading timeline: ' + (err.message || 'Unknown error'));
    } finally {
      setLoadingTimeline(false);
    }
  }, [userJourneyDateTo]);

  // Load user journey sessions for selected date range (inclusive)
  const loadUserJourneySessions = useCallback(async (fromDate, toDate) => {
    try {
      setLoadingUserJourney(true);
      const freshToken = await getFreshTokenRef.current();
      const from = fromDate || userJourneyDateFrom;
      const to = toDate || userJourneyDateTo;
      const days = enumerateYmdDatesInclusive(from, to);
      if (days.length === 0) {
        setUserJourneySessions([]);
        setSelectedSessionId(null);
        setSelectedSessionDate(null);
        setUserJourneyTimeline(null);
        setUserJourneySessionFilter([]);
        setSelectedJourneySessionKeys(new Set());
        alert('Invalid User Journey date range');
        return;
      }

      const responses = await Promise.all(
        days.map(async (dateParam) => {
          const response = await fetch(`/api/admin/user-journey/sessions?date=${encodeURIComponent(dateParam)}`, {
            headers: {
              'Authorization': `Bearer ${freshToken}`,
              'Content-Type': 'application/json',
            },
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || `Failed to load sessions for ${dateParam}`);
          }
          return {
            date: dateParam,
            sessions: Array.isArray(data.sessions) ? data.sessions : [],
          };
        })
      );

      const mergedSessions = responses
        .flatMap(({ date, sessions }) =>
          sessions.map((session) => ({
            ...session,
            source_date: date,
            session_key: `${date}::${session.session_id}`,
          }))
        )
        .sort((a, b) => {
          const ta = new Date(a.first_event_time || 0).getTime();
          const tb = new Date(b.first_event_time || 0).getTime();
          return tb - ta;
        });

      setUserJourneySessions(mergedSessions);
      const currentSelectedSessionId = selectedSessionIdRef.current;
      const currentSelectedSessionDate = selectedSessionDateRef.current;
      const hasSavedSelection = mergedSessions.some(
        (s) =>
          s.session_id === currentSelectedSessionId &&
          (s.source_date || null) === (currentSelectedSessionDate || null)
      );
      if (!hasSavedSelection) {
        setSelectedSessionId(null);
        setSelectedSessionDate(null);
        setUserJourneyTimeline(null);
      } else if (currentSelectedSessionId) {
        await loadUserJourneyTimeline(currentSelectedSessionId, currentSelectedSessionDate || userJourneyDateTo);
      }
      setUserJourneySessionFilter([]);
      setSelectedJourneySessionKeys(new Set());
    } catch (err) {
      setUserJourneySessions([]);
      setSelectedSessionId(null);
      setSelectedSessionDate(null);
      setUserJourneyTimeline(null);
      setSelectedJourneySessionKeys(new Set());
      if (err?.message) {
        alert('Error loading sessions: ' + err.message);
      } else {
        alert('Error loading sessions: Unknown error');
      }
    } finally {
      setLoadingUserJourney(false);
    }
  }, [userJourneyDateFrom, userJourneyDateTo, loadUserJourneyTimeline]);

  const exportUserJourneyRange = useCallback(async () => {
    const days = enumerateYmdDatesInclusive(userJourneyDateFrom, userJourneyDateTo);
    if (days.length === 0) {
      alert('Invalid User Journey date range');
      return;
    }
    try {
      setExportingUserJourney(true);
      const freshToken = await getFreshTokenRef.current();
      const from = userJourneyDateFrom;
      const to = userJourneyDateTo;
      const response = await fetch(
        `/api/admin/user-journey/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        {
          headers: {
            Authorization: `Bearer ${freshToken}`,
          },
        }
      );
      if (!response.ok) {
        let msg = 'Export failed';
        try {
          const err = await response.json();
          if (err?.error) msg = err.error;
        } catch (_) {
          /* ignore */
        }
        alert(msg);
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-journey_${from}_to_${to}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setExportingUserJourney(false);
    }
  }, [userJourneyDateFrom, userJourneyDateTo]);

  // Delete a session from user journey
  const deleteUserJourneySession = useCallback(async (sessionId, sessionDate) => {
    if (!sessionId) return;
    const targetDate = sessionDate || selectedSessionDate || userJourneyDateTo;
    if (!targetDate) return;
    try {
      setDeletingSessionId(sessionId);
      const freshToken = await getFreshTokenRef.current();
      const response = await fetch('/api/admin/user-journey/session', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: targetDate,
          session_id: sessionId,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        if (selectedSessionId === sessionId) {
          setSelectedSessionId(null);
          setSelectedSessionDate(null);
          setUserJourneyTimeline(null);
        }
        setSelectedJourneySessionKeys((prev) => {
          const next = new Set(prev);
          next.delete(`${targetDate}::${sessionId}`);
          return next;
        });
        await loadUserJourneySessions(userJourneyDateFrom, userJourneyDateTo);
      } else {
        alert(data.error || 'Failed to delete session');
      }
    } catch (err) {
      alert('Error deleting session: ' + (err.message || 'Unknown error'));
    } finally {
      setDeletingSessionId(null);
    }
  }, [selectedSessionDate, userJourneyDateTo, selectedSessionId, loadUserJourneySessions, userJourneyDateFrom]);

  const deleteSelectedUserJourneySessions = useCallback(async (sessionsInView) => {
    const selectedSessions = (sessionsInView || []).filter((session) =>
      selectedJourneySessionKeys.has(session.session_key || `${session.source_date || ''}::${session.session_id}`)
    );
    if (selectedSessions.length === 0) return;
    const confirmed = window.confirm(`Delete ${selectedSessions.length} selected session(s)?`);
    if (!confirmed) return;

    try {
      setBatchDeletingJourneySessions(true);
      const freshToken = await getFreshTokenRef.current();
      let failed = 0;
      for (const session of selectedSessions) {
        const targetDate = session.source_date || selectedSessionDate || userJourneyDateTo;
        if (!targetDate || !session.session_id) {
          failed += 1;
          continue;
        }
        const response = await fetch('/api/admin/user-journey/session', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            date: targetDate,
            session_id: session.session_id,
          }),
        });
        if (!response.ok) {
          failed += 1;
        } else if (selectedSessionId === session.session_id && selectedSessionDate === (session.source_date || null)) {
          setSelectedSessionId(null);
          setSelectedSessionDate(null);
          setUserJourneyTimeline(null);
        }
      }

      await loadUserJourneySessions(userJourneyDateFrom, userJourneyDateTo);
      setSelectedJourneySessionKeys(new Set());
      if (failed > 0) {
        alert(`Deleted ${selectedSessions.length - failed}/${selectedSessions.length} sessions. ${failed} failed.`);
      }
    } catch (err) {
      alert('Error deleting selected sessions: ' + (err.message || 'Unknown error'));
    } finally {
      setBatchDeletingJourneySessions(false);
    }
  }, [selectedJourneySessionKeys, selectedSessionDate, userJourneyDateTo, selectedSessionId, loadUserJourneySessions, userJourneyDateFrom]);

  /**
   * Open Firestore → Tasks with filters aligned to this journey session:
   * - user email from session (if any)
   * - date range: local calendar days spanned by [journey file date, first_event_time, last_event_time]
   */
  const openTasksForJourneySession = useCallback(
    (session) => {
      const sessionDate = session?.source_date || selectedSessionDate || userJourneyDateTo;
      if (!sessionDate || !session) return;
      const parts = sessionDate.split('-').map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return;
      const [yy, mm, dd] = parts;
      const fileDayStart = new Date(yy, mm - 1, dd, 0, 0, 0, 0);
      const candidateTimes = [fileDayStart.getTime()];
      if (session.first_event_time) {
        candidateTimes.push(new Date(session.first_event_time).getTime());
      }
      if (session.last_event_time) {
        candidateTimes.push(new Date(session.last_event_time).getTime());
      }
      const minT = Math.min(...candidateTimes);
      const maxT = Math.max(...candidateTimes);
      const rangeStart = new Date(minT);
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(maxT);
      rangeEnd.setHours(0, 0, 0, 0);

      const email =
        session.email && String(session.email).trim() ? String(session.email).trim() : '';

      setFilters((prev) => ({
        ...prev,
        tasks: {
          ...prev.tasks,
          userEmail: email,
          dateRange: [rangeStart, rangeEnd],
        },
      }));
      setSelectedTaskIds(new Set());
      setNavigationState((prev) => ({
        ...prev,
        fromTask: null,
        previousCollection: null,
        previousTab: null,
        fromFileManagement: null,
        fromFirestoreUsers: null,
        fromUserJourney: {
          date: sessionDate,
          sessionId: session.session_id,
        },
      }));
      setActiveTab('firestore');
      setSelectedCollection('tasks');
    },
    [selectedSessionDate, userJourneyDateTo]
  );

  /** Open Firestore → Users filtered by session email (journey only has email after login). */
  const openUsersForJourneySession = useCallback(
    (session) => {
      const email =
        session?.email && String(session.email).trim() ? String(session.email).trim() : '';
      if (!email) return;
      setFilters((prev) => ({
        ...prev,
        users: {
          ...prev.users,
          email,
        },
      }));
      setNavigationState((prev) => ({
        ...prev,
        fromTask: null,
        previousCollection: null,
        previousTab: null,
        fromFileManagement: null,
        fromFirestoreUsers: null,
        fromUserJourney: {
          date: session?.source_date || selectedSessionDate || userJourneyDateTo,
          sessionId: session.session_id,
        },
      }));
      setActiveTab('firestore');
      setSelectedCollection('users');
    },
    [selectedSessionDate, userJourneyDateTo]
  );

  // Helper function to get email from user_id
  const getUserEmail = useCallback((userId) => {
    return userEmailCache[userId] || userId; // Return email if found, otherwise return user_id
  }, [userEmailCache]);

  // Helper function to get timestamp from Firestore date
  const getTimestamp = (dateField) => {
    if (!dateField) return 0;
    if (dateField.toDate) {
      return dateField.toDate().getTime();
    }
    if (dateField._seconds) {
      return dateField._seconds * 1000;
    }
    if (typeof dateField === 'string' || typeof dateField === 'number') {
      return new Date(dateField).getTime();
    }
    return 0;
  };

  /** True if user's account created date and last-activity date (local calendar) differ */
  const isReturningUser = useCallback(
    (userId) => {
      if (!userId) return false;
      const m = userMetaById[userId];
      if (!m || !m.created_at) return false;
      const last = m.last_active || m.updated_at;
      if (!last) return false;
      const t0 = getTimestamp(m.created_at);
      const t1 = getTimestamp(last);
      if (!t0 || !t1) return false;
      const d0 = new Date(t0);
      const d1 = new Date(t1);
      return (
        d0.getFullYear() !== d1.getFullYear() ||
        d0.getMonth() !== d1.getMonth() ||
        d0.getDate() !== d1.getDate()
      );
    },
    [userMetaById]
  );

  // Helper function to format "last active since" (time ago)
  const formatTimeAgo = (dateField) => {
    if (!dateField) return 'Never';
    
    const timestamp = getTimestamp(dateField);
    if (!timestamp) return 'Never';
    
    const now = Date.now();
    const diffMs = now - timestamp;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    
    if (diffYears > 0) {
      return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
    } else if (diffMonths > 0) {
      return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    } else if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} ${diffMinutes === 1 ? 'min' : 'mins'} ago`;
    } else {
      return 'Just now';
    }
  };

  // Toggle sort order
  const toggleSortOrder = (collection) => {
    setSortLatestFirst(prev => ({
      ...prev,
      [collection]: !prev[collection]
    }));
  };

  // Toggle user sort type (created or active)
  const toggleUserSortType = () => {
    setUserSortType(prev => prev === 'created' ? 'active' : 'created');
  };

  // Toggle collapse state for an item
  const toggleCollapse = (collection, itemId) => {
    setCollapsedItems(prev => {
      const newSet = new Set(prev[collection]);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return {
        ...prev,
        [collection]: newSet
      };
    });
  };

  // Update filter
  const updateFilter = (collection, filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [collection]: {
        ...prev[collection],
        [filterKey]: value
      }
    }));
  };

  /** Preset date range to the current local calendar day (start/end same day). */
  const applyDatePresetToday = (collection, rangeKey) => {
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    setFilters((prev) => ({
      ...prev,
      [collection]: {
        ...prev[collection],
        [rangeKey]: [start, end],
      },
    }));
  };

  // Clear all filters for a collection
  const clearFilters = (collection) => {
    if (collection === 'tasks') {
      setFilters(prev => ({
        ...prev,
        tasks: {
          status: '',
          userEmail: '',
          searchText: '',
          dateRange: [null, null],
          lambdaOnly: false,
          returningUsersOnly: false,
          deviceType: '',
          deviceOs: '',
        }
      }));
    } else if (collection === 'users') {
      setFilters(prev => ({
        ...prev,
        users: {
          email: '',
          name: '',
          phone: '',
          country: '',
          paidCount: 'all',
          streakFilter: 'all',
          hasVideos: null,
          hasPhone: 'none',
          premiumFilter: 'all',
          createdRange: [null, null],
          lastActiveRange: [null, null],
          remindersOnly: false,
          socialHandleOnly: false,
        }
      }));
    }
  };

  const handleBackToUserJourney = useCallback(
    (collection) => {
      const fj = navigationState.fromUserJourney;
      if (!fj) return;
      if (collection === 'tasks') {
        clearFilters('tasks');
      } else if (collection === 'users') {
        clearFilters('users');
      }
      setNavigationState((prev) => ({
        ...prev,
        fromUserJourney: null,
        fromFileManagement: null,
        fromFirestoreUsers: null,
      }));
      setUserJourneyDateFrom(fj.date);
      setUserJourneyDateTo(fj.date);
      setUserJourneyEmailScope('');
      setSelectedSessionId(fj.sessionId || null);
      setSelectedSessionDate(fj.date || null);
      setActiveTab('user-journey');
    },
    [navigationState.fromUserJourney]
  );

  const handleBackToFileManagement = useCallback((collection) => {
    if (!navigationState.fromFileManagement) return;
    if (collection === 'tasks') {
      setFilters((prev) => ({
        ...prev,
        tasks: { ...prev.tasks, userEmail: '' },
      }));
    } else if (collection === 'users') {
      setFilters((prev) => ({
        ...prev,
        users: { ...prev.users, email: '' },
      }));
    }
    setNavigationState((prev) => ({
      ...prev,
      fromFileManagement: null,
      fromFirestoreUsers: null,
    }));
    setActiveTab('files');
  }, [navigationState.fromFileManagement]);

  const handleBackToFirestoreUsers = useCallback(() => {
    const ctx = navigationState.fromFirestoreUsers;
    if (!ctx) return;
    setFilters((prev) => ({
      ...prev,
      users: { ...prev.users, email: ctx.email },
      tasks: { ...prev.tasks, userEmail: '' },
    }));
    setNavigationState((prev) => ({
      ...prev,
      fromFirestoreUsers: null,
    }));
    setActiveTab('firestore');
    setSelectedCollection('users');
    setTimeout(() => {
      setCollapsedItems((prev) => {
        const newSet = new Set(prev.users);
        newSet.delete(ctx.userId);
        return { ...prev, users: newSet };
      });
      setTimeout(() => {
        const element = document.querySelector(`[data-user-id="${ctx.userId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 80);
    }, 50);
  }, [navigationState.fromFirestoreUsers]);

  /** When user switched to Firestore → Users via tabs, restore Tasks or Files without clearing context. */
  const handleResumeFromFirestoreUsersDrill = useCallback(() => {
    const ctx = navigationState.fromFirestoreUsers;
    if (!ctx) return;
    const source = ctx.source === 'tasks' ? 'tasks' : 'files';
    if (source === 'tasks') {
      setActiveTab('firestore');
      setSelectedCollection('tasks');
      return;
    }
    setActiveTab('files');
    setSelectedCustomer(ctx.userId);
    setFilenameSearch('');
    setTimeout(() => {
      const element = document.querySelector(`[data-user-id="${ctx.userId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
  }, [navigationState.fromFirestoreUsers]);

  const openTasksFromFirestoreUserRow = useCallback((user) => {
    const userId = user?.user_id;
    const email = String((user?.email && String(user.email).trim()) || userId || '').trim();
    if (!email || !userId) return;
    setFilters((prev) => ({
      ...prev,
      tasks: { ...prev.tasks, userEmail: email },
    }));
    setNavigationState((prev) => ({
      ...prev,
      fromTask: null,
      previousCollection: null,
      previousTab: null,
      fromUserJourney: null,
      fromFileManagement: null,
      fromFirestoreUsers: { email, userId, source: 'tasks' },
    }));
    setSelectedTaskIds(new Set());
    setActiveTab('firestore');
    setSelectedCollection('tasks');
  }, []);

  const openFileManagementFromFirestoreUserRow = useCallback((user) => {
    const userId = user?.user_id;
    if (!userId) return;
    const email = String((user?.email && String(user.email).trim()) || userId || '').trim();
    setNavigationState((prev) => ({
      ...prev,
      fromTask: null,
      previousCollection: null,
      previousTab: null,
      fromUserJourney: null,
      fromFileManagement: null,
      fromFirestoreUsers: { email, userId, source: 'files' },
    }));
    setActiveTab('files');
    setSelectedCustomer(userId);
    setFilenameSearch('');
    setTimeout(() => {
      const element = document.querySelector(`[data-user-id="${userId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
  }, []);

  const openUserJourneyFromFirestoreUserRow = useCallback((user) => {
    const userId = user?.user_id;
    const email = String((user?.email && String(user.email).trim()) || '').trim();
    if (!email || !userId) return;
    const createdTs = getTimestamp(user?.created_at);
    const createdDate = createdTs > 0 ? new Date(createdTs) : new Date();
    createdDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = createdDate <= today ? createdDate : today;
    const to = today;
    setUserJourneyDateFrom(toYmdLocal(from));
    setUserJourneyDateTo(toYmdLocal(to));
    setUserJourneyEmailScope(email);
    setSelectedSessionId(null);
    setSelectedSessionDate(null);
    setUserJourneyTimeline(null);
    setUserJourneySessionFilter([]);
    setNavigationState((prev) => ({
      ...prev,
      fromTask: null,
      previousCollection: null,
      previousTab: null,
      fromUserJourney: null,
      fromFileManagement: null,
      fromFirestoreUsers: { email, userId, source: 'journey' },
    }));
    setActiveTab('user-journey');
  }, []);

  const openFirestoreUsersFromFileManagement = useCallback(
    (userId) => {
      const email = String(getUserEmail(userId) || '').trim();
      if (!email) return;
      setFilters((prev) => ({
        ...prev,
        users: { ...prev.users, email },
      }));
      setNavigationState((prev) => ({
        ...prev,
        fromTask: null,
        previousCollection: null,
        previousTab: null,
        fromUserJourney: null,
        fromFirestoreUsers: null,
        fromFileManagement: { email, userId, collection: 'users' },
      }));
      setSelectedTaskIds(new Set());
      setActiveTab('firestore');
      setSelectedCollection('users');
      setTimeout(() => {
        setCollapsedItems((prev) => {
          const newSet = new Set(prev.users);
          newSet.delete(userId);
          return { ...prev, users: newSet };
        });
        setTimeout(() => {
          const element = document.querySelector(`[data-user-id="${userId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      }, 100);
    },
    [getUserEmail]
  );

  const openFirestoreTasksFromFileManagement = useCallback(
    (userId) => {
      const email = String(getUserEmail(userId) || '').trim();
      if (!email) return;
      setFilters((prev) => ({
        ...prev,
        tasks: { ...prev.tasks, userEmail: email },
      }));
      setNavigationState((prev) => ({
        ...prev,
        fromTask: null,
        previousCollection: null,
        previousTab: null,
        fromUserJourney: null,
        fromFirestoreUsers: null,
        fromFileManagement: { email, userId, collection: 'tasks' },
      }));
      setSelectedTaskIds(new Set());
      setActiveTab('firestore');
      setSelectedCollection('tasks');
    },
    [getUserEmail]
  );

  // Get unique status values from tasks
  const getUniqueStatuses = () => {
    if (!tasks || !tasks.tasks_by_user) return [];
    const statuses = new Set();
    Object.values(tasks.tasks_by_user).forEach(userTasks => {
      userTasks.forEach(task => {
        const status = task.lifecycle_state || task.status;
        if (status) {
          statuses.add(status);
        }
      });
    });
    return Array.from(statuses).sort();
  };

  // Sort and flatten tasks by user with filters
  const getSortedTasks = () => {
    if (!tasks || !tasks.tasks_by_user) return [];
    
    const allTasks = [];
    Object.entries(tasks.tasks_by_user).forEach(([userId, userTasks]) => {
      userTasks.forEach(task => {
        allTasks.push({ ...task, userId });
      });
    });
    
    // Apply filters
    let filteredTasks = allTasks;
    const taskFilters = filters.tasks;
    
    // Filter by status
    if (taskFilters.status) {
      filteredTasks = filteredTasks.filter(task => {
        const status = (task.lifecycle_state || task.status || '').toLowerCase();
        return status === taskFilters.status.toLowerCase();
      });
    }
    
    // Filter by user email
    if (taskFilters.userEmail) {
      const searchEmail = taskFilters.userEmail.toLowerCase();
      filteredTasks = filteredTasks.filter(task => {
        const userEmail = getUserEmail(task.userId).toLowerCase();
        return userEmail.includes(searchEmail);
      });
    }
    
    // General search text filter
    if (taskFilters.searchText) {
      const searchText = taskFilters.searchText.toLowerCase();
      filteredTasks = filteredTasks.filter(task => {
        const taskId = (task.task_id || '').toLowerCase();
        const userEmail = getUserEmail(task.userId).toLowerCase();
        const status = ((task.lifecycle_state || task.status) || '').toLowerCase();
        return taskId.includes(searchText) || userEmail.includes(searchText) || status.includes(searchText);
      });
    }
    
    // Filter by date range (created_at or video_uploaded_at)
    const [dateFrom, dateTo] = taskFilters.dateRange || [null, null];
    if (dateFrom || dateTo) {
      filteredTasks = filteredTasks.filter(task => {
        const taskDate = task.video_uploaded_at || task.created_at;
        if (!taskDate) return false;
        
        const taskTimestamp = getTimestamp(taskDate);
        const fromTime = dateFrom ? new Date(dateFrom).setHours(0, 0, 0, 0) : null;
        const toTime = dateTo ? new Date(dateTo).setHours(23, 59, 59, 999) : null;
        
        if (fromTime && taskTimestamp < fromTime) return false;
        if (toTime && taskTimestamp > toTime) return false;
        
        return true;
      });
    }

    if (taskFilters.lambdaOnly) {
      filteredTasks = filteredTasks.filter((task) => hasTaskLambdaMeta(task));
    }

    if (taskFilters.returningUsersOnly) {
      filteredTasks = filteredTasks.filter((task) => isReturningUser(task.userId));
    }

    if (taskFilters.deviceType) {
      const wanted = taskFilters.deviceType.toLowerCase();
      filteredTasks = filteredTasks.filter((task) => {
        const v = taskDeviceTypeString(task).toLowerCase();
        return v === wanted;
      });
    }

    if (taskFilters.deviceOs) {
      filteredTasks = filteredTasks.filter((task) =>
        matchesTaskDeviceOsFilter(task, taskFilters.deviceOs)
      );
    }
    
    // Sort
    filteredTasks.sort((a, b) => {
      // Use video_uploaded_at instead of created_at
      const timeA = getTimestamp(a.video_uploaded_at || a.created_at);
      const timeB = getTimestamp(b.video_uploaded_at || b.created_at);
      return sortLatestFirst.tasks ? timeB - timeA : timeA - timeB;
    });
    
    return filteredTasks;
  };

  // Sort users with filters
  const getSortedUsers = () => {
    if (!users || !users.users) return [];
    
    let filteredUsers = [...users.users];
    const userFilters = filters.users;
    const rawPremiumFilter = userFilters.premiumFilter || 'all';
    const pf = rawPremiumFilter === 'subscription' ? 'active' : rawPremiumFilter;

    // Filter by email
    if (userFilters.email) {
      const searchEmail = userFilters.email.toLowerCase();
      filteredUsers = filteredUsers.filter(user => {
        const email = (user.email || '').toLowerCase();
        return email.includes(searchEmail);
      });
    }
    
    // Filter by name
    if (userFilters.name) {
      const searchName = userFilters.name.toLowerCase();
      filteredUsers = filteredUsers.filter(user => {
        const name = (user.name || '').toLowerCase();
        return name.includes(searchName);
      });
    }
    
    // Filter by phone
    if (userFilters.phone) {
      const searchPhone = userFilters.phone.toLowerCase().trim();
      if (searchPhone === 'null') {
        // Show entries with no phone number
        filteredUsers = filteredUsers.filter(user => {
          return !user.phone_number || user.phone_number === '';
        });
      } else {
        // Regular phone search
        filteredUsers = filteredUsers.filter(user => {
          const phone = (user.phone_number || '').toLowerCase();
          return phone.includes(searchPhone);
        });
      }
    }

    // Filter by signup country code (ISO-2)
    if (userFilters.country) {
      const wantedCountry = String(userFilters.country).trim().toUpperCase();
      filteredUsers = filteredUsers.filter((user) => {
        const cc = String(user.signup_country_code || '').trim().toUpperCase();
        return cc === wantedCountry;
      });
    }
    
    // Filter by has phone (boolean filter)
    if (userFilters.hasPhone && userFilters.hasPhone !== 'none') {
      const hasPhone = userFilters.hasPhone === 'true';
      filteredUsers = filteredUsers.filter(user => {
        const userHasPhone = !!(user.phone_number && user.phone_number.trim());
        return userHasPhone === hasPhone;
      });
    }
    
    // Filter by has videos
    if (userFilters.hasVideos !== null) {
      filteredUsers = filteredUsers.filter(user => {
        const hasVideos = (user.total_videos_uploaded || 0) > 0;
        return userFilters.hasVideos === hasVideos;
      });
    }

    if (userFilters.remindersOnly) {
      filteredUsers = filteredUsers.filter((user) => user.reminders_enabled === true);
    }

    if (userFilters.socialHandleOnly) {
      filteredUsers = filteredUsers.filter((user) => hasSocialHandle(user));
    }

    if (userFilters.connectedAccountOnly) {
      filteredUsers = filteredUsers.filter((user) =>
        hasConnectedAccount(user.user_id, connectedAccountsByUser)
      );
    }

    if (pf === 'premium') {
      filteredUsers = filteredUsers.filter((user) => {
        const status = getEffectiveSubscriptionStatus(user).toLowerCase();
        const hasActiveSubscription = status === 'active';
        const hasActiveCoupon = isCouponTrialActive(user);
        return hasActiveSubscription || hasActiveCoupon;
      });
    }

    if (pf === 'any-subscription') {
      filteredUsers = filteredUsers.filter((user) => {
        const hasStatus = String(user.subscription_status || '').trim() !== '';
        const hasSubId = String(user.razorpay_subscription_id || '').trim() !== '';
        const hasSubCreated = String(user.subscription_created_at || '').trim() !== '';
        const hasSubStart = user.subscription_current_start != null || String(user.subscription_current_start_iso || '').trim() !== '';
        const hasSubEnd = user.subscription_current_end != null || String(user.subscription_current_end_iso || '').trim() !== '';
        const hasAnyRazorpayEvent = String(user.last_razorpay_event || '').trim() !== '';
        return hasStatus || hasSubId || hasSubCreated || hasSubStart || hasSubEnd || hasAnyRazorpayEvent;
      });
    }

    if (pf === 'coupon') {
      filteredUsers = filteredUsers.filter((user) => {
        const code = user.coupon_code_applied;
        return code != null && String(code).trim() !== '';
      });
    }

    if (pf === 'cancel-at-period-end') {
      filteredUsers = filteredUsers.filter(isCancelAtPeriodEndUser);
    }

    // Filter by paid count from Razorpay subscription snapshot.
    if (userFilters.paidCount && userFilters.paidCount !== 'all') {
      filteredUsers = filteredUsers.filter((user) => {
        const paidCount = Number(user.subscription_paid_count || 0);
        if (userFilters.paidCount === '1') return paidCount === 1;
        if (userFilters.paidCount === 'gt1') return paidCount > 1;
        return true;
      });
    }

    if (userFilters.streakFilter && userFilters.streakFilter !== 'all') {
      filteredUsers = filteredUsers.filter((user) => {
        const streak = getDisplayStreakCount(user);
        if (userFilters.streakFilter === '1') return streak === 1;
        if (userFilters.streakFilter === 'gt1') return streak > 1;
        return true;
      });
    }

    const subscriptionStatusExactFilters = new Set(['active', 'halted', 'pending', 'paused', 'created', 'cancelled', 'expired']);
    if (subscriptionStatusExactFilters.has(pf)) {
      const want = pf;
      filteredUsers = filteredUsers.filter(
        (user) => getEffectiveSubscriptionStatus(user).toLowerCase() === want
      );
    }
    
    // Filter by created_at date range
    const [createdFrom, createdTo] = userFilters.createdRange || [null, null];
    if (createdFrom || createdTo) {
      filteredUsers = filteredUsers.filter(user => {
        if (!user.created_at) return false;
        
        const createdTimestamp = getTimestamp(user.created_at);
        const fromTime = createdFrom ? new Date(createdFrom).setHours(0, 0, 0, 0) : null;
        const toTime = createdTo ? new Date(createdTo).setHours(23, 59, 59, 999) : null;
        
        if (fromTime && createdTimestamp < fromTime) return false;
        if (toTime && createdTimestamp > toTime) return false;
        
        return true;
      });
    }
    
    // Filter by last_active date range
    const [lastActiveFrom, lastActiveTo] = userFilters.lastActiveRange || [null, null];
    if (lastActiveFrom || lastActiveTo) {
      filteredUsers = filteredUsers.filter(user => {
        const lastActive = user.last_active || user.updated_at;
        if (!lastActive) return false;
        
        const lastActiveTimestamp = getTimestamp(lastActive);
        const fromTime = lastActiveFrom ? new Date(lastActiveFrom).setHours(0, 0, 0, 0) : null;
        const toTime = lastActiveTo ? new Date(lastActiveTo).setHours(23, 59, 59, 999) : null;
        
        if (fromTime && lastActiveTimestamp < fromTime) return false;
        if (toTime && lastActiveTimestamp > toTime) return false;
        
        return true;
      });
    }
    
    // Sort by created_at or last_active based on userSortType
    filteredUsers.sort((a, b) => {
      let timeA, timeB;
      
      if (userSortType === 'active') {
        timeA = getTimestamp(a.last_active || a.updated_at);
        timeB = getTimestamp(b.last_active || b.updated_at);
      } else {
        timeA = getTimestamp(a.created_at);
        timeB = getTimestamp(b.created_at);
      }
      
      return sortLatestFirst.users ? timeB - timeA : timeA - timeB;
    });
    
    return filteredUsers;
  };

  const availableUserCountryCodes = React.useMemo(() => {
    if (!users || !Array.isArray(users.users)) return [];
    const counts = new Map();
    users.users.forEach((user) => {
      const cc = String(user?.signup_country_code || '').trim().toUpperCase();
      if (cc.length === 2 && /^[A-Z]{2}$/.test(cc)) {
        counts.set(cc, (counts.get(cc) || 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([code, count]) => ({ code, count }));
  }, [users]);

  const availableTaskDeviceTypes = useMemo(() => {
    const counts = new Map();
    flattenTasksByUser(tasks).forEach((task) => {
      const v = taskDeviceTypeString(task);
      if (!v) return;
      counts.set(v, (counts.get(v) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, count }));
  }, [tasks]);

  const availableTaskDeviceOs = useMemo(() => {
    const counts = new Map();
    flattenTasksByUser(tasks).forEach((task) => {
      const v = taskDeviceOsString(task);
      if (!v) return;
      counts.set(v, (counts.get(v) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, count }));
  }, [tasks]);

  const taskDeviceOsFamilyCounts = useMemo(() => {
    const counts = { android: 0, ios: 0, windows: 0, mac: 0 };
    flattenTasksByUser(tasks).forEach((task) => {
      const os = taskDeviceOsString(task).toLowerCase();
      if (!os) return;
      if (matchesTaskDeviceOsFamily(os, 'android')) counts.android += 1;
      if (matchesTaskDeviceOsFamily(os, 'ios')) counts.ios += 1;
      if (matchesTaskDeviceOsFamily(os, 'windows')) counts.windows += 1;
      if (matchesTaskDeviceOsFamily(os, 'mac')) counts.mac += 1;
    });
    return counts;
  }, [tasks]);


  // Validate admin access only once when component mounts or auth state changes
  useEffect(() => {
    // Reset validation flag when auth state changes
    if (!isAuthenticated || !idToken) {
      adminValidatedRef.current = false;
      usersLoadedRef.current = false;
      pushStatsLoadedRef.current = false;
      cachedUsersRef.current = null;
      tasksPageCursorRef.current = null;
      setUserEmailCache({});
      setUserMetaById({});
      setUsers(null);
      setTasks(null);
      setTasksHasMore(false);
      setPushStats(null);
      return;
    }

    // Only validate once per auth session
    if (adminValidatedRef.current) {
      return;
    }

    const validateAdminAccess = async () => {
      if (authLoading) return;

      if (!isAuthenticated || !idToken) {
        navigate('/');
        return;
      }

      try {
        setLoading(true);
        const freshToken = await getFreshTokenRef.current();
        const response = await fetch('/api/reelsadmin', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${freshToken}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setAdminData(data);
          setError(null);
          adminValidatedRef.current = true; // Mark as validated
          // Load all users for email mapping once admin is validated
          loadAllUsers();
        } else {
          setError(data.error || data.message || 'Not authorized');
          setTimeout(() => navigate('/notfound'), 1500);
        }
      } catch (err) {
        setError('Failed to validate admin access');
        setTimeout(() => navigate('/notfound'), 1500);
      } finally {
        setLoading(false);
      }
    };

    validateAdminAccess();
  }, [idToken, isAuthenticated, authLoading, navigate, loadAllUsers]);

  // Push stats: lazy-load when Controls or Firestore Users is opened (not on login)
  useEffect(() => {
    if (!adminData) return;
    const needsPushStats =
      activeTab === 'controls' ||
      (activeTab === 'firestore' && selectedCollection === 'users');
    if (!needsPushStats || pushStatsLoadedRef.current) return;
    pushStatsLoadedRef.current = true;
    loadPushStats();
  }, [adminData, activeTab, selectedCollection, loadPushStats]);

  // Connected accounts: lazy-load when Firestore Users is opened
  useEffect(() => {
    if (!adminData) return;
    if (activeTab === 'firestore' && selectedCollection === 'users') {
      loadConnectedAccountsSummary({ force: false });
    }
  }, [adminData, activeTab, selectedCollection, loadConnectedAccountsSummary]);

  // Track which tabs have been loaded to prevent reloading
  const filesTabLoadedRef = useRef(false);
  const logsTabLoadedRef = useRef(false);
  const razorpayWebhooksTabLoadedRef = useRef(false);
  const paymentSurveysTabLoadedRef = useRef(false);
  const clipFeedbackTabLoadedRef = useRef(false);
  const userJourneyTabLoadedRef = useRef(false);
  const logContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const pageScrollBeforeLogsRefreshRef = useRef(null);
  const restoreScrollYRef = useRef(null);

  // Restore saved scroll position after first successful admin load.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const parsed = getSavedAdminPageState();
      if (typeof parsed?.scrollY === 'number' && Number.isFinite(parsed.scrollY) && parsed.scrollY >= 0) {
        restoreScrollYRef.current = parsed.scrollY;
      }
    } catch (_) {
      // ignore parse/storage errors
    }
  }, []);

  useEffect(() => {
    if (!adminData || loading || restoreScrollYRef.current == null) return;
    const y = restoreScrollYRef.current;
    restoreScrollYRef.current = null;
    requestAnimationFrame(() => {
      window.scrollTo(0, y);
    });
  }, [adminData, loading]);

  // Persist current admin tab and page scroll so refresh resumes context.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const writeState = (nextScrollY) => {
      try {
        sessionStorage.setItem(
          REELS_ADMIN_PAGE_STATE_KEY,
          JSON.stringify({
            activeTab,
            userJourneyDateFrom,
            userJourneyDateTo,
            selectedSessionId,
            selectedSessionDate,
            scrollY: typeof nextScrollY === 'number' ? nextScrollY : window.scrollY || 0,
          })
        );
      } catch (_) {
        // ignore storage quota/access errors
      }
    };
    writeState();
    const onScroll = () => writeState(window.scrollY || 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [activeTab, userJourneyDateFrom, userJourneyDateTo, selectedSessionId, selectedSessionDate]);

  // Load customers when admin data is available and files tab is active
  useEffect(() => {
    if (adminData && activeTab === 'files' && !filesTabLoadedRef.current) {
      filesTabLoadedRef.current = true;
      loadCustomers();
    }
    // Reset when switching away from files tab
    if (activeTab !== 'files') {
      filesTabLoadedRef.current = false;
    }
  }, [adminData, activeTab, loadCustomers]);

  // Firestore: Users from login cache; Tasks lazy (25 recent, load more = rest)
  useEffect(() => {
    if (!adminData || activeTab !== 'firestore') return;
    if (selectedCollection === 'users') {
      if (!hydrateFirestoreUsersFromCache()) {
        refreshFirestoreUsers();
      }
    } else if (selectedCollection === 'tasks') {
      fetchTasksRecent({ force: false });
    }
  }, [
    adminData,
    activeTab,
    selectedCollection,
    hydrateFirestoreUsersFromCache,
    refreshFirestoreUsers,
    fetchTasksRecent,
  ]);

  // Load logs when admin data is available and logs tab is active
  useEffect(() => {
    if (adminData && activeTab === 'logs' && !logsTabLoadedRef.current) {
      logsTabLoadedRef.current = true;
      loadLogs(selectedDate);
    }
    // Reset when switching away from logs tab
    if (activeTab !== 'logs') {
      logsTabLoadedRef.current = false;
    }
  }, [adminData, activeTab, loadLogs, selectedDate]);

  // Reload logs when date changes
  useEffect(() => {
    if (adminData && activeTab === 'logs' && logsTabLoadedRef.current) {
      loadLogs(selectedDate);
    }
  }, [selectedDate, adminData, activeTab, loadLogs]);

  useEffect(() => {
    if (adminData && activeTab === 'razorpay-webhooks' && !razorpayWebhooksTabLoadedRef.current) {
      razorpayWebhooksTabLoadedRef.current = true;
      loadRazorpayWebhookEvents({ append: false });
    }
    if (activeTab !== 'razorpay-webhooks') {
      razorpayWebhooksTabLoadedRef.current = false;
    }
  }, [adminData, activeTab, loadRazorpayWebhookEvents]);

  useEffect(() => {
    if (adminData && activeTab === 'survey' && !paymentSurveysTabLoadedRef.current) {
      paymentSurveysTabLoadedRef.current = true;
      loadPaymentAbandonmentSurveys({ append: false });
    }
    if (activeTab !== 'survey') {
      paymentSurveysTabLoadedRef.current = false;
    }
  }, [adminData, activeTab, loadPaymentAbandonmentSurveys]);

  useEffect(() => {
    if (adminData && activeTab === 'clip-feedback' && !clipFeedbackTabLoadedRef.current) {
      clipFeedbackTabLoadedRef.current = true;
      loadClipFeedback({ append: false });
    }
    if (activeTab !== 'clip-feedback') {
      clipFeedbackTabLoadedRef.current = false;
    }
  }, [adminData, activeTab, loadClipFeedback]);

  useEffect(() => {
    if (activeTab !== 'razorpay-webhooks') {
      setWebhookEventFilterOpen(false);
      setSelectedRazorpayWebhookDocIds([]);
    }
  }, [activeTab]);

  useEffect(() => {
    const el = razorpayWebhookSelectAllRef.current;
    if (!el) return;
    const vis = filteredRazorpayVisibleDocIds;
    const n = vis.length;
    if (n === 0) {
      el.indeterminate = false;
      el.checked = false;
      return;
    }
    const c = vis.filter((id) => selectedRazorpayWebhookDocIds.includes(id)).length;
    el.indeterminate = c > 0 && c < n;
    el.checked = c === n;
  }, [filteredRazorpayVisibleDocIds, selectedRazorpayWebhookDocIds]);

  useEffect(() => {
    if (!webhookEventFilterOpen) return undefined;
    const onDown = (e) => {
      const el = webhooksFilterMenuRef.current;
      if (el && !el.contains(e.target)) {
        setWebhookEventFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [webhookEventFilterOpen]);

  useEffect(() => {
    const t = setTimeout(() => setLogViewerSearchDebounced(logViewerSearchInput.trim()), 280);
    return () => clearTimeout(t);
  }, [logViewerSearchInput]);

  useEffect(() => {
    setLogAnalyzedTasks([]);
    setSelectedLogTaskKey('');
    setLogViewerSearchInput('');
    setLogViewerSearchDebounced('');
    setLogSearchMatchCursor(0);
  }, [selectedDate]);

  useEffect(() => {
    if (showErrorLogsOnly) setSelectedLogTaskKey('');
  }, [showErrorLogsOnly]);

  useEffect(() => {
    setLogAnalyzedTasks([]);
    setSelectedLogTaskKey('');
    setLogSearchMatchCursor(0);
  }, [logFile?.content]);

  useEffect(() => {
    setLogSearchMatchCursor(0);
  }, [logViewerSearchDebounced, showErrorLogsOnly]);

  useEffect(() => {
    const n = logSearchMatchLineIndices.length;
    setLogSearchMatchCursor((c) => {
      if (n === 0) return 0;
      return Math.min(c, n - 1);
    });
  }, [logSearchMatchLineIndices]);

  useEffect(() => {
    if (!logViewerSearchDebounced || logSearchCurrentLineIndex == null) return;
    const id = `admin-log-line-${logSearchCurrentLineIndex}`;
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [logSearchCurrentLineIndex, logViewerSearchDebounced]);

  // Scroll to bottom on fresh load, preserve position on manual scroll
  useEffect(() => {
    const container = logContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      userScrolledUpRef.current = distanceFromBottom > 80;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [logFile]);

  useEffect(() => {
    const container = logContainerRef.current;
    if (!container || userScrolledUpRef.current) return;
    container.scrollTop = container.scrollHeight;
  }, [logFile]);

  // Restore page scroll position after log refresh so the page doesn't jump to top
  useEffect(() => {
    if (!loadingLogs && pageScrollBeforeLogsRefreshRef.current != null) {
      window.scrollTo(0, pageScrollBeforeLogsRefreshRef.current);
      pageScrollBeforeLogsRefreshRef.current = null;
    }
  }, [loadingLogs, logFile]);

  // Auto-refresh logs every 10s when Logs tab is active
  useEffect(() => {
    if (activeTab !== 'logs' || !adminData) return;
    const interval = setInterval(() => {
      loadLogs(selectedDate);
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTab, adminData, selectedDate, loadLogs]);

  // Load user journey sessions when tab is active
  useEffect(() => {
    if (adminData && activeTab === 'user-journey' && !userJourneyTabLoadedRef.current) {
      userJourneyTabLoadedRef.current = true;
      loadUserJourneySessions(userJourneyDateFrom, userJourneyDateTo);
    }
    // Reset when switching away from user journey tab
    if (activeTab !== 'user-journey') {
      userJourneyTabLoadedRef.current = false;
    }
  }, [adminData, activeTab, loadUserJourneySessions, userJourneyDateFrom, userJourneyDateTo]);

  // Reload sessions when date range changes
  useEffect(() => {
    if (adminData && activeTab === 'user-journey' && userJourneyTabLoadedRef.current) {
      loadUserJourneySessions(userJourneyDateFrom, userJourneyDateTo);
    }
  }, [userJourneyDateFrom, userJourneyDateTo, adminData, activeTab, loadUserJourneySessions]);

  // Load timeline when session is selected
  useEffect(() => {
    if (selectedSessionId && activeTab === 'user-journey') {
      loadUserJourneyTimeline(selectedSessionId, selectedSessionDate || userJourneyDateTo);
    }
    if (activeTab === 'youtube-cookies') {
      loadCookies();
    }
    if (activeTab === 'task-queue') {
      loadQueue();
    }
    if (activeTab === 'coupons') {
      loadCoupons();
    }
    if (activeTab === 'daily-content') {
      loadDailyContentPrompts();
      loadDailyContentLlmConfig();
      loadScriptFeedbackSurvey();
    }
    if (activeTab === 'daily-content-requests') {
      loadGetNowRequests();
    }
    if (activeTab === 'controls') {
      loadRazorpaySubscriptionToggle();
      loadRazorpayMode();
      loadYoutubeUrlToggle();
      loadTrimSilenceFeatureToggle();
      loadBrollDefaultToggle();
      loadStreakPushSchedulerToggle();
      loadPhoneDownloadToggle();
      loadPricingInviteCardToggle();
      loadLambdaToggle();
      loadChatwootTemplatesToggle();
    }
  }, [selectedSessionId, selectedSessionDate, userJourneyDateTo, activeTab, loadUserJourneyTimeline, loadCookies, loadYoutubeUrlToggle, loadTrimSilenceFeatureToggle, loadBrollDefaultToggle, loadStreakPushSchedulerToggle, loadPhoneDownloadToggle, loadPricingInviteCardToggle, loadLambdaToggle, loadChatwootTemplatesToggle, loadQueue, loadCoupons, loadDailyContentPrompts, loadDailyContentLlmConfig, loadScriptFeedbackSurvey, loadGetNowRequests, loadRazorpaySubscriptionToggle, loadRazorpayMode]);

  useEffect(() => {
    if (!adminData || activeTab !== 'scheduled-posts') return;
    fetchScheduledPostsRecent({ force: true });
  }, [adminData, activeTab, fetchScheduledPostsRecent]);

  // Marketing: use login cache when opening/switching channel; no full-user refetch
  useEffect(() => {
    if (activeTab === 'marketing') {
      loadMarketingUsers(marketingChannel, { forceRefresh: false, clearSelection: false });
    }
  }, [activeTab, marketingChannel, loadMarketingUsers]);

  // Marketing templates per channel
  useEffect(() => {
    if (activeTab === 'marketing') {
      loadMarketingTemplates();
    }
  }, [activeTab, marketingChannel, loadMarketingTemplates]);

  const handleDownload = useCallback(async (filePath, fileType, fileName = null) => {
    const key = makeFileDownloadKey(fileType, filePath);
    if (downloadingFileKeysRef.current.has(key)) return;

    const displayName = fileName || filePath.split('/').pop() || 'download';
    downloadingFileKeysRef.current.add(key);

    const setProgress = (patch) => {
      setFileDownloadProgress((prev) => ({
        ...prev,
        [key]: { ...prev[key], fileName: displayName, ...patch },
      }));
    };

    try {
      setProgress({ percent: 0, indeterminate: false, loaded: 0, total: 0 });
      const freshToken = await getFreshToken();
      const url = `/api/admin/file/download?file_path=${encodeURIComponent(filePath)}&file_type=${fileType}`;

      const blob = await fetchAdminFileBlobWithProgress(
        url,
        { Authorization: `Bearer ${freshToken}` },
        (progress) => setProgress(progress)
      );

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      alert('Error downloading file');
    } finally {
      downloadingFileKeysRef.current.delete(key);
      setFileDownloadProgress((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [getFreshToken]);

  const isPreviewableVideoFile = useCallback((fileName) => {
    if (!fileName) return false;
    const lower = String(fileName).toLowerCase();
    return ADMIN_VIDEO_EXT.some((ext) => lower.endsWith(ext));
  }, []);

  const toggleCustomerThumbnailView = useCallback((userId) => {
    let enabling = false;
    setThumbnailViewUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else {
        next.add(userId);
        enabling = true;
      }
      return next;
    });
    if (enabling) setSelectedCustomer(userId);
  }, []);

  const closePreviewModal = useCallback(() => {
    setPreviewVideo((prev) => {
      if (prev.url && prev.isBlobUrl && String(prev.url).startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev.url);
        } catch (_) {
          // Best-effort cleanup
        }
      }
      return {
        isOpen: false,
        isLoading: false,
        error: '',
        url: '',
        name: '',
        userEmail: '',
        isBlobUrl: true,
        previewKind: 'video',
      };
    });
  }, []);

  const openStreamPreview = useCallback(
    async (filePath, fileType, fileName, previewKind = 'video', userId = null) => {
      try {
        const freshToken = await getFreshToken();
        const q = new URLSearchParams({
          file_path: filePath,
          file_type: fileType,
          token: freshToken,
        });
        const streamUrl = `/api/admin/file/stream?${q.toString()}`;
        setPreviewVideo((prev) => {
          if (prev.url && prev.isBlobUrl && String(prev.url).startsWith('blob:')) {
            try {
              URL.revokeObjectURL(prev.url);
            } catch (_) {}
          }
          return {
            isOpen: true,
            isLoading: false,
            error: '',
            url: streamUrl,
            name: fileName || filePath.split('/').pop() || 'Preview',
            userEmail: userId ? getUserEmail(userId) : '',
            isBlobUrl: false,
            previewKind,
          };
        });
      } catch (err) {
        setPreviewVideo({
          isOpen: true,
          isLoading: false,
          error: err?.message || 'Could not open preview',
          url: '',
          name: fileName || '',
          userEmail: userId ? getUserEmail(userId) : '',
          isBlobUrl: false,
          previewKind: 'video',
        });
      }
    },
    [getFreshToken, getUserEmail]
  );

  const handlePreview = useCallback(
    async (filePath, fileType, fileName) => {
      try {
        setPreviewVideo((prev) => {
          if (prev.url && prev.isBlobUrl && String(prev.url).startsWith('blob:')) {
            try {
              URL.revokeObjectURL(prev.url);
            } catch (_) {
              // Best-effort cleanup
            }
          }
          return {
            isOpen: true,
            isLoading: true,
            error: '',
            url: '',
            name: fileName || filePath.split('/').pop() || 'Video Preview',
            userEmail: '',
            isBlobUrl: true,
            previewKind: 'video',
          };
        });

        const freshToken = await getFreshToken();
        const url = `/api/admin/file/download?file_path=${encodeURIComponent(filePath)}&file_type=${fileType}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${freshToken}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to load video preview');
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setPreviewVideo((prev) => ({
          ...prev,
          isLoading: false,
          error: '',
          url: objectUrl,
          isBlobUrl: true,
          previewKind: 'video',
        }));
      } catch (err) {
        setPreviewVideo((prev) => ({
          ...prev,
          isLoading: false,
          url: '',
          error: err?.message || 'Could not load preview',
          isBlobUrl: true,
          previewKind: 'video',
        }));
      }
    },
    [getFreshToken]
  );

  const makeFileSelectionKey = (fileType, filePath) => JSON.stringify({ t: fileType, p: filePath });

  const toggleFileSelection = (fileType, filePath) => {
    const key = makeFileSelectionKey(fileType, filePath);
    setSelectedFileKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sortFilesBySize = (files, order) => {
    if (!files || !files.length || !order || order === 'none') return files;
    const sorted = [...files];
    sorted.sort((a, b) => (a.size || 0) - (b.size || 0));
    if (order === 'largest') sorted.reverse();
    return sorted;
  };

  const getFileKeysForList = (fileType, files) =>
    (files || []).map((f) => makeFileSelectionKey(fileType, f.path));

  const mergeKeysIntoSet = (set, keys, add) => {
    const next = new Set(set);
    keys.forEach((k) => {
      if (add) next.add(k);
      else next.delete(k);
    });
    return next;
  };

  const toggleSelectAllInSection = (fileType, files) => {
    const keys = getFileKeysForList(fileType, files);
    if (keys.length === 0) return;
    const allOn = keys.every((k) => selectedFileKeys.has(k));
    setSelectedFileKeys((prev) => mergeKeysIntoSet(prev, keys, !allOn));
  };

  const toggleSelectAllForCustomerVisibleFiles = (inFiles, outFiles, arcFiles) => {
    const keys = [
      ...getFileKeysForList('input', inFiles),
      ...getFileKeysForList('output', outFiles),
      ...getFileKeysForList('archive', arcFiles),
    ];
    if (keys.length === 0) return;
    const allOn = keys.every((k) => selectedFileKeys.has(k));
    setSelectedFileKeys((prev) => mergeKeysIntoSet(prev, keys, !allOn));
  };

  const handleDelete = async (filePath, fileType) => {
    if (!window.confirm(`Are you sure you want to delete this file?`)) {
      return;
    }

    try {
      const freshToken = await getFreshToken();
      const response = await fetch('/api/admin/file/delete', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_path: filePath, file_type: fileType }),
      });

      if (response.ok) {
        // Reload customers
        loadCustomers();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete file');
      }
    } catch (err) {
      alert('Error deleting file');
    }
  };

  const handleBatchDeleteSelectedFiles = async () => {
    if (selectedFileKeys.size === 0) return;
    if (
      !window.confirm(`Delete ${selectedFileKeys.size} selected file(s)? This cannot be undone.`)
    ) {
      return;
    }
    setBatchDeletingFiles(true);
    const errors = [];
    try {
      const freshToken = await getFreshToken();
      for (const key of selectedFileKeys) {
        let fileType;
        let filePath;
        try {
          const parsed = JSON.parse(key);
          fileType = parsed.t;
          filePath = parsed.p;
        } catch {
          errors.push(`Invalid selection key`);
          continue;
        }
        try {
          const response = await fetch('/api/admin/file/delete', {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${freshToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ file_path: filePath, file_type: fileType }),
          });
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            errors.push(`${filePath}: ${data.error || response.status}`);
          }
        } catch (e) {
          errors.push(`${filePath}: ${e.message || 'request failed'}`);
        }
      }
      setSelectedFileKeys(new Set());
      await loadCustomers();
      if (errors.length > 0) {
        alert(`Some deletes failed (${errors.length}):\n${errors.slice(0, 8).join('\n')}${errors.length > 8 ? '\n…' : ''}`);
      }
    } finally {
      setBatchDeletingFiles(false);
    }
  };

  useEffect(() => {
    return () => {
      try {
        if (
          previewVideo.url &&
          previewVideo.isBlobUrl &&
          String(previewVideo.url).startsWith('blob:')
        ) {
          URL.revokeObjectURL(previewVideo.url);
        }
      } catch (_) {
        // Best-effort cleanup
      }
    };
  }, [previewVideo.url, previewVideo.isBlobUrl]);

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm(`Are you sure you want to delete this task?`)) {
      return;
    }

    try {
      const freshToken = await getFreshTokenRef.current();
      // URL encode the taskId to handle any special characters
      const encodedTaskId = encodeURIComponent(taskId);
      const response = await fetch(`/api/admin/firestore/tasks/${encodedTaskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSelectedTaskIds((prev) => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
        await fetchTasksRecent({ force: true });
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete task');
      }
    } catch (err) {
      alert('Error deleting task');
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const handleDeleteSelectedTasks = async () => {
    const ids = [...selectedTaskIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} task(s)? This cannot be undone.`)) {
      return;
    }
    setBatchDeletingTasks(true);
    const failed = [];
    try {
      const freshToken = await getFreshTokenRef.current();
      for (const taskId of ids) {
        const encodedTaskId = encodeURIComponent(taskId);
        try {
          const response = await fetch(`/api/admin/firestore/tasks/${encodedTaskId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${freshToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (!response.ok) failed.push(taskId);
        } catch {
          failed.push(taskId);
        }
      }
    } finally {
      setBatchDeletingTasks(false);
    }
    setSelectedTaskIds(new Set());
    await fetchTasksRecent({ force: true });
    if (failed.length > 0) {
      alert(`Failed to delete ${failed.length} task(s). Others were removed.`);
    }
  };

  const handleDownloadLog = useCallback(async (filename) => {
    try {
      const freshToken = await getFreshTokenRef.current();
      const url = `/api/logs/download?filename=${encodeURIComponent(filename)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${freshToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to download log file');
      }
    } catch (err) {
      alert('Error downloading log file: ' + (err.message || 'Unknown error'));
    }
  }, []); // Empty deps - using ref for getFreshToken


  const formatFileSize = (bytes) => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n < 0) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const sumFileBytes = (files) =>
    (files || []).reduce((acc, f) => acc + (typeof f.size === 'number' ? f.size : 0), 0);

  const fileMgmtStorageTotalBytes = useMemo(() => {
    return customers.reduce((acc, c) => {
      if (typeof c.total_bytes === 'number') return acc + c.total_bytes;
      return (
        acc +
        sumFileBytes(c.input_files) +
        sumFileBytes(c.output_files) +
        sumFileBytes(c.archive_files)
      );
    }, 0);
  }, [customers]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  /** Razorpay admin webhooks table — pill color by event suffix. */
  const webhookEventToneClass = (eventType) => {
    const t = String(eventType || '').toLowerCase().trim();
    if (!t) return '';
    if (t.endsWith('.cancelled')) return 'webhook-event--cancelled';
    if (t.endsWith('.charged')) return 'webhook-event--charged';
    if (t.endsWith('.activated')) return 'webhook-event--activated';
    return '';
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getSubscriptionStatusBadgeStyle = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'active') return { background: '#10b981', color: 'white' };
    if (normalized === 'pending' || normalized === 'authenticated') return { background: '#f59e0b', color: 'white' };
    if (normalized === 'halted' || normalized === 'cancelled') return { background: '#ef4444', color: 'white' };
    if (normalized === 'expired') return { background: '#6b7280', color: 'white' };
    if (normalized === 'paused' || normalized === 'completed') return { background: '#6b7280', color: 'white' };
    return { background: '#374151', color: 'white' };
  };

  /** Compact "1m 44s · 2 videos" for SUCCESS task headers only */
  const formatTaskSuccessHeaderSummary = (task) => {
    if (task.lifecycle_state !== 'SUCCESS' && task.status !== 'SUCCESS') return null;
    if (!task.output) return null;
    const parts = [];
    if (typeof task.output.total_time_taken === 'number' && !Number.isNaN(task.output.total_time_taken)) {
      parts.push(formatTime(task.output.total_time_taken));
    }
    if (typeof task.output.total_videos_generated === 'number' && !Number.isNaN(task.output.total_videos_generated)) {
      const n = task.output.total_videos_generated;
      parts.push(`${n} short${n !== 1 ? 's' : ''}`);
    }
    return parts.length ? parts.join(' · ') : null;
  };

  if (authLoading || loading) {
    return (
      <div className="reels-admin-page">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Validating admin access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reels-admin-page">
        <div className="admin-error">
          <h2>Access Denied</h2>
          <p>{error}</p>
          <p>Redirecting...</p>
        </div>
      </div>
    );
  }

  if (!adminData) return null;

  return (
    <div className="reels-admin-page">
      <div className="admin-container">
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => setActiveTab('files')}
          >
            📁 File Management
          </button>
          <button
            className={`admin-tab ${activeTab === 'controls' ? 'active' : ''}`}
            onClick={() => setActiveTab('controls')}
          >
            ⚙️ Controls
          </button>
          <button
            className={`admin-tab ${activeTab === 'firestore' ? 'active' : ''}`}
            onClick={() => setActiveTab('firestore')}
          >
            🗄️ Firestore Collections
          </button>
          <button
            className={`admin-tab ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => setActiveTab('coupons')}
          >
            🎟️ Coupons
          </button>
          <button
            className={`admin-tab ${activeTab === 'daily-content' ? 'active' : ''}`}
            onClick={() => setActiveTab('daily-content')}
          >
            💡 Daily Content
          </button>
          <button
            className={`admin-tab ${activeTab === 'daily-content-requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('daily-content-requests')}
          >
            📩 Get Now Requests
          </button>
          <button
            className={`admin-tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📋 Logs
          </button>
          <button
            className={`admin-tab ${activeTab === 'razorpay-webhooks' ? 'active' : ''}`}
            onClick={() => setActiveTab('razorpay-webhooks')}
          >
            💳 Razorpay webhooks
          </button>
          <button
            className={`admin-tab ${activeTab === 'survey' ? 'active' : ''}`}
            onClick={() => setActiveTab('survey')}
          >
            🧾 Survey
          </button>
          <button
            className={`admin-tab ${activeTab === 'clip-feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('clip-feedback')}
          >
            ⭐ Clip Feedback
          </button>
          <button
            className={`admin-tab ${activeTab === 'user-journey' ? 'active' : ''}`}
            onClick={() => setActiveTab('user-journey')}
          >
            👤 User Journey
          </button>
          <button
            className={`admin-tab ${activeTab === 'youtube-cookies' ? 'active' : ''}`}
            onClick={() => setActiveTab('youtube-cookies')}
          >
            🍪 YouTube Cookies
          </button>
          <button
            className={`admin-tab ${activeTab === 'marketing' ? 'active' : ''}`}
            onClick={() => setActiveTab('marketing')}
          >
            📢 Marketing
          </button>
          <button
            className={`admin-tab ${activeTab === 'task-queue' ? 'active' : ''}`}
            onClick={() => setActiveTab('task-queue')}
          >
            📋 Task Queue
          </button>
          <button
            className={`admin-tab ${activeTab === 'scheduled-posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('scheduled-posts')}
          >
            📅 Scheduled Posts
          </button>
        </div>

        {activeTab === 'controls' && (
          <div className="admin-content controls-content">
            <div className="section-header">
              <h2>Controls</h2>
              <button
                type="button"
                onClick={() => {
                  loadRazorpaySubscriptionToggle();
                  loadRazorpayMode();
                  loadYoutubeUrlToggle();
                  loadTrimSilenceFeatureToggle();
                  loadBrollDefaultToggle();
                  loadStreakPushSchedulerToggle();
                  loadPhoneDownloadToggle();
                  loadPricingInviteCardToggle();
                  loadLambdaToggle();
                  loadChatwootTemplatesToggle();
                }}
                className="btn-refresh"
                disabled={
                  loadingRazorpayToggle ||
                  loadingRazorpayMode ||
                  loadingToggle ||
                  loadingTrimSilenceToggle ||
                  loadingBrollDefaultToggle ||
                  loadingStreakPushToggle ||
                  loadingPhoneDownloadToggle ||
                  loadingPricingInviteToggle ||
                  loadingLambdaToggle ||
                  loadingChatwootToggle
                }
              >
                {loadingRazorpayToggle ||
                loadingRazorpayMode ||
                loadingToggle ||
                loadingTrimSilenceToggle ||
                loadingBrollDefaultToggle ||
                loadingStreakPushToggle ||
                loadingPhoneDownloadToggle ||
                loadingPricingInviteToggle ||
                loadingLambdaToggle ||
                loadingChatwootToggle
                  ? 'Loading...'
                  : '🔄 Refresh all'}
              </button>
            </div>

            <div className="cookie-section coupons-panel">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Razorpay subscription checkout</h3>
                <button
                  type="button"
                  onClick={() => {
                    loadRazorpaySubscriptionToggle();
                    loadRazorpayMode();
                  }}
                  className="btn-refresh"
                  disabled={loadingRazorpayToggle || loadingRazorpayMode}
                >
                  {loadingRazorpayToggle || loadingRazorpayMode ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={razorpayCheckoutEnabled}
                    onChange={toggleRazorpaySubscriptionCheckout}
                    disabled={loadingRazorpayToggle}
                  />
                  <span>Allow new subscription checkouts</span>
                </label>
                <p className="toggle-description">
                  When off (no new subscription), when on if allowlist has emails only those can access & if allowlist empty (no new subscription).
                </p>
                {!razorpayEnvEnabled && (
                  <p className="toggle-description" style={{ color: '#b45309', fontWeight: 600 }}>
                    ENV kill-switch is off: set ENABLE_RAZORPAY_SUBSCRIPTION=true (and restart) for checkout to work, even if this toggle is on.
                  </p>
                )}
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                    Billing mode
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <select
                      value={razorpayMode}
                      onChange={(e) => setRazorpayMode(e.target.value === 'live' ? 'live' : 'test')}
                      disabled={loadingRazorpayMode}
                      style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}
                    >
                      <option value="test">Test</option>
                      <option value="live">Live</option>
                    </select>
                    <button
                      type="button"
                      className="btn-refresh"
                      disabled={loadingRazorpayMode}
                      onClick={() => setRazorpayBillingMode(razorpayMode)}
                    >
                      {loadingRazorpayMode ? 'Saving...' : 'Save mode'}
                    </button>
                    <span className="toggle-description" style={{ margin: 0 }}>
                      Current mode: <strong>{razorpayMode}</strong>
                      {!razorpayModeReady && ' (missing key/secret/plan for selected mode)'}
                    </span>
                  </div>
                </div>

                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <strong style={{ fontSize: 13 }}>Pricing display (Firestore)</strong>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn-refresh"
                          onClick={loadPricingDisplay}
                          disabled={loadingPricingDisplay || savingPricingDisplay}
                        >
                          {loadingPricingDisplay ? 'Loading...' : '🔄 Refresh'}
                        </button>
                        <button
                          type="button"
                          className="btn-refresh"
                          onClick={savePricingDisplay}
                          disabled={!pricingDisplayLoaded || savingPricingDisplay || loadingPricingDisplay}
                        >
                          {savingPricingDisplay ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                    {!pricingDisplayLoaded ? (
                      <p className="toggle-description" style={{ margin: 0 }}>
                        Click Refresh to load current values.
                      </p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '50px repeat(3, 1fr)', gap: 6, alignItems: 'center', fontSize: 12 }}>
                        <div></div>
                        <div style={{ fontWeight: 600 }}>Old price</div>
                        <div style={{ fontWeight: 600 }}>New price</div>
                        <div style={{ fontWeight: 600 }}>Period</div>
                        {['in', 'intl'].map((region) => (
                          <React.Fragment key={region}>
                            <div style={{ fontWeight: 600, textTransform: 'uppercase' }}>{region}</div>
                            {['price_old', 'price_new', 'period'].map((field) => (
                              <input
                                key={field}
                                type="text"
                                value={pricingDisplayForm[region][field]}
                                onChange={(e) =>
                                  setPricingDisplayForm((prev) => ({
                                    ...prev,
                                    [region]: { ...prev[region], [field]: e.target.value },
                                  }))
                                }
                                style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 12, minWidth: 0 }}
                                disabled={savingPricingDisplay}
                              />
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <strong style={{ fontSize: 13 }}>Razorpay plan IDs (Firestore)</strong>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn-refresh"
                          onClick={loadRazorpayPlans}
                          disabled={loadingRazorpayPlans || savingRazorpayPlans}
                        >
                          {loadingRazorpayPlans ? 'Loading...' : '🔄 Refresh'}
                        </button>
                        <button
                          type="button"
                          className="btn-refresh"
                          onClick={saveRazorpayPlans}
                          disabled={!razorpayPlansLoaded || savingRazorpayPlans || loadingRazorpayPlans}
                        >
                          {savingRazorpayPlans ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                    {!razorpayPlansLoaded ? (
                      <p className="toggle-description" style={{ margin: 0 }}>
                        Click Refresh to load current values.
                      </p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 1fr', gap: 6, alignItems: 'center', fontSize: 12 }}>
                        <div></div>
                        <div style={{ fontWeight: 600 }}>India (in)</div>
                        <div style={{ fontWeight: 600 }}>International (intl)</div>
                        {['test', 'live'].map((modeKey) => (
                          <React.Fragment key={modeKey}>
                            <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{modeKey}</div>
                            {['in', 'intl'].map((region) => (
                              <input
                                key={region}
                                type="text"
                                placeholder="plan_..."
                                value={razorpayPlansForm[modeKey][region]}
                                onChange={(e) =>
                                  setRazorpayPlansForm((prev) => ({
                                    ...prev,
                                    [modeKey]: { ...prev[modeKey], [region]: e.target.value },
                                  }))
                                }
                                style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 12, fontFamily: 'monospace', minWidth: 0 }}
                                disabled={savingRazorpayPlans}
                              />
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12, gap: 12 }}>
                <h3 style={{ margin: 0, flex: '1 1 auto', minWidth: 0 }}>Subscription status cleanup</h3>
                <div
                  className="reconcile-actions-row"
                  style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center', flexShrink: 0 }}
                >
                  <button
                    type="button"
                    className="btn-refresh"
                    onClick={previewSubscriptionReconcile}
                    disabled={subscriptionReconcileLoading || subscriptionReconcileApplying}
                  >
                    {subscriptionReconcileLoading ? 'Scanning...' : 'Preview'}
                  </button>
                  {subscriptionReconcilePreview?.dry_run !== false &&
                    (subscriptionReconcilePreview?.candidate_count ?? 0) > 0 && (
                      <button
                        type="button"
                        className="btn-refresh"
                        onClick={applySubscriptionReconcile}
                        disabled={subscriptionReconcileApplying || subscriptionReconcileLoading}
                        style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                      >
                        {subscriptionReconcileApplying
                          ? 'Applying...'
                          : `Confirm (${subscriptionReconcilePreview.candidate_count})`}
                      </button>
                    )}
                  {subscriptionReconcilePreview && (
                    <button
                      type="button"
                      className="btn-refresh"
                      onClick={() => setSubscriptionReconcilePreview(null)}
                      disabled={subscriptionReconcileLoading || subscriptionReconcileApplying}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="toggle-container">
                <p className="toggle-description" style={{ marginTop: 0 }}>
                  Mark <code>active</code> / <code>cancelled</code> users as <code>expired</code> when{' '}
                  <code>subscription_current_end_iso</code> is in the past. Coupon fields are not changed.
                  Queries only active/cancelled users (not your full user list).
                </p>
                {subscriptionReconcilePreview && (
                  <div style={{ marginTop: 16 }}>
                    <p className="toggle-description" style={{ margin: '0 0 8px' }}>
                      Scanned <strong>{subscriptionReconcilePreview.scanned ?? 0}</strong> active/cancelled users ·{' '}
                      <strong>{subscriptionReconcilePreview.candidate_count ?? 0}</strong> to update
                      {subscriptionReconcilePreview.dry_run === false && (
                        <>
                          {' '}
                          · applied <strong>{subscriptionReconcilePreview.updated ?? 0}</strong>
                          {(subscriptionReconcilePreview.skipped ?? 0) > 0 && (
                            <> · skipped {subscriptionReconcilePreview.skipped}</>
                          )}
                        </>
                      )}
                    </p>
                    {(subscriptionReconcilePreview.candidates || []).length === 0 ? (
                      <p className="toggle-description" style={{ margin: 0 }}>
                        No stale subscriptions found.
                      </p>
                    ) : (
                      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Period end</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Plan after</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(subscriptionReconcilePreview.candidates || []).map((row) => (
                              <tr key={row.user_id}>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                                  {row.email || row.user_id}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                                  <strong>{row.status_from}</strong> → <strong>{row.status_to}</strong>
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                                  {row.subscription_current_end_iso || '—'}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                                  {row.plan_tier_after || 'free'}
                                  {row.coupon_trial_active ? ' (coupon trial still active)' : ''}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', maxWidth: 280 }}>
                                  {row.reason}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12, gap: 12 }}>
                <h3 style={{ margin: 0, flex: '1 1 auto', minWidth: 0 }}>Coupon trial cleanup</h3>
                <div
                  className="reconcile-actions-row"
                  style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center', flexShrink: 0 }}
                >
                  <button
                    type="button"
                    className="btn-refresh"
                    onClick={previewCouponReconcile}
                    disabled={couponReconcileLoading || couponReconcileApplying}
                  >
                    {couponReconcileLoading ? 'Scanning...' : 'Preview'}
                  </button>
                  {couponReconcilePreview?.dry_run !== false &&
                    (couponReconcilePreview?.candidate_count ?? 0) > 0 && (
                      <button
                        type="button"
                        className="btn-refresh"
                        onClick={applyCouponReconcile}
                        disabled={couponReconcileApplying || couponReconcileLoading}
                        style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                      >
                        {couponReconcileApplying
                          ? 'Applying...'
                          : `Confirm (${couponReconcilePreview.candidate_count})`}
                      </button>
                    )}
                  {couponReconcilePreview && (
                    <button
                      type="button"
                      className="btn-refresh"
                      onClick={() => setCouponReconcilePreview(null)}
                      disabled={couponReconcileLoading || couponReconcileApplying}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="toggle-container">
                <p className="toggle-description" style={{ marginTop: 0 }}>
                  Clear stale <code>premium_source=coupon</code> when <code>coupon_trial_end_at_iso</code> is in the
                  past. Keeps <code>coupon_code_applied</code> for history; sets <code>coupon_trial_expired_at_iso</code>.
                  Queries coupon users only (not your full user list).
                </p>
                {couponReconcilePreview && (
                  <div style={{ marginTop: 16 }}>
                    <p className="toggle-description" style={{ margin: '0 0 8px' }}>
                      Scanned <strong>{couponReconcilePreview.scanned ?? 0}</strong> coupon users ·{' '}
                      <strong>{couponReconcilePreview.candidate_count ?? 0}</strong> to update
                      {couponReconcilePreview.dry_run === false && (
                        <>
                          {' '}
                          · applied <strong>{couponReconcilePreview.updated ?? 0}</strong>
                          {(couponReconcilePreview.skipped ?? 0) > 0 && (
                            <> · skipped {couponReconcilePreview.skipped}</>
                          )}
                        </>
                      )}
                    </p>
                    {(couponReconcilePreview.candidates || []).length === 0 ? (
                      <p className="toggle-description" style={{ margin: 0 }}>
                        No stale coupon trials found.
                      </p>
                    ) : (
                      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Coupon</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Trial end</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Plan after</th>
                              <th style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(couponReconcilePreview.candidates || []).map((row) => (
                              <tr key={row.user_id}>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                                  {row.email || row.user_id}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                                  {row.coupon_code_applied || '—'}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                                  <strong>{row.status_from}</strong> → <strong>{row.status_to}</strong>
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                                  {row.coupon_trial_end_at_iso || '—'}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                                  {row.plan_tier_after || 'free'}
                                </td>
                                <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', maxWidth: 280 }}>
                                  {row.reason}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>YouTube URL feature</h3>
                <button
                  type="button"
                  onClick={loadYoutubeUrlToggle}
                  className="btn-refresh"
                  disabled={loadingToggle}
                >
                  {loadingToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={youtubeUrlEnabled}
                    onChange={toggleYoutubeUrl}
                    disabled={loadingToggle}
                  />
                  <span>Enable YouTube URL Feature</span>
                </label>
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Trim silence (processing)</h3>
                <button
                  type="button"
                  onClick={loadTrimSilenceFeatureToggle}
                  className="btn-refresh"
                  disabled={loadingTrimSilenceToggle}
                >
                  {loadingTrimSilenceToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={trimSilenceFeatureEnabled}
                    onChange={toggleTrimSilenceFeature}
                    disabled={loadingTrimSilenceToggle}
                  />
                  <span>Allow trim silence in the video pipeline</span>
                </label>
                <p className="toggle-description">
                  When off, every job skips silence trimming regardless of the user&apos;s Trim Silence toggle (saves processing time). When on, the user&apos;s choice applies.
                </p>
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Add B-roll (upload default)</h3>
                <button
                  type="button"
                  onClick={loadBrollDefaultToggle}
                  className="btn-refresh"
                  disabled={loadingBrollDefaultToggle}
                >
                  {loadingBrollDefaultToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={includeBrollDefaultEnabled}
                    onChange={toggleBrollDefault}
                    disabled={loadingBrollDefaultToggle}
                  />
                  <span>B-roll on by default for new uploads</span>
                </label>
                <p className="toggle-description">
                  Sets the initial Add B-roll toggle on the upload screen. Users can still turn B-roll on or off for each upload.
                </p>
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Streak push reminders (cron)</h3>
                <button
                  type="button"
                  onClick={() => {
                    loadStreakPushSchedulerToggle();
                    loadPushStats();
                  }}
                  className="btn-refresh"
                  disabled={loadingStreakPushToggle}
                >
                  {loadingStreakPushToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={streakPushSchedulerEnabled}
                    onChange={toggleStreakPushScheduler}
                    disabled={loadingStreakPushToggle}
                  />
                  <span>Enable streak push scheduler</span>
                </label>
                <p className="toggle-description">
                  When on, the backend sends one daily Web Push at local 7pm to all opted-in users (background thread every 15 min). Opt-in UI still works when off.
                </p>
                {pushStats && (
                  <p className="toggle-description" style={{ marginTop: 8 }}>
                    Last 7 days: <strong>{pushStats.sent ?? 0}</strong> sent · <strong>{pushStats.clicked ?? 0}</strong> clicked
                  </p>
                )}
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Phone number requirement for download</h3>
                <button
                  type="button"
                  onClick={loadPhoneDownloadToggle}
                  className="btn-refresh"
                  disabled={loadingPhoneDownloadToggle}
                >
                  {loadingPhoneDownloadToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={requirePhoneForDownload}
                    onChange={togglePhoneDownloadRequirement}
                    disabled={loadingPhoneDownloadToggle}
                  />
                  <span>Ask phone number before download</span>
                </label>
                <p className="toggle-description">
                  When on, users without a phone number must add one before downloading shorts.
                </p>
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Pricing page — Invite Friends card</h3>
                <button
                  type="button"
                  onClick={loadPricingInviteCardToggle}
                  className="btn-refresh"
                  disabled={loadingPricingInviteToggle}
                >
                  {loadingPricingInviteToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={pricingInviteCardEnabled}
                    onChange={togglePricingInviteCard}
                    disabled={loadingPricingInviteToggle}
                  />
                  <span>Show Invite Friends block on /pricing</span>
                </label>
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Remotion Lambda</h3>
                <button
                  type="button"
                  onClick={loadLambdaToggle}
                  className="btn-refresh"
                  disabled={loadingLambdaToggle}
                >
                  {loadingLambdaToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={lambdaEnabled}
                    onChange={toggleLambda}
                    disabled={loadingLambdaToggle}
                  />
                  <span>Enable Remotion AWS Lambda Rendering</span>
                </label>
                {!lambdaEnvConfigured && (
                  <p className="toggle-description" style={{ color: '#b45309', fontWeight: 600 }}>
                    Env config missing{lambdaMissingEnvFields.length ? `: ${lambdaMissingEnvFields.join(', ')}` : ''}. Lambda render will fall back to local render.
                  </p>
                )}
              </div>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Chatwoot template settings</h3>
                <button
                  type="button"
                  onClick={loadChatwootTemplatesToggle}
                  className="btn-refresh"
                  disabled={loadingChatwootToggle}
                >
                  {loadingChatwootToggle ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="toggle-container">
                <label>
                  <input
                    type="checkbox"
                    checked={chatwootTemplatesEnabled.welcome}
                    onChange={() => toggleChatwootTemplate('welcome')}
                    disabled={loadingChatwootToggle}
                  />
                  <span>Enable Welcome Template</span>
                </label>
              </div>
              <div className="toggle-container" style={{ marginTop: '15px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={chatwootTemplatesEnabled.finished}
                    onChange={() => toggleChatwootTemplate('finished')}
                    disabled={loadingChatwootToggle}
                  />
                  <span>Enable Finished Template</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'coupons' && (
          <div className="admin-content">
            <div className="section-header">
              <h2>Coupons</h2>
              <button type="button" onClick={loadCoupons} className="btn-refresh" disabled={loadingCoupons}>
                {loadingCoupons ? 'Loading...' : '🔄 Refresh'}
              </button>
            </div>

            <div className="cookie-section">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Create coupon</h3>
              </div>

              <div className="cookie-form coupons-form">
                <div className="coupons-form-row">
                  <div className="coupon-form-field">
                    <label htmlFor="coupon-create-code">Coupon code</label>
                    <input
                      id="coupon-create-code"
                      type="text"
                      placeholder="e.g. LAUNCH50"
                      autoComplete="off"
                      value={couponForm.code}
                      onChange={(e) => setCouponForm((p) => ({ ...p, code: e.target.value }))}
                    />
                  </div>
                  <div className="coupon-form-field">
                    <label htmlFor="coupon-create-type">Type</label>
                    <select
                      id="coupon-create-type"
                      value={couponForm.type}
                      onChange={(e) =>
                        setCouponForm((p) => ({
                          ...p,
                          type: e.target.value === 'paid' ? 'paid' : 'trial',
                        }))
                      }
                    >
                      <option value="trial">Free trial</option>
                      <option value="paid">Paid discount</option>
                    </select>
                  </div>
                  <div className="coupon-form-field">
                    <label htmlFor="coupon-create-max">Max uses</label>
                    <input
                      id="coupon-create-max"
                      type="number"
                      min={0}
                      placeholder="0 = unlimited"
                      value={couponForm.max_uses}
                      onChange={(e) => setCouponForm((p) => ({ ...p, max_uses: Number(e.target.value) }))}
                    />
                  </div>
                  <div className="coupon-form-field">
                    <label htmlFor="coupon-create-expires">Expiry</label>
                    <DatePicker
                      id="coupon-create-expires"
                      selected={couponForm.expires_at_date}
                      onChange={(date) => {
                        if (!date) {
                          setCouponForm((p) => ({ ...p, expires_at_date: null, expires_at_iso: '' }));
                          return;
                        }
                        const endUtc = new Date(
                          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)
                        );
                        setCouponForm((p) => ({
                          ...p,
                          expires_at_date: date,
                          expires_at_iso: endUtc.toISOString().replace('.000Z', 'Z'),
                        }));
                      }}
                      placeholderText="Expiry date"
                      className="coupon-date-input"
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      isClearable
                    />
                  </div>
                </div>

                {couponForm.type === 'trial' ? (
                  <div className="coupons-form-row coupons-form-row--compact">
                    <div className="coupon-form-field coupon-form-field--narrow">
                      <label htmlFor="coupon-create-trial">Trial days</label>
                      <input
                        id="coupon-create-trial"
                        type="number"
                        min={1}
                        placeholder="7"
                        value={couponForm.trial_days}
                        onChange={(e) =>
                          setCouponForm((p) => ({ ...p, trial_days: Number(e.target.value) }))
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="coupons-form-row coupons-form-row--compact">
                      <div className="coupon-form-field">
                        <label htmlFor="coupon-create-plan-in">Plan ID (IN)</label>
                        <input
                          id="coupon-create-plan-in"
                          type="text"
                          placeholder="plan_xxxxx"
                          value={couponForm.razorpay_plan_id_in}
                          onChange={(e) =>
                            setCouponForm((p) => ({ ...p, razorpay_plan_id_in: e.target.value }))
                          }
                        />
                      </div>
                      <div className="coupon-form-field">
                        <label htmlFor="coupon-create-plan-intl">Plan ID (Intl)</label>
                        <input
                          id="coupon-create-plan-intl"
                          type="text"
                          placeholder="Optional"
                          value={couponForm.razorpay_plan_id_intl}
                          onChange={(e) =>
                            setCouponForm((p) => ({ ...p, razorpay_plan_id_intl: e.target.value }))
                          }
                        />
                      </div>
                      <div className="coupon-form-field coupon-form-field--narrow">
                        <label htmlFor="coupon-create-price-new">Discount price</label>
                        <input
                          id="coupon-create-price-new"
                          type="text"
                          placeholder="₹100"
                          value={couponForm.display_price_new}
                          onChange={(e) =>
                            setCouponForm((p) => ({ ...p, display_price_new: e.target.value }))
                          }
                        />
                      </div>
                      <div className="coupon-form-field coupon-form-field--narrow">
                        <label htmlFor="coupon-create-period">Period</label>
                        <input
                          id="coupon-create-period"
                          type="text"
                          placeholder="per month"
                          value={couponForm.display_period}
                          onChange={(e) =>
                            setCouponForm((p) => ({ ...p, display_period: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <p className="coupon-form-hint coupon-form-hint--row">
                      Strikethrough uses current site pricing automatically.
                    </p>
                  </>
                )}

                <div className="coupons-form-row coupons-form-row--actions">
                  <button type="button" className="btn-add" onClick={createCoupon} disabled={creatingCoupon}>
                    {creatingCoupon ? 'Creating...' : 'Create coupon'}
                  </button>
                </div>
              </div>
            </div>

            <div className="cookie-section coupons-panel">
              <div className="section-header" style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>All coupons</h3>
              </div>

              {couponsError && <p style={{ color: '#b00020' }}>{couponsError}</p>}
              {!couponsError && !loadingCoupons && coupons.length === 0 && <p>No coupons yet.</p>}

              {coupons.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table coupons-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Type</th>
                        <th>Trial / price</th>
                        <th>Used</th>
                        <th>Max</th>
                        <th>Expires</th>
                        <th>Active</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupons.map((c) => {
                        const code = String(c.code || '').toUpperCase();
                        const editing = editingCouponCode === code;
                        return (
                          <tr key={code || Math.random()}>
                            <td style={{ fontWeight: 800 }}>{code}</td>
                            <td>{c.type === 'paid' ? 'Paid' : 'Trial'}</td>
                            <td>
                              {editing ? (
                                c.type === 'paid' || editCouponPatch.type === 'paid' ? (
                                  <div className="coupon-edit-paid-fields">
                                    <input
                                      type="text"
                                      placeholder="Plan ID (IN)"
                                      value={editCouponPatch.razorpay_plan_id_in}
                                      onChange={(e) =>
                                        setEditCouponPatch((p) => ({
                                          ...p,
                                          razorpay_plan_id_in: e.target.value,
                                        }))
                                      }
                                    />
                                    <input
                                      type="text"
                                      placeholder="Price new"
                                      value={editCouponPatch.display_price_new}
                                      onChange={(e) =>
                                        setEditCouponPatch((p) => ({
                                          ...p,
                                          display_price_new: e.target.value,
                                        }))
                                      }
                                    />
                                  </div>
                                ) : (
                                  <input
                                    type="number"
                                    value={editCouponPatch.trial_days}
                                    onChange={(e) =>
                                      setEditCouponPatch((p) => ({
                                        ...p,
                                        trial_days: Number(e.target.value),
                                      }))
                                    }
                                  />
                                )
                              ) : c.type === 'paid' ? (
                                <>
                                  {c.display_price_new || '—'}
                                  {c.display_period ? ` ${c.display_period}` : ''}
                                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                                    {c.razorpay_plan_id_in || 'no plan id'}
                                  </div>
                                </>
                              ) : (
                                c.trial_days
                              )}
                            </td>
                            <td>{c.used_count || 0}</td>
                            <td>
                              {editing ? (
                                <input
                                  type="number"
                                  value={editCouponPatch.max_uses}
                                  onChange={(e) => setEditCouponPatch((p) => ({ ...p, max_uses: Number(e.target.value) }))}
                                />
                              ) : (
                                c.max_uses
                              )}
                            </td>
                            <td>
                              {editing ? (
                                <DatePicker
                                  selected={editCouponPatch.expires_at_date}
                                  onChange={(date) => {
                                    if (!date) {
                                      setEditCouponPatch((p) => ({ ...p, expires_at_date: null, expires_at_iso: '' }));
                                      return;
                                    }
                                    const endUtc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59));
                                    setEditCouponPatch((p) => ({
                                      ...p,
                                      expires_at_date: date,
                                      expires_at_iso: endUtc.toISOString().replace('.000Z', 'Z'),
                                    }));
                                  }}
                                  placeholderText="Expiry date"
                                  className="coupon-date-input"
                                  dateFormat="yyyy-MM-dd"
                                  minDate={new Date()}
                                  isClearable
                                />
                              ) : (
                                c.expires_at_iso || c.expires_at || ''
                              )}
                            </td>
                            <td>
                              {editing ? (
                                <input
                                  type="checkbox"
                                  checked={editCouponPatch.is_active === true}
                                  onChange={(e) => setEditCouponPatch((p) => ({ ...p, is_active: e.target.checked }))}
                                />
                              ) : (
                                c.is_active ? '✅' : '❌'
                              )}
                            </td>
                            <td>
                              {!editing ? (
                                <div className="coupon-row-actions">
                                  <button
                                    type="button"
                                    className="coupon-icon-btn"
                                    title={c.is_active ? 'Pause' : 'Start'}
                                    aria-label={c.is_active ? 'Pause coupon' : 'Start coupon'}
                                    onClick={() => toggleCouponActive(code, !c.is_active)}
                                  >
                                    {c.is_active ? '⏸' : '▶'}
                                  </button>
                                  <button
                                    type="button"
                                    className="coupon-icon-btn"
                                    title="Edit"
                                    aria-label="Edit coupon"
                                    onClick={() => startEditCoupon(c)}
                                  >
                                    ✎
                                  </button>
                                  <button
                                    type="button"
                                    className="coupon-icon-btn danger"
                                    title="Delete"
                                    aria-label="Delete coupon"
                                    onClick={() => deleteCoupon(code)}
                                  >
                                    🗑
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button type="button" className="btn-small" onClick={saveEditCoupon}>
                                    Save
                                  </button>
                                  <button type="button" className="btn-small" onClick={() => setEditingCouponCode('')}>
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'daily-content' && (
          <div className="admin-content daily-content-admin-tab">
            <div className="daily-content-admin-header">
              <div className="daily-content-admin-header__intro">
                <h2>Daily Content</h2>
                <p className="daily-content-admin-header__hint">
                  Configure the global LLM, assign per-user system prompts, and review script like/dislike feedback from premium creators.
                </p>
              </div>
              <div className="daily-content-admin-header__actions">
                <button
                  type="button"
                  onClick={() => {
                    loadDailyContentPrompts();
                    loadDailyContentLlmConfig();
                    loadScriptFeedbackSurvey();
                  }}
                  className="btn-refresh"
                  disabled={loadingDailyContentPrompts || loadingDailyContentLlmConfig || loadingScriptFeedback}
                >
                  {loadingDailyContentPrompts || loadingDailyContentLlmConfig || loadingScriptFeedback
                    ? 'Loading…'
                    : '🔄 Refresh all'}
                </button>
              </div>
            </div>

            <div className="daily-content-admin-stats">
              <div className="daily-content-admin-stat">
                <span className="daily-content-admin-stat__label">Prompt assignments</span>
                <span className="daily-content-admin-stat__value">{dailyContentPrompts.length}</span>
              </div>
              <div className="daily-content-admin-stat">
                <span className="daily-content-admin-stat__label">Scheduler</span>
                <span
                  className={`daily-content-admin-stat__value ${dailyContentSchedulerEnabled ? 'daily-content-admin-stat__value--on' : 'daily-content-admin-stat__value--off'}`}
                >
                  {dailyContentSchedulerEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="daily-content-admin-stat">
                <span className="daily-content-admin-stat__label">Script feedback</span>
                <span className="daily-content-admin-stat__value">{scriptFeedbackRows.length}</span>
              </div>
              <div className="daily-content-admin-stat">
                <span className="daily-content-admin-stat__label">Active LLM</span>
                <span
                  className="daily-content-admin-stat__value"
                  title={`${dailyContentLlmConfig.provider || 'nvidia_nim'} / ${dailyContentLlmConfig.model || ''}`}
                >
                  {dailyContentLlmConfig.provider || 'nvidia_nim'} · {dailyContentLlmConfig.model || '—'}
                </span>
              </div>
            </div>

            <div className="daily-content-admin-grid daily-content-admin-grid--settings">
              <section className="daily-content-admin-panel">
                <div className="daily-content-admin-panel__head">
                  <div>
                    <h3>LLM settings</h3>
                    <p className="daily-content-admin-panel__desc">
                      Switch provider and model without redeploying. API keys stay in config.env.
                    </p>
                  </div>
                </div>
                {dailyContentLlmConfigError && (
                  <p className="daily-content-admin-error">{dailyContentLlmConfigError}</p>
                )}
                <div className="daily-content-admin-llm-grid">
                  <div className="coupon-form-field">
                    <label htmlFor="dc-llm-provider">Provider</label>
                    <select
                      id="dc-llm-provider"
                      value={dailyContentLlmConfig.provider || 'nvidia_nim'}
                      onChange={(e) =>
                        setDailyContentLlmConfig((p) => ({ ...p, provider: e.target.value }))
                      }
                    >
                      <option value="nvidia_nim">NVIDIA NIM</option>
                      <option value="openrouter">OpenRouter</option>
                    </select>
                  </div>
                  <div className="coupon-form-field">
                    <label htmlFor="dc-llm-model">Model</label>
                    <input
                      id="dc-llm-model"
                      type="text"
                      value={dailyContentLlmConfig.model || ''}
                      onChange={(e) =>
                        setDailyContentLlmConfig((p) => ({ ...p, model: e.target.value }))
                      }
                    />
                  </div>
                  <div className="coupon-form-field">
                    <label htmlFor="dc-llm-fallback-provider">Fallback provider</label>
                    <select
                      id="dc-llm-fallback-provider"
                      value={dailyContentLlmConfig.fallback_provider || 'openrouter'}
                      onChange={(e) =>
                        setDailyContentLlmConfig((p) => ({ ...p, fallback_provider: e.target.value }))
                      }
                    >
                      <option value="openrouter">OpenRouter</option>
                      <option value="nvidia_nim">NVIDIA NIM</option>
                    </select>
                  </div>
                  <div className="coupon-form-field">
                    <label htmlFor="dc-llm-fallback-model">Fallback model</label>
                    <input
                      id="dc-llm-fallback-model"
                      type="text"
                      value={dailyContentLlmConfig.fallback_model || ''}
                      onChange={(e) =>
                        setDailyContentLlmConfig((p) => ({ ...p, fallback_model: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="daily-content-admin-llm-grid daily-content-admin-llm-grid--params">
                  <div className="coupon-form-field coupon-form-field--narrow">
                    <label htmlFor="dc-llm-temperature">Temperature</label>
                    <input
                      id="dc-llm-temperature"
                      type="number"
                      min={0}
                      max={2}
                      step={0.1}
                      value={dailyContentLlmConfig.temperature ?? 0.7}
                      onChange={(e) =>
                        setDailyContentLlmConfig((p) => ({
                          ...p,
                          temperature: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="coupon-form-field coupon-form-field--narrow">
                    <label htmlFor="dc-llm-max-tokens">Max tokens</label>
                    <input
                      id="dc-llm-max-tokens"
                      type="number"
                      min={100}
                      max={4000}
                      value={dailyContentLlmConfig.max_tokens ?? 800}
                      onChange={(e) =>
                        setDailyContentLlmConfig((p) => ({
                          ...p,
                          max_tokens: Number(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="daily-content-admin-actions">
                  <button
                    type="button"
                    className="btn-small"
                    onClick={saveDailyContentLlmConfig}
                    disabled={savingDailyContentLlmConfig}
                  >
                    {savingDailyContentLlmConfig ? 'Saving…' : 'Save LLM config'}
                  </button>
                </div>
              </section>

              <section className="daily-content-admin-panel">
                <div className="daily-content-admin-panel__head">
                  <div>
                    <h3>Automation</h3>
                    <p className="daily-content-admin-panel__desc">
                      Cron generates daily scripts for enabled users at their local send hour.
                    </p>
                  </div>
                </div>
                <div className="daily-content-admin-automation">
                  <div
                    className={`daily-content-admin-automation__status ${dailyContentSchedulerEnabled ? 'daily-content-admin-automation__status--on' : 'daily-content-admin-automation__status--off'}`}
                  >
                    {dailyContentSchedulerEnabled ? '● Scheduler running' : '○ Scheduler paused'}
                  </div>
                  <label className="daily-content-admin-toggle">
                    <input
                      type="checkbox"
                      checked={dailyContentSchedulerEnabled}
                      onChange={toggleDailyContentScheduler}
                      disabled={loadingDailyContentSchedulerToggle}
                    />
                    <span>Enable daily content scheduler</span>
                  </label>
                </div>
              </section>
            </div>

            <section className="daily-content-admin-panel">
              <div className="daily-content-admin-panel__head">
                <div>
                  <h3>User prompts</h3>
                  <p className="daily-content-admin-panel__desc">
                    Assign a personalised system prompt to each premium creator. They receive one script per day.
                  </p>
                </div>
              </div>
              {dailyContentPromptsError && (
                <p className="daily-content-admin-error">{dailyContentPromptsError}</p>
              )}

              <div className="daily-content-admin-grid daily-content-admin-grid--prompts">
                <div className="daily-content-admin-card">
                  <h4 className="daily-content-admin-card__title">New assignment</h4>
                  <div className="coupons-form-row coupons-form-row--compact">
                    <div className="coupon-form-field">
                      <label htmlFor="dc-prompt-email">User email</label>
                      <input
                        id="dc-prompt-email"
                        type="email"
                        placeholder="creator@example.com"
                        value={dailyContentForm.email}
                        onChange={(e) =>
                          setDailyContentForm((p) => ({ ...p, email: e.target.value }))
                        }
                      />
                    </div>
                    <div className="coupon-form-field coupon-form-field--narrow">
                      <label htmlFor="dc-prompt-hour">Send hour</label>
                      <input
                        id="dc-prompt-hour"
                        type="number"
                        min={0}
                        max={23}
                        value={dailyContentForm.send_hour_local}
                        onChange={(e) =>
                          setDailyContentForm((p) => ({
                            ...p,
                            send_hour_local: Number(e.target.value),
                          }))
                        }
                      />
                    </div>
                    <div className="coupon-form-field coupon-form-field--narrow">
                      <label htmlFor="dc-prompt-enabled">Enabled</label>
                      <select
                        id="dc-prompt-enabled"
                        value={dailyContentForm.enabled ? 'yes' : 'no'}
                        onChange={(e) =>
                          setDailyContentForm((p) => ({
                            ...p,
                            enabled: e.target.value === 'yes',
                          }))
                        }
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>
                  <div className="coupon-form-field" style={{ width: '100%', marginTop: 10 }}>
                    <label htmlFor="dc-prompt-system">System prompt</label>
                    <textarea
                      id="dc-prompt-system"
                      rows={5}
                      placeholder="You are a content strategist for a creator who..."
                      value={dailyContentForm.system_prompt}
                      onChange={(e) =>
                        setDailyContentForm((p) => ({ ...p, system_prompt: e.target.value }))
                      }
                      style={{ width: '100%', minHeight: 110, fontFamily: 'inherit' }}
                    />
                  </div>
                  <div className="daily-content-admin-actions">
                    <button
                      type="button"
                      className="btn-small"
                      onClick={createDailyContentPrompt}
                      disabled={creatingDailyContentPrompt}
                    >
                      {creatingDailyContentPrompt ? 'Creating…' : 'Assign prompt'}
                    </button>
                  </div>
                </div>

                {previewDailyContent ? (
                  <div className="daily-content-admin-card daily-content-admin-preview">
                    <div className="daily-content-admin-panel__head" style={{ marginBottom: 8 }}>
                      <h4 className="daily-content-admin-card__title" style={{ margin: 0 }}>Preview generate</h4>
                      <button type="button" className="btn-small" onClick={() => setPreviewDailyContent(null)}>
                        Clear
                      </button>
                    </div>
                    <p className="daily-content-admin-preview__idea">{previewDailyContent.idea}</p>
                    <pre className="daily-content-admin-preview__script">{previewDailyContent.script}</pre>
                  </div>
                ) : (
                  <div className="daily-content-admin-card">
                    <h4 className="daily-content-admin-card__title">Preview</h4>
                    <p className="daily-content-admin-panel__desc" style={{ margin: 0 }}>
                      Use ▶ on an assignment row to test-generate idea + script without saving to the user.
                    </p>
                  </div>
                )}
              </div>

              <div className="daily-content-admin-table-wrap">
                {loadingDailyContentPrompts && (
                  <p className="daily-content-admin-empty">Loading prompt assignments…</p>
                )}
                {!loadingDailyContentPrompts && dailyContentPrompts.length === 0 && (
                  <p className="daily-content-admin-empty">No prompt assignments yet.</p>
                )}
                {!loadingDailyContentPrompts && dailyContentPrompts.length > 0 && (
                  <table className="admin-table coupons-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Enabled</th>
                        <th>Hour</th>
                        <th>Ver.</th>
                        <th>System prompt</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyContentPrompts.map((p) => {
                        const uid = p.user_id || '';
                        const editing = editingDailyContentUserId === uid;
                        return (
                          <tr key={uid}>
                            <td>{p.email || uid}</td>
                            <td>
                              {editing ? (
                                <select
                                  value={editDailyContentPatch.enabled ? 'yes' : 'no'}
                                  onChange={(e) =>
                                    setEditDailyContentPatch((prev) => ({
                                      ...prev,
                                      enabled: e.target.value === 'yes',
                                    }))
                                  }
                                >
                                  <option value="yes">Yes</option>
                                  <option value="no">No</option>
                                </select>
                              ) : (
                                p.enabled ? 'Yes' : 'No'
                              )}
                            </td>
                            <td>
                              {editing ? (
                                <input
                                  type="number"
                                  min={0}
                                  max={23}
                                  value={editDailyContentPatch.send_hour_local}
                                  onChange={(e) =>
                                    setEditDailyContentPatch((prev) => ({
                                      ...prev,
                                      send_hour_local: Number(e.target.value),
                                    }))
                                  }
                                  style={{ width: 56 }}
                                />
                              ) : (
                                p.send_hour_local ?? 8
                              )}
                            </td>
                            <td>{p.prompt_version ?? 1}</td>
                            <td style={{ maxWidth: 320 }}>
                              {editing ? (
                                <textarea
                                  rows={4}
                                  value={editDailyContentPatch.system_prompt}
                                  onChange={(e) =>
                                    setEditDailyContentPatch((prev) => ({
                                      ...prev,
                                      system_prompt: e.target.value,
                                    }))
                                  }
                                  style={{ width: '100%', minWidth: 220, fontFamily: 'inherit' }}
                                />
                              ) : (
                                <span
                                  title={p.system_prompt || ''}
                                  style={{
                                    display: 'block',
                                    maxWidth: 300,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {p.system_prompt || '—'}
                                </span>
                              )}
                            </td>
                            <td>
                              {!editing ? (
                                <div className="coupon-row-actions">
                                  <button
                                    type="button"
                                    className="coupon-icon-btn"
                                    title="Preview generate"
                                    aria-label="Preview generate"
                                    disabled={previewingDailyContentUserId === uid}
                                    onClick={() => previewDailyContentPrompt(uid)}
                                  >
                                    {previewingDailyContentUserId === uid ? '…' : '▶'}
                                  </button>
                                  <button
                                    type="button"
                                    className="coupon-icon-btn"
                                    title="Edit"
                                    aria-label="Edit prompt"
                                    onClick={() => startEditDailyContentPrompt(p)}
                                  >
                                    ✎
                                  </button>
                                  <button
                                    type="button"
                                    className="coupon-icon-btn danger"
                                    title="Delete"
                                    aria-label="Delete prompt"
                                    onClick={() => deleteDailyContentPrompt(uid)}
                                  >
                                    🗑
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button type="button" className="btn-small" onClick={saveEditDailyContentPrompt}>
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-small"
                                    onClick={() => setEditingDailyContentUserId('')}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            <section className="daily-content-admin-panel">
              <div className="daily-content-admin-panel__head">
                <div>
                  <h3>Script feedback survey</h3>
                  <p className="daily-content-admin-panel__desc">
                    Like/dislike responses from daily content (stored on VPS, not Firestore).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={loadScriptFeedbackSurvey}
                  className="btn-refresh"
                  disabled={loadingScriptFeedback}
                >
                  {loadingScriptFeedback && scriptFeedbackRows.length === 0 ? 'Loading…' : '🔄 Refresh'}
                </button>
              </div>
              <div className="daily-content-admin-feedback-meta">
                <span>
                  <strong>{scriptFeedbackRows.filter((r) => r.sentiment === 'like').length}</strong> likes
                </span>
                <span>
                  <strong>{scriptFeedbackRows.filter((r) => r.sentiment === 'dislike').length}</strong> dislikes
                </span>
              </div>
              {scriptFeedbackError && (
                <p className="daily-content-admin-error">{scriptFeedbackError}</p>
              )}
              {loadingScriptFeedback && scriptFeedbackRows.length === 0 ? (
                <p className="daily-content-admin-empty">Loading script feedback…</p>
              ) : !scriptFeedbackRows.length && !scriptFeedbackError ? (
                <p className="daily-content-admin-empty">No script feedback recorded yet.</p>
              ) : (
                <div className="webhooks-table-wrapper daily-content-admin-table-wrap" style={{ marginTop: 0 }}>
                  <table className="webhooks-table survey-table">
                    <thead>
                      <tr>
                        <th className="survey-col-created">Submitted</th>
                        <th className="survey-col-email">Email</th>
                        <th className="survey-col-uid">User ID</th>
                        <th>Sentiment</th>
                        <th>Hook</th>
                        <th>Title</th>
                        <th className="survey-col-other">Dislike reason</th>
                        <th>Source</th>
                        <th>Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scriptFeedbackRows.map((row) => (
                        <tr key={row.id}>
                          <td className="survey-col-created">
                            {row.submitted_at ? formatDate(row.submitted_at) : '—'}
                          </td>
                          <td className="survey-col-email">{row.user_email || '—'}</td>
                          <td className="survey-col-uid">
                            <code title={row.user_id || ''}>{row.user_id || '—'}</code>
                          </td>
                          <td>{row.sentiment === 'like' ? '👍 Like' : row.sentiment === 'dislike' ? '👎 Dislike' : '—'}</td>
                          <td>{row.hook_score != null ? row.hook_score : '—'}</td>
                          <td title={row.title || ''}>{row.title || '—'}</td>
                          <td className="survey-col-other">
                            {row.dislike_reason ? (
                              <span title={row.dislike_reason}>{row.dislike_reason}</span>
                            ) : '—'}
                          </td>
                          <td>{row.source || '—'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn-small"
                              disabled={!row.id || deletingScriptFeedbackId === row.id}
                              onClick={() => deleteScriptFeedback(row.id)}
                              style={{
                                color: '#b91c1c',
                                borderColor: '#fecaca',
                                background: '#fff',
                              }}
                            >
                              {deletingScriptFeedbackId === row.id ? '…' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        {activeTab === 'daily-content-requests' && (
          <div className="admin-content webhooks-tab survey-tab">
            <div className="section-header">
              <h2>Daily Content — Get Now Requests</h2>
              <div className="webhooks-toolbar">
                <button
                  type="button"
                  onClick={loadGetNowRequests}
                  className="btn-refresh"
                  disabled={loadingGetNowRequests}
                >
                  {loadingGetNowRequests && getNowRequestRows.length === 0 ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '14px' }}>
              Creators who click <strong>Get now</strong> on the Daily Content Idea page appear here with their email.
            </p>

            {getNowRequestsError && (
              <div className="empty-state" style={{ color: '#b91c1c' }}>
                {getNowRequestsError}
              </div>
            )}

            {loadingGetNowRequests && getNowRequestRows.length === 0 ? (
              <div className="loading-state">Loading get-now requests…</div>
            ) : !getNowRequestRows.length && !getNowRequestsError ? (
              <div className="empty-state">No get-now requests yet.</div>
            ) : (
              <div className="webhooks-table-wrapper">
                <table className="webhooks-table survey-table">
                  <thead>
                    <tr>
                      <th className="survey-col-created">Requested</th>
                      <th className="survey-col-email">Email</th>
                      <th className="survey-col-uid">User ID</th>
                      <th>Profile ready</th>
                      <th>Manage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getNowRequestRows.map((row) => (
                      <tr key={row.id}>
                        <td className="survey-col-created">
                          {row.requested_at ? formatDate(row.requested_at) : '—'}
                        </td>
                        <td className="survey-col-email">{row.user_email || '—'}</td>
                        <td className="survey-col-uid">
                          <code title={row.user_id || ''}>{row.user_id || '—'}</code>
                        </td>
                        <td>{row.has_prompt ? 'Yes' : 'No — needs setup'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-small"
                            disabled={!row.id || deletingGetNowRequestId === row.id}
                            onClick={() => deleteGetNowRequest(row.id)}
                            style={{
                              color: '#b91c1c',
                              borderColor: '#fecaca',
                              background: '#fff',
                            }}
                          >
                            {deletingGetNowRequestId === row.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <div className="admin-content">
            <div className="section-header">
              <h2>Customer Files</h2>
              <button onClick={loadCustomers} className="btn-refresh" disabled={loadingFiles}>
                {loadingFiles ? 'Loading...' : '🔄 Refresh'}
              </button>
            </div>

            {/* Latest 25 by mtime (input + archive only; no output — avoids duplicates) */}
            <div
              className="admin-recent-files-strip"
              style={{
                marginBottom: '14px',
                padding: '10px 12px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            >
              <div
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#475569',
                  marginBottom: '8px',
                }}
              >
                Latest 25 files
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  overflowX: 'auto',
                  paddingBottom: '4px',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {recentFiles.length === 0 && !loadingFiles && (
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No files yet</span>
                )}
                {recentFiles.map((rf) => {
                  const ownerLabel = rf.user_id ? getUserEmail(rf.user_id) : '';
                  const dlKey = makeFileDownloadKey(rf.file_type, rf.path);
                  const isDownloading = Boolean(fileDownloadProgress[dlKey]);
                  const thumbUrl =
                    streamTokenForAssets && rf.path
                      ? `/api/admin/file/thumbnail?${new URLSearchParams({
                          file_path: rf.path,
                          file_type: rf.file_type,
                          token: streamTokenForAssets,
                        }).toString()}`
                      : '';
                  const onPick = () => {
                    if (isDownloading) return;
                    if (rf.kind === 'video') {
                      openStreamPreview(rf.path, rf.file_type, rf.name, 'video', rf.user_id);
                    } else if (rf.kind === 'image') {
                      openStreamPreview(rf.path, rf.file_type, rf.name, 'image', rf.user_id);
                    } else {
                      handleDownload(rf.path, rf.file_type, rf.name);
                    }
                  };
                  return (
                    <button
                      key={`${rf.file_type}-${rf.path}`}
                      type="button"
                      onClick={onPick}
                      disabled={isDownloading}
                      title={[rf.name, ownerLabel].filter(Boolean).join('\n')}
                      style={{
                        flex: '0 0 auto',
                        width: '100px',
                        padding: 0,
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        cursor: isDownloading ? 'wait' : 'pointer',
                        background: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          height: '100px',
                          background: '#e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {thumbUrl && rf.kind !== 'other' ? (
                          <img
                            src={thumbUrl}
                            alt=""
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: '0.7rem', color: '#64748b', padding: '4px', textAlign: 'center' }}>
                            {rf.kind === 'other' ? rf.name?.slice(0, 12) || 'File' : '…'}
                          </span>
                        )}
                        <AdminFileDownloadProgressOverlay
                          fileType={rf.file_type}
                          filePath={rf.path}
                          fileDownloadProgress={fileDownloadProgress}
                        />
                      </div>
                      <div style={{ padding: '4px 6px', textAlign: 'left', lineHeight: 1.2, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '0.65rem',
                            color: '#475569',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {rf.name}
                        </div>
                        {ownerLabel && (
                          <div
                            style={{
                              fontSize: '0.58rem',
                              color: '#64748b',
                              marginTop: '2px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {ownerLabel}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Back button if navigated from task */}
            {navigationState.fromTask && navigationState.previousTab === 'firestore' && (
              <div style={{ marginBottom: '15px', padding: '12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#0369a1', fontSize: '0.9rem' }}>
                  <strong>Viewing files from task:</strong> {navigationState.fromTask.taskId}
                </div>
                <button
                  onClick={() => {
                    const taskId = navigationState.fromTask?.taskId;
                    const previousCollection = navigationState.fromTask?.collection || 'tasks';
                    // Clear navigation state first
                    setNavigationState({
                      fromTask: null,
                      previousCollection: null,
                      previousTab: null,
                      fromUserJourney: null,
                      fromFileManagement: null,
                      fromFirestoreUsers: null,
                    });
                    // Restore previous state
                    setSelectedCustomer(null);
                    setFilenameSearch('');
                    setSelectedCollection(previousCollection);
                    setActiveTab('firestore');
                    // Expand and scroll back to task - wait for tab switch and DOM render
                    if (taskId) {
                      // Use a function that retries until the element is found
                      const expandAndScrollToTask = (retryCount = 0) => {
                        const element = document.querySelector(`[data-task-id="${taskId}"]`);
                        if (element) {
                          // Element found, expand it
                          setCollapsedItems(prev => {
                            const newSet = new Set(prev.tasks);
                            newSet.delete(taskId);
                            return {
                              ...prev,
                              tasks: newSet
                            };
                          });
                          // Wait for expansion to render, then scroll
                          setTimeout(() => {
                            const expandedElement = document.querySelector(`[data-task-id="${taskId}"]`);
                            if (expandedElement) {
                              expandedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                        } else if (retryCount < 10) {
                          // Element not found yet, retry after delay
                          setTimeout(() => {
                            expandAndScrollToTask(retryCount + 1);
                          }, 200);
                        }
                      };
                      
                      // Start checking after initial delay for tab switch
                      setTimeout(() => {
                        expandAndScrollToTask();
                      }, 200);
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500'
                  }}
                >
                  ← Back to Task
                </button>
              </div>
            )}

            {navigationState.fromFirestoreUsers && !navigationState.fromTask && (
              <div
                style={{
                  marginBottom: '15px',
                  padding: '12px',
                  background: '#eff6ff',
                  borderRadius: '6px',
                  border: '1px solid #93c5fd',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ color: '#1d4ed8', fontSize: '0.9rem' }}>
                  <strong>From Users:</strong> {navigationState.fromFirestoreUsers.email}
                </div>
                <button
                  type="button"
                  onClick={handleBackToFirestoreUsers}
                  style={{
                    padding: '6px 12px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                  }}
                >
                  ← Back to Users
                </button>
              </div>
            )}

            {/* Search: email, user id, or file names — single compact toolbar row */}
            <div
              style={{
                marginBottom: '16px',
                padding: '10px 12px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              {!loadingFiles && customers.length > 0 && (
                <div
                  style={{
                    marginBottom: '10px',
                    fontSize: '0.875rem',
                    color: '#374151',
                    fontWeight: 600,
                  }}
                >
                  Total storage (all users): {formatFileSize(fileMgmtStorageTotalBytes)}
                  <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '8px' }}>
                    · {customers.length} user{customers.length === 1 ? '' : 's'}
                  </span>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '8px',
                  rowGap: '8px',
                }}
              >
                <label
                  htmlFor="filename-search"
                  style={{ fontWeight: 500, color: '#555', flexShrink: 0, fontSize: '0.875rem' }}
                >
                  Search:
                </label>
                <input
                  id="filename-search"
                  type="text"
                  placeholder="Email, user id, or file name…"
                  value={filenameSearch}
                  onChange={(e) => setFilenameSearch(e.target.value)}
                  style={{
                    flex: '1 1 160px',
                    minWidth: 'min(100%, 140px)',
                    padding: '6px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                  }}
                />
                {filenameSearch.trim() && (
                  <button
                    type="button"
                    onClick={() => setFilenameSearch('')}
                    style={{
                      padding: '6px 10px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    Clear search
                  </button>
                )}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 500,
                    color: '#555',
                    fontSize: '0.875rem',
                    flexShrink: 0,
                  }}
                >
                  Sort by size:
                  <select
                    value={fileSortBySize}
                    onChange={(e) => setFileSortBySize(e.target.value)}
                    style={{
                      padding: '6px 8px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.875rem',
                      maxWidth: '200px',
                    }}
                  >
                    <option value="none">Default (server order)</option>
                    <option value="largest">Largest first</option>
                    <option value="smallest">Smallest first</option>
                  </select>
                </label>
                {selectedFileKeys.size > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={handleBatchDeleteSelectedFiles}
                      disabled={batchDeletingFiles}
                      style={{
                        padding: '6px 12px',
                        background: batchDeletingFiles ? '#9ca3af' : '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: batchDeletingFiles ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {batchDeletingFiles ? 'Deleting…' : `Delete selected (${selectedFileKeys.size})`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedFileKeys(new Set())}
                      disabled={batchDeletingFiles}
                      style={{
                        padding: '6px 12px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: batchDeletingFiles ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        flexShrink: 0,
                      }}
                    >
                      Clear selection
                    </button>
                  </>
                )}
              </div>
            </div>

            {loadingFiles ? (
              <div className="loading-state">Loading customers...</div>
            ) : customers.length === 0 ? (
              <div className="empty-state">No customers found</div>
            ) : (
              <div className="customers-list">
                {customers
                  .map((customer) => {
                    const q = filenameSearch.trim().toLowerCase();
                    const searchActive = q.length > 0;

                    const filterFilesByName = (files) => {
                      if (!searchActive) return files || [];
                      return (files || []).filter((file) => file.name.toLowerCase().includes(q));
                    };

                    let byNameInput = filterFilesByName(customer.input_files);
                    let byNameOutput = filterFilesByName(customer.output_files);
                    let byNameArchive = filterFilesByName(customer.archive_files || []);

                    const anyFileMatchedByName =
                      byNameInput.length > 0 || byNameOutput.length > 0 || byNameArchive.length > 0;

                    const emailStr = getUserEmail(customer.user_id);
                    const emailOrIdMatches =
                      searchActive &&
                      (String(emailStr).toLowerCase().includes(q) ||
                        String(customer.user_id ?? '').toLowerCase().includes(q));

                    let filteredInputFiles = byNameInput;
                    let filteredOutputFiles = byNameOutput;
                    let filteredArchiveFiles = byNameArchive;

                    if (searchActive && emailOrIdMatches && !anyFileMatchedByName) {
                      filteredInputFiles = customer.input_files || [];
                      filteredOutputFiles = customer.output_files || [];
                      filteredArchiveFiles = customer.archive_files || [];
                    }

                    if (searchActive && !emailOrIdMatches && !anyFileMatchedByName) {
                      return null;
                    }

                    const sortedInputFiles = sortFilesBySize(filteredInputFiles, fileSortBySize);
                    const sortedOutputFiles = sortFilesBySize(filteredOutputFiles, fileSortBySize);
                    const sortedArchiveFiles = sortFilesBySize(filteredArchiveFiles, fileSortBySize);

                    const customerTotalBytes =
                      typeof customer.total_bytes === 'number'
                        ? customer.total_bytes
                        : sumFileBytes(customer.input_files) +
                          sumFileBytes(customer.output_files) +
                          sumFileBytes(customer.archive_files);
                    const matchedBytes =
                      sumFileBytes(filteredInputFiles) +
                      sumFileBytes(filteredOutputFiles) +
                      sumFileBytes(filteredArchiveFiles);
                    const inputSectionBytes = searchActive
                      ? sumFileBytes(filteredInputFiles)
                      : typeof customer.total_bytes_input === 'number'
                        ? customer.total_bytes_input
                        : sumFileBytes(customer.input_files);
                    const outputSectionBytes = searchActive
                      ? sumFileBytes(filteredOutputFiles)
                      : typeof customer.total_bytes_output === 'number'
                        ? customer.total_bytes_output
                        : sumFileBytes(customer.output_files);
                    const archiveSectionBytes = searchActive
                      ? sumFileBytes(filteredArchiveFiles)
                      : typeof customer.total_bytes_archive === 'number'
                        ? customer.total_bytes_archive
                        : sumFileBytes(customer.archive_files);

                    const inputKeys = getFileKeysForList('input', filteredInputFiles);
                    const outputKeys = getFileKeysForList('output', filteredOutputFiles);
                    const archiveKeys = getFileKeysForList('archive', filteredArchiveFiles);
                    const customerAllKeys = [...inputKeys, ...outputKeys, ...archiveKeys];
                    const customerAllSelected =
                      customerAllKeys.length > 0 && customerAllKeys.every((k) => selectedFileKeys.has(k));
                    const customerSomeSelected = customerAllKeys.some((k) => selectedFileKeys.has(k));
                    const showThumbnails = thumbnailViewUserIds.has(customer.user_id);

                    return (
                      <div key={customer.user_id} className="customer-card" data-user-id={customer.user_id}>
                        <div className="customer-header">
                          <label
                            className="customer-header-checkbox-wrap"
                            onClick={(e) => e.stopPropagation()}
                            title="Select all files for this user (visible lists)"
                          >
                            <input
                              type="checkbox"
                              className="admin-file-checkbox"
                              checked={customerAllSelected}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate =
                                    customerSomeSelected && !customerAllSelected && customerAllKeys.length > 0;
                                }
                              }}
                              onChange={() =>
                                toggleSelectAllForCustomerVisibleFiles(
                                  filteredInputFiles,
                                  filteredOutputFiles,
                                  filteredArchiveFiles
                                )
                              }
                              disabled={
                                batchDeletingFiles ||
                                filteredInputFiles.length +
                                  filteredOutputFiles.length +
                                  filteredArchiveFiles.length ===
                                  0
                              }
                              aria-label={`Select all files for ${getUserEmail(customer.user_id)}`}
                            />
                          </label>
                          <div
                            className="customer-header-main"
                            onClick={() =>
                              setSelectedCustomer(selectedCustomer === customer.user_id ? null : customer.user_id)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedCustomer(selectedCustomer === customer.user_id ? null : customer.user_id);
                              }
                            }}
                          >
                            <div className="customer-header-email-with-actions">
                              <h3>{getUserEmail(customer.user_id)}</h3>
                              <div
                                className="customer-header-firestore-actions"
                                onClick={(e) => e.stopPropagation()}
                                role="group"
                                aria-label="Open in Firestore"
                              >
                                <button
                                  type="button"
                                  className="btn-file-mgmt-firestore btn-file-mgmt-firestore--users"
                                  onClick={() => openFirestoreUsersFromFileManagement(customer.user_id)}
                                  title="Open Users collection filtered by this email"
                                >
                                  Users
                                </button>
                                <button
                                  type="button"
                                  className="btn-file-mgmt-firestore btn-file-mgmt-firestore--tasks"
                                  onClick={() => openFirestoreTasksFromFileManagement(customer.user_id)}
                                  title="Open Tasks filtered by this user email"
                                >
                                  Tasks
                                </button>
                                <button
                                  type="button"
                                  className={`btn-file-mgmt-firestore btn-file-mgmt-thumbnails${
                                    showThumbnails ? ' btn-file-mgmt-thumbnails--active' : ''
                                  }`}
                                  onClick={() => toggleCustomerThumbnailView(customer.user_id)}
                                  title={
                                    showThumbnails
                                      ? 'Switch to list view'
                                      : 'Switch to thumbnail view (all files)'
                                  }
                                >
                                  {showThumbnails ? 'List' : 'Thumbs'}
                                </button>
                              </div>
                            </div>
                            <span className="file-count">
                              {searchActive
                                ? `${filteredInputFiles.length + filteredOutputFiles.length + filteredArchiveFiles.length} matching files · ${formatFileSize(matchedBytes)}`
                                : `${customer.total_files} files · ${formatFileSize(customerTotalBytes)}`}
                            </span>
                            <span className="expand-icon">{selectedCustomer === customer.user_id ? '▼' : '▶'}</span>
                          </div>
                        </div>

                        {selectedCustomer === customer.user_id && (
                          <div className="customer-files">
                            {filteredInputFiles.length > 0 && (
                              <div className="file-section">
                                <h4 className="file-section-title">
                                  <label
                                    className="file-section-title-select"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Select all input files"
                                  >
                                    <input
                                      type="checkbox"
                                      className="admin-file-checkbox"
                                      checked={
                                        inputKeys.length > 0 && inputKeys.every((k) => selectedFileKeys.has(k))
                                      }
                                      ref={(el) => {
                                        if (!el) return;
                                        const some = inputKeys.some((k) => selectedFileKeys.has(k));
                                        const all =
                                          inputKeys.length > 0 && inputKeys.every((k) => selectedFileKeys.has(k));
                                        el.indeterminate = some && !all;
                                      }}
                                      onChange={() => toggleSelectAllInSection('input', filteredInputFiles)}
                                      disabled={batchDeletingFiles}
                                      aria-label="Select all input files"
                                    />
                                  </label>
                                  <span className="file-section-icon">📥</span>
                                  Input Files
                                  <span className="file-section-count">
                                    ({filteredInputFiles.length}
                                    {searchActive ? ` of ${customer.input_files.length}` : ''}) · {formatFileSize(inputSectionBytes)}
                                  </span>
                                </h4>
                                {showThumbnails ? (
                                  <CustomerFileThumbnailGrid
                                    files={sortedInputFiles}
                                    fileType="input"
                                    streamToken={streamTokenForAssets}
                                    userId={customer.user_id}
                                    selectedFileKeys={selectedFileKeys}
                                    makeFileSelectionKey={makeFileSelectionKey}
                                    toggleFileSelection={toggleFileSelection}
                                    batchDeletingFiles={batchDeletingFiles}
                                    openStreamPreview={openStreamPreview}
                                    handleDownload={handleDownload}
                                    formatFileSize={formatFileSize}
                                    fileDownloadProgress={fileDownloadProgress}
                                  />
                                ) : (
                                <div className="files-list">
                                  {sortedInputFiles.map((file, idx) => (
                                    <div key={`in-${file.path}-${idx}`} className="file-row">
                                      <label className="file-row-select" style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
                                        <input
                                          type="checkbox"
                                          className="admin-file-checkbox"
                                          checked={selectedFileKeys.has(makeFileSelectionKey('input', file.path))}
                                          onChange={() => toggleFileSelection('input', file.path)}
                                          disabled={batchDeletingFiles}
                                          aria-label={`Select ${file.name}`}
                                        />
                                      </label>
                                      <div className="file-info">
                                        <span className="file-name" title={file.name}>{file.name}</span>
                                        <span className="file-size">{formatFileSize(file.size)}</span>
                                      </div>
                                      <div className="file-actions">
                                        {isPreviewableVideoFile(file.name) && (
                                          <button
                                            onClick={() => handlePreview(file.path, 'input', file.name)}
                                            className="btn-action btn-preview"
                                            title="Preview"
                                          >
                                            <span>▶️</span>
                                          </button>
                                        )}
                                        <AdminFileDownloadButton
                                          filePath={file.path}
                                          fileType="input"
                                          fileName={file.name}
                                          onDownload={handleDownload}
                                          fileDownloadProgress={fileDownloadProgress}
                                        />
                                        <button onClick={() => handleDelete(file.path, 'input')} className="btn-action btn-delete" title="Delete">
                                          <span>🗑️</span>
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                )}
                              </div>
                            )}

                            {filteredOutputFiles.length > 0 && (
                              <div className="file-section">
                                <h4 className="file-section-title">
                                  <label
                                    className="file-section-title-select"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Select all output files"
                                  >
                                    <input
                                      type="checkbox"
                                      className="admin-file-checkbox"
                                      checked={
                                        outputKeys.length > 0 && outputKeys.every((k) => selectedFileKeys.has(k))
                                      }
                                      ref={(el) => {
                                        if (!el) return;
                                        const some = outputKeys.some((k) => selectedFileKeys.has(k));
                                        const all =
                                          outputKeys.length > 0 && outputKeys.every((k) => selectedFileKeys.has(k));
                                        el.indeterminate = some && !all;
                                      }}
                                      onChange={() => toggleSelectAllInSection('output', filteredOutputFiles)}
                                      disabled={batchDeletingFiles}
                                      aria-label="Select all output files"
                                    />
                                  </label>
                                  <span className="file-section-icon">📤</span>
                                  Output Files
                                  <span className="file-section-count">
                                    ({filteredOutputFiles.length}
                                    {searchActive ? ` of ${customer.output_files.length}` : ''}) · {formatFileSize(outputSectionBytes)}
                                  </span>
                                </h4>
                                {showThumbnails ? (
                                  <CustomerFileThumbnailGrid
                                    files={sortedOutputFiles}
                                    fileType="output"
                                    streamToken={streamTokenForAssets}
                                    userId={customer.user_id}
                                    selectedFileKeys={selectedFileKeys}
                                    makeFileSelectionKey={makeFileSelectionKey}
                                    toggleFileSelection={toggleFileSelection}
                                    batchDeletingFiles={batchDeletingFiles}
                                    openStreamPreview={openStreamPreview}
                                    handleDownload={handleDownload}
                                    formatFileSize={formatFileSize}
                                    fileDownloadProgress={fileDownloadProgress}
                                  />
                                ) : (
                                <div className="files-list">
                                  {sortedOutputFiles.map((file, idx) => (
                                    <div key={`out-${file.path}-${idx}`} className="file-row">
                                      <label className="file-row-select" style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
                                        <input
                                          type="checkbox"
                                          className="admin-file-checkbox"
                                          checked={selectedFileKeys.has(makeFileSelectionKey('output', file.path))}
                                          onChange={() => toggleFileSelection('output', file.path)}
                                          disabled={batchDeletingFiles}
                                          aria-label={`Select ${file.name}`}
                                        />
                                      </label>
                                      <div className="file-info">
                                        <span className="file-name" title={file.name}>{file.name}</span>
                                        <span className="file-size">{formatFileSize(file.size)}</span>
                                      </div>
                                      <div className="file-actions">
                                        {isPreviewableVideoFile(file.name) && (
                                          <button
                                            onClick={() => handlePreview(file.path, 'output', file.name)}
                                            className="btn-action btn-preview"
                                            title="Preview"
                                          >
                                            <span>▶️</span>
                                          </button>
                                        )}
                                        <AdminFileDownloadButton
                                          filePath={file.path}
                                          fileType="output"
                                          fileName={file.name}
                                          onDownload={handleDownload}
                                          fileDownloadProgress={fileDownloadProgress}
                                        />
                                        <button onClick={() => handleDelete(file.path, 'output')} className="btn-action btn-delete" title="Delete">
                                          <span>🗑️</span>
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                )}
                              </div>
                            )}
                            
                            {filteredArchiveFiles.length > 0 && (
                              <div className="file-section">
                                <h4 className="file-section-title">
                                  <label
                                    className="file-section-title-select"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Select all archive files"
                                  >
                                    <input
                                      type="checkbox"
                                      className="admin-file-checkbox"
                                      checked={
                                        archiveKeys.length > 0 &&
                                        archiveKeys.every((k) => selectedFileKeys.has(k))
                                      }
                                      ref={(el) => {
                                        if (!el) return;
                                        const some = archiveKeys.some((k) => selectedFileKeys.has(k));
                                        const all =
                                          archiveKeys.length > 0 &&
                                          archiveKeys.every((k) => selectedFileKeys.has(k));
                                        el.indeterminate = some && !all;
                                      }}
                                      onChange={() => toggleSelectAllInSection('archive', filteredArchiveFiles)}
                                      disabled={batchDeletingFiles}
                                      aria-label="Select all archive files"
                                    />
                                  </label>
                                  <span className="file-section-icon">📦</span>
                                  Archive Files
                                  <span className="file-section-count">
                                    ({filteredArchiveFiles.length}
                                    {searchActive ? ` of ${customer.archive_files?.length || 0}` : ''}) · {formatFileSize(archiveSectionBytes)}
                                  </span>
                                </h4>
                                {showThumbnails ? (
                                  <CustomerFileThumbnailGrid
                                    files={sortedArchiveFiles}
                                    fileType="archive"
                                    streamToken={streamTokenForAssets}
                                    userId={customer.user_id}
                                    selectedFileKeys={selectedFileKeys}
                                    makeFileSelectionKey={makeFileSelectionKey}
                                    toggleFileSelection={toggleFileSelection}
                                    batchDeletingFiles={batchDeletingFiles}
                                    openStreamPreview={openStreamPreview}
                                    handleDownload={handleDownload}
                                    formatFileSize={formatFileSize}
                                    fileDownloadProgress={fileDownloadProgress}
                                  />
                                ) : (
                                <div className="files-list">
                                  {sortedArchiveFiles.map((file, idx) => (
                                    <div key={`arc-${file.path}-${idx}`} className="file-row">
                                      <label className="file-row-select" style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
                                        <input
                                          type="checkbox"
                                          className="admin-file-checkbox"
                                          checked={selectedFileKeys.has(makeFileSelectionKey('archive', file.path))}
                                          onChange={() => toggleFileSelection('archive', file.path)}
                                          disabled={batchDeletingFiles}
                                          aria-label={`Select ${file.name}`}
                                        />
                                      </label>
                                      <div className="file-info">
                                        <span className="file-name" title={file.name}>{file.name}</span>
                                        <span className="file-size">{formatFileSize(file.size)}</span>
                                      </div>
                                      <div className="file-actions">
                                        {isPreviewableVideoFile(file.name) && (
                                          <button
                                            onClick={() => handlePreview(file.path, 'archive', file.name)}
                                            className="btn-action btn-preview"
                                            title="Preview"
                                          >
                                            <span>▶️</span>
                                          </button>
                                        )}
                                        <AdminFileDownloadButton
                                          filePath={file.path}
                                          fileType="archive"
                                          fileName={file.name}
                                          onDownload={handleDownload}
                                          fileDownloadProgress={fileDownloadProgress}
                                        />
                                        <button onClick={() => handleDelete(file.path, 'archive')} className="btn-action btn-delete" title="Delete">
                                          <span>🗑️</span>
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                )}
                              </div>
                            )}
                            
                            {searchActive && filteredInputFiles.length === 0 && filteredOutputFiles.length === 0 && filteredArchiveFiles.length === 0 && (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                No files for this user
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                  .filter(Boolean) // Remove null entries from filtered results
                }
              </div>
            )}

            {Object.keys(fileDownloadProgress).length > 0 && (
              <div className="admin-file-download-toast" aria-live="polite">
                {Object.entries(fileDownloadProgress).map(([key, prog]) => (
                  <div key={key} className="admin-file-download-toast-row">
                    <span className="admin-file-download-toast-name" title={prog.fileName}>
                      {prog.fileName}
                    </span>
                    <div className="admin-file-download-toast-bar-wrap">
                      <div
                        className={`admin-file-download-toast-bar${
                          prog.indeterminate ? ' admin-file-download-toast-bar--indeterminate' : ''
                        }`}
                        style={prog.indeterminate ? undefined : { width: `${prog.percent}%` }}
                      />
                    </div>
                    <span className="admin-file-download-toast-pct">
                      {prog.indeterminate ? '…' : `${prog.percent}%`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {previewVideo.isOpen && (
          <div className="modal-overlay" onClick={closePreviewModal}>
            <div className="modal-content video-preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="video-preview-header">
                <div>
                  <h3 style={{ margin: 0 }}>Preview: {previewVideo.name}</h3>
                  {previewVideo.userEmail && (
                    <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400, marginTop: '4px' }}>
                      {previewVideo.userEmail}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="video-preview-close-btn"
                  aria-label="Close preview"
                  title="Close"
                >
                  ×
                </button>
              </div>
              <div className="video-preview-body">
                {previewVideo.isLoading ? (
                  <div className="loading-state">Loading preview...</div>
                ) : previewVideo.error ? (
                  <div className="log-error">{previewVideo.error}</div>
                ) : previewVideo.previewKind === 'image' && previewVideo.url ? (
                  <img
                    className="video-preview-player"
                    src={previewVideo.url}
                    alt={previewVideo.name}
                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                  />
                ) : (
                  <video className="video-preview-player" src={previewVideo.url} controls autoPlay playsInline />
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'firestore' && (
          <div className="admin-content">
            <div className="section-header">
              <h2>Firestore Collections</h2>
              <div className="collection-tabs">
                <button
                  className={`collection-tab ${selectedCollection === 'users' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCollection('users');
                    setSelectedTaskIds(new Set());
                  }}
                >
                  Users
                </button>
                <button
                  className={`collection-tab ${selectedCollection === 'tasks' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedCollection('tasks');
                    setSelectedTaskIds(new Set());
                  }}
                >
                  Tasks
                </button>
              </div>
            </div>

            {loadingFirestore && selectedCollection === 'users' && !users ? (
              <div className="loading-state">Loading users...</div>
            ) : selectedCollection === 'tasks' ? (
              <div className="firestore-data">
                {navigationState.fromUserJourney && (
                  <div
                    style={{
                      marginBottom: '15px',
                      padding: '12px',
                      background: '#faf5ff',
                      borderRadius: '6px',
                      border: '1px solid #e9d5ff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ color: '#6b21a8', fontSize: '0.9rem' }}>
                      <strong>From User Journey:</strong>{' '}
                      {navigationState.fromUserJourney.date}
                      {navigationState.fromUserJourney.sessionId
                        ? ` · session ${String(navigationState.fromUserJourney.sessionId).substring(0, 24)}…`
                        : ''}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleBackToUserJourney('tasks')}
                      style={{
                        padding: '6px 12px',
                        background: '#7c3aed',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                      }}
                    >
                      ← Back to User Journey
                    </button>
                  </div>
                )}
                {navigationState.fromFirestoreUsers && !navigationState.fromUserJourney && (
                  <div
                    style={{
                      marginBottom: '15px',
                      padding: '12px',
                      background: '#eff6ff',
                      borderRadius: '6px',
                      border: '1px solid #93c5fd',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ color: '#1d4ed8', fontSize: '0.9rem' }}>
                      <strong>From Users:</strong> {navigationState.fromFirestoreUsers.email}
                    </div>
                    <button
                      type="button"
                      onClick={handleBackToFirestoreUsers}
                      style={{
                        padding: '6px 12px',
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                      }}
                    >
                      ← Back to Users
                    </button>
                  </div>
                )}
                {navigationState.fromFileManagement &&
                  navigationState.fromFileManagement.collection === 'tasks' &&
                  !navigationState.fromUserJourney &&
                  !navigationState.fromFirestoreUsers && (
                  <div
                    style={{
                      marginBottom: '15px',
                      padding: '12px',
                      background: '#ecfdf5',
                      borderRadius: '6px',
                      border: '1px solid #6ee7b7',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ color: '#047857', fontSize: '0.9rem' }}>
                      <strong>From File Management:</strong> {navigationState.fromFileManagement.email}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleBackToFileManagement('tasks')}
                      style={{
                        padding: '6px 12px',
                        background: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                      }}
                    >
                      ← Back to File Management
                    </button>
                  </div>
                )}
                <div className="filter-section filter-section--sticky">
                  <div className="filter-header">
                    <h3>Filters</h3>
                    <button 
                      onClick={() => {
                        clearFilters('tasks');
                        setNavigationState((prev) => ({
                          ...prev,
                          fromFileManagement: null,
                          fromFirestoreUsers: null,
                        }));
                      }} 
                      className="btn-clear-filters"
                      title="Clear all filters"
                    >
                      Clear Filters
                    </button>
                  </div>
                  <div className="filter-controls">
                    <div className="filter-group">
                      <label htmlFor="filter-status">Status:</label>
                      <select
                        id="filter-status"
                        value={filters.tasks.status}
                        onChange={(e) => updateFilter('tasks', 'status', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Statuses</option>
                        {getUniqueStatuses().map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-device-type">Device type:</label>
                      <select
                        id="filter-device-type"
                        value={filters.tasks.deviceType}
                        onChange={(e) => updateFilter('tasks', 'deviceType', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All types</option>
                        {availableTaskDeviceTypes.map(({ value, count }) => (
                          <option key={value} value={value}>
                            {`${value} (${count})`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-device-os">Device OS:</label>
                      <select
                        id="filter-device-os"
                        value={filters.tasks.deviceOs}
                        onChange={(e) => updateFilter('tasks', 'deviceOs', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All OS</option>
                        <optgroup label="OS families">
                          <option value={TASK_DEVICE_OS_FAMILY.android}>
                            {`Android (${taskDeviceOsFamilyCounts.android})`}
                          </option>
                          <option value={TASK_DEVICE_OS_FAMILY.ios}>
                            {`iOS (${taskDeviceOsFamilyCounts.ios})`}
                          </option>
                          <option value={TASK_DEVICE_OS_FAMILY.windows}>
                            {`Windows (${taskDeviceOsFamilyCounts.windows})`}
                          </option>
                          <option value={TASK_DEVICE_OS_FAMILY.mac}>
                            {`Mac (${taskDeviceOsFamilyCounts.mac})`}
                          </option>
                        </optgroup>
                        {availableTaskDeviceOs.length > 0 && (
                          <optgroup label="Specific versions">
                            {availableTaskDeviceOs.map(({ value, count }) => (
                              <option key={value} value={value}>
                                {`${value} (${count})`}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-user-email">User Email:</label>
                      <input
                        id="filter-user-email"
                        type="text"
                        placeholder="Search by user email..."
                        value={filters.tasks.userEmail}
                        onChange={(e) => updateFilter('tasks', 'userEmail', e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    <div className="filter-group filter-group-checkbox">
                      <label
                        htmlFor="filter-tasks-lambda-only"
                        className="filter-checkbox-label"
                      >
                        <input
                          id="filter-tasks-lambda-only"
                          type="checkbox"
                          checked={filters.tasks.lambdaOnly}
                          onChange={(e) =>
                            updateFilter('tasks', 'lambdaOnly', e.target.checked)
                          }
                        />
                        <span>AWS Lambda</span>
                      </label>
                    </div>
                    <div className="filter-group filter-group-checkbox">
                      <label
                        htmlFor="filter-tasks-returning-users-only"
                        className="filter-checkbox-label"
                      >
                        <input
                          id="filter-tasks-returning-users-only"
                          type="checkbox"
                          checked={filters.tasks.returningUsersOnly}
                          onChange={(e) =>
                            updateFilter('tasks', 'returningUsersOnly', e.target.checked)
                          }
                        />
                        <span>Returning users</span>
                      </label>
                    </div>
                    <div className="filter-group filter-group-search">
                      <label htmlFor="filter-search">Search:</label>
                      <input
                        id="filter-search"
                        type="text"
                        placeholder="Search task ID, email, status..."
                        value={filters.tasks.searchText}
                        onChange={(e) => updateFilter('tasks', 'searchText', e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-date-range">Created Date Range:</label>
                      <div className="filter-date-with-inline-today">
                        <DatePicker
                          id="filter-date-range"
                          selected={filters.tasks.dateRange[0]}
                          onChange={(dates) => {
                            const [start, end] = dates || [null, null];
                            setFilters(prev => ({
                              ...prev,
                              tasks: {
                                ...prev.tasks,
                                dateRange: [start, end]
                              }
                            }));
                          }}
                          startDate={filters.tasks.dateRange[0]}
                          endDate={filters.tasks.dateRange[1]}
                          selectsRange
                          isClearable
                          placeholderText="Select date range"
                          className="filter-input"
                          dateFormat="yyyy-MM-dd"
                        />
                        <button
                          type="button"
                          className="filter-date-with-inline-today__btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            applyDatePresetToday('tasks', 'dateRange');
                          }}
                          title="Filter to tasks from today (local date)"
                        >
                          Today
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {loadingFirestore && !tasks && (
                  <div className="loading-state">Loading tasks...</div>
                )}
                {!tasks && !loadingFirestore && (
                  <div className="empty-state" style={{ marginBottom: 12 }}>
                    <p style={{ margin: '0 0 8px 0' }}>Tasks could not be loaded.</p>
                    <button type="button" className="btn-refresh" onClick={() => fetchTasksRecent({ force: true })}>
                      Load tasks
                    </button>
                  </div>
                )}
                {tasks && (
                <>
                <div className="data-summary">
                  <div className="summary-info">
                    <p className="summary-metric">
                      <span className="summary-metric-label">Total Tasks:</span>{' '}
                      <span className="summary-metric-value summary-metric-value--tasks">
                        {tasks.total_tasks || 0}
                      </span>
                    </p>
                    <p className="summary-metric">
                      <span className="summary-metric-label">Filtered:</span>{' '}
                      <span className="summary-metric-value summary-metric-value--filtered">
                        {getSortedTasks().length}
                      </span>
                    </p>
                    <p className="summary-metric">
                      <span className="summary-metric-label">Total Users:</span>{' '}
                      <span className="summary-metric-value summary-metric-value--users">
                        {tasks.total_users || 0}
                      </span>
                    </p>
                    {tasks.partial && tasksHasMore && (
                      <p className="summary-metric" style={{ fontSize: '12px', color: '#6b7280' }}>
                        Showing latest 25 · load more for full list
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {tasksHasMore && (
                      <button
                        type="button"
                        className="btn-refresh"
                        onClick={loadTasksRemaining}
                        disabled={loadingTasksRemaining || loadingFirestore}
                      >
                        {loadingTasksRemaining ? 'Loading…' : 'Load all remaining'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-refresh"
                      onClick={refreshFirestoreTasks}
                      disabled={loadingFirestore || loadingTasksRemaining}
                    >
                      Refresh
                    </button>
                    <button 
                      onClick={() => toggleSortOrder('tasks')} 
                      className="btn-sort"
                      title={sortLatestFirst.tasks ? 'Show oldest created first' : 'Show latest created first'}
                    >
                      {sortLatestFirst.tasks ? '⬇️ Latest Created' : '⬆️ Oldest Created'}
                    </button>
                  </div>
                </div>
                {(() => {
                  const filteredTasks = getSortedTasks();
                  const allVisibleSelected =
                    filteredTasks.length > 0 && filteredTasks.every((t) => selectedTaskIds.has(t.task_id));
                  const someVisibleSelected = filteredTasks.some((t) => selectedTaskIds.has(t.task_id));
                  return (
                    <div
                      className="task-select-visible-bar"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '8px 15px',
                        marginBottom: '4px',
                        background: 'rgba(0,0,0,0.04)',
                        borderRadius: '6px',
                        border: '1px solid rgba(0,0,0,0.08)',
                      }}
                    >
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: batchDeletingTasks || filteredTasks.length === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          margin: 0,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <input
                          type="checkbox"
                          className="admin-task-checkbox"
                          ref={(el) => {
                            if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                          }}
                          checked={allVisibleSelected}
                          disabled={batchDeletingTasks || filteredTasks.length === 0}
                          onChange={() => {
                            if (allVisibleSelected) {
                              setSelectedTaskIds((prev) => {
                                const next = new Set(prev);
                                filteredTasks.forEach((t) => next.delete(t.task_id));
                                return next;
                              });
                            } else {
                              setSelectedTaskIds((prev) => {
                                const next = new Set(prev);
                                filteredTasks.forEach((t) => next.add(t.task_id));
                                return next;
                              });
                            }
                          }}
                          aria-label="Select all visible tasks"
                        />
                        <span style={{ width: '22px', flexShrink: 0 }} aria-hidden />
                        <span>Select visible ({filteredTasks.length})</span>
                      </label>
                      <div className="header-right" style={{ flexShrink: 0, gap: '10px' }}>
                        <button
                          type="button"
                          className="btn-action btn-delete"
                          disabled={batchDeletingTasks || selectedTaskIds.size === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSelectedTasks();
                          }}
                          title="Delete selected tasks"
                          style={{
                            opacity: batchDeletingTasks || selectedTaskIds.size === 0 ? 0.45 : 1,
                            cursor: batchDeletingTasks || selectedTaskIds.size === 0 ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {batchDeletingTasks
                            ? 'Deleting…'
                            : `🗑️ Delete (${selectedTaskIds.size})`}
                        </button>
                      </div>
                    </div>
                  );
                })()}
                <div className="tasks-list">
                  {getSortedTasks().map((task) => {
                    const isCollapsed = collapsedItems.tasks.has(task.task_id);
                    const taskSuccessHeaderSummary = formatTaskSuccessHeaderSummary(task);
                    return (
                      <div key={task.task_id} className="data-item" data-task-id={task.task_id}>
                        <div 
                          className="data-header clickable" 
                          onClick={() => toggleCollapse('tasks', task.task_id)}
                        >
                          <div className="header-left">
                            <label
                              onClick={(e) => e.stopPropagation()}
                              style={{ display: 'flex', alignItems: 'center', marginRight: '6px', cursor: batchDeletingTasks ? 'not-allowed' : 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                className="admin-task-checkbox"
                                checked={selectedTaskIds.has(task.task_id)}
                                disabled={batchDeletingTasks}
                                onChange={() => toggleTaskSelection(task.task_id)}
                                aria-label={`Select task ${task.task_id}`}
                              />
                            </label>
                            <span className="expand-icon">{isCollapsed ? '▶' : '▼'}</span>
                            {task.youtube_link && (
                              <span className="task-source-icon" title="YouTube video">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
                                </svg>
                              </span>
                            )}
                            <span className="task-id">{getUserEmail(task.userId)}</span>
                            {isReturningUser(task.userId) && (
                              <span
                                className="task-returning-user-badge"
                                title="User account was created on a different calendar day than their last activity"
                                style={{
                                  marginLeft: '8px',
                                  padding: '2px 8px',
                                  background: '#7c3aed',
                                  color: '#fff',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                Returning user
                              </span>
                            )}
                            {hasTaskLambdaMeta(task) && (() => {
                              const headerLambdaTotals = aggregateLambdaRemotionTotals(task.remotion);
                              const headerCost = headerLambdaTotals.cost;
                              const headerTitle =
                                headerCost != null
                                  ? `Lambda · Est. total $${formatLambdaCostUSD(headerCost)} (expand for details)`
                                  : 'Lambda render metadata (expand for details)';
                              return (
                                <span className="task-lambda-header-group" title={headerTitle}>
                                  <span className="task-source-icon task-lambda-header-icon">
                                    <span className="task-lambda-header-badge" aria-hidden="true">
                                      λ
                                    </span>
                                  </span>
                                  {headerCost != null && (
                                    <span className="task-lambda-header-cost">
                                      ${formatLambdaCostUSD(headerCost)}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                            <span className="task-date">{formatDate(task.video_uploaded_at || task.created_at)}</span>
                            {taskSuccessHeaderSummary && (
                              <span
                                className="task-success-header-meta"
                                title="Processing time · videos generated"
                                style={{ fontSize: '13.5px', opacity: 0.9, marginLeft: '10px', color: 'var(--admin-text-muted, #64748b)' }}
                              >
                                {taskSuccessHeaderSummary}
                              </span>
                            )}
                          </div>
                          <div className="header-right">
                            {task.error_code && (
                              <span className="task-error-code" title={`Error code: ${task.error_code}`}>
                                {task.error_code}
                              </span>
                            )}
                            <span className={`status-badge status-${(task.lifecycle_state || task.status)?.toLowerCase()}`}>
                              {task.lifecycle_state || task.status}
                            </span>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTask(task.task_id);
                              }}
                              className="btn-action btn-delete"
                              title="Delete Task"
                            >
                              <span>🗑️</span>
                            </button>
                          </div>
                        </div>
                        {!isCollapsed && (
                          <div className="data-details" data-task-details={task.task_id}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '8px',
                                marginBottom: '10px',
                              }}
                            >
                              <strong style={{ color: 'var(--admin-text, #0f172a)', fontSize: '14px' }}>
                                Task details
                              </strong>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: '8px',
                                }}
                              >
                                {task.userId && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setNavigationState({
                                        fromTask: {
                                          taskId: task.task_id,
                                          userId: task.userId,
                                          collection: selectedCollection,
                                        },
                                        previousCollection: selectedCollection,
                                        previousTab: 'firestore',
                                        fromUserJourney: null,
                                        fromFileManagement: null,
                                        fromFirestoreUsers: null,
                                      });
                                      setSelectedCollection('users');
                                      updateFilter('users', 'email', getUserEmail(task.userId));
                                      setActiveTab('firestore');
                                      setTimeout(() => {
                                        setCollapsedItems((prev) => {
                                          const newSet = new Set(prev.users);
                                          newSet.delete(task.userId);
                                          return { ...prev, users: newSet };
                                        });
                                        setTimeout(() => {
                                          const element = document.querySelector(`[data-user-id="${task.userId}"]`);
                                          if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          }
                                        }, 50);
                                      }, 100);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                    }}
                                    title="Go to user in Users collection"
                                  >
                                    👤 View User
                                  </button>
                                )}
                                {task.input &&
                                  typeof task.input === 'object' &&
                                  (task.input.file_name || task.input.filename) && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setNavigationState({
                                          fromTask: {
                                            taskId: task.task_id,
                                            userId: task.userId,
                                            collection: selectedCollection,
                                          },
                                          previousCollection: selectedCollection,
                                          previousTab: 'firestore',
                                          fromUserJourney: null,
                                          fromFileManagement: null,
                                          fromFirestoreUsers: null,
                                        });
                                        setActiveTab('files');
                                        setSelectedCustomer(task.userId);
                                        setFilenameSearch(task.input.file_name || task.input.filename);
                                        setTimeout(() => {
                                          const element = document.querySelector(`[data-user-id="${task.userId}"]`);
                                          if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          }
                                        }, 100);
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        background: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                        fontWeight: '500',
                                      }}
                                      title={`Search for input file: ${task.input.file_name || task.input.filename}`}
                                    >
                                      📥 Find Input File
                                    </button>
                                  )}
                                <button
                                  type="button"
                                  className="btn-refresh"
                                  style={{ fontSize: '13px', padding: '6px 12px', flexShrink: 0 }}
                                  title="Fetch latest data for this task from Firestore"
                                  disabled={taskSingleRefreshIds.has(task.task_id)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    refreshSingleFirestoreTask(task.task_id);
                                  }}
                                >
                                  {taskSingleRefreshIds.has(task.task_id) ? 'Loading…' : '🔄 Refresh'}
                                </button>
                              </div>
                            </div>
                            <p><strong>Task ID:</strong> {task.task_id}</p>
                            <p><strong>Lifecycle State:</strong> {task.lifecycle_state || task.status || 'UNKNOWN'}</p>
                            {task.error_code && (
                              <p><strong>Error Code:</strong> <span className="task-error-code-inline">{task.error_code}</span></p>
                            )}
                            {task.output?.message && (task.lifecycle_state === 'FAILED' || task.status === 'FAILED') && (
                              <p><strong>Error Message:</strong> {task.output.message}</p>
                            )}
                            {task.video_uploaded_at && <p><strong>Uploaded:</strong> {formatDate(task.video_uploaded_at)}</p>}
                            {task.video_processing_started && <p><strong>Processing Started:</strong> {formatDate(task.video_processing_started)}</p>}
                            {task.video_processing_ended && <p><strong>Processing Ended:</strong> {formatDate(task.video_processing_ended)}</p>}
                            {task.completed_percent !== undefined && <p><strong>Progress:</strong> {task.completed_percent}%</p>}
                            {task.metadata && typeof task.metadata === 'object' && (
                              <>
                                {(task.metadata.device_type != null && String(task.metadata.device_type).trim() !== '') && (
                                  <p><strong>Device type:</strong> {String(task.metadata.device_type)}</p>
                                )}
                                {(task.metadata.referrer_url != null && String(task.metadata.referrer_url).trim() !== '') && (
                                  <p>
                                    <strong>Referrer URL:</strong>{' '}
                                    {/^https?:\/\//i.test(String(task.metadata.referrer_url)) ? (
                                      <a
                                        href={String(task.metadata.referrer_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#3b82f6', textDecoration: 'underline', wordBreak: 'break-all' }}
                                      >
                                        {task.metadata.referrer_url}
                                      </a>
                                    ) : (
                                      <span style={{ wordBreak: 'break-all' }}>{task.metadata.referrer_url}</span>
                                    )}
                                  </p>
                                )}
                                {(task.metadata.device_os != null && String(task.metadata.device_os).trim() !== '') && (
                                  <p><strong>Device OS:</strong> {String(task.metadata.device_os)}</p>
                                )}
                              </>
                            )}
                            {task.youtube_link && (
                              <div style={{ marginTop: '10px', marginBottom: '10px', padding: '10px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                                <p style={{ margin: '0 0 5px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
                                  </svg>
                                  <strong>YouTube Video:</strong>
                                </p>
                                <p style={{ margin: '0 0 5px 0', wordBreak: 'break-all' }}>
                                  <a 
                                    href={task.youtube_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ 
                                      color: '#3b82f6', 
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    {task.youtube_link}
                                  </a>
                                </p>
                                {task.metadata && task.metadata.title && (
                                  <p style={{ margin: '0', fontSize: '0.9rem', color: '#92400e' }}>
                                    <strong>Title:</strong> {task.metadata.title}
                                  </p>
                                )}
                                {task.input && typeof task.input === 'object' && task.input.file_name && !task.metadata?.title && (
                                  <p style={{ margin: '0', fontSize: '0.9rem', color: '#92400e' }}>
                                    <strong>Filename:</strong> {task.input.file_name.replace(/\.mp4$/, '')}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Quick link: Find Output (View User + Find Input are in the header row) */}
                            {task.output &&
                              typeof task.output === 'object' &&
                              (task.output.file_name || task.output.filename || task.output.output_file_name) && (
                            <div style={{ marginTop: '15px', marginBottom: '15px', padding: '12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                              <strong style={{ display: 'block', marginBottom: '8px', color: '#0369a1' }}>Quick Links:</strong>
                              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Store navigation state
                                      setNavigationState({
                                        fromTask: {
                                          taskId: task.task_id,
                                          userId: task.userId,
                                          collection: selectedCollection
                                        },
                                        previousCollection: selectedCollection,
                                        previousTab: 'firestore',
                                        fromUserJourney: null,
                                        fromFileManagement: null,
                                        fromFirestoreUsers: null,
                                      });
                                      setActiveTab('files');
                                      setSelectedCustomer(task.userId);
                                      setFilenameSearch(task.output.file_name || task.output.filename || task.output.output_file_name);
                                      setTimeout(() => {
                                        const element = document.querySelector(`[data-user-id="${task.userId}"]`);
                                        if (element) {
                                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        }
                                      }, 100);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#10b981',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500'
                                    }}
                                    title={`Search for output file: ${task.output.file_name || task.output.filename || task.output.output_file_name}`}
                                  >
                                    📤 Find Output File
                                  </button>
                              </div>
                            </div>
                            )}

                            {task.input && (
                              <div className="nested-data">
                                <strong>Input:</strong>
                                <pre>{JSON.stringify(task.input, null, 2)}</pre>
                              </div>
                            )}
                            {task.output && (
                              <div className="nested-data">
                                <strong>Output:</strong>
                                <pre>{JSON.stringify(task.output, null, 2)}</pre>
                              </div>
                            )}
                            {hasTaskLambdaMeta(task) && (() => {
                              const lambdaTotals = aggregateLambdaRemotionTotals(task.remotion);
                              const hasLambdaTotals =
                                lambdaTotals.lambdas != null ||
                                lambdaTotals.seconds != null ||
                                lambdaTotals.cost != null;
                              const showLtot = lambdaTotals.lambdas != null;
                              const showStot = lambdaTotals.seconds != null;
                              const showCtot = lambdaTotals.cost != null;
                              return (
                              <div className="nested-data task-lambda-section" style={{ marginTop: '10px' }}>
                                {hasLambdaTotals && (
                                  <div className="task-lambda-totals-row">
                                    <strong>Totals:</strong>{' '}
                                    {showLtot && (
                                      <span title="Total Lambda invocations (sum of per-clip counts)">
                                        <strong>{lambdaTotals.lambdas}</strong> λ used
                                      </span>
                                    )}
                                    {showLtot && showStot && (
                                      <span style={{ color: '#94a3b8', margin: '0 8px' }}>·</span>
                                    )}
                                    {showStot && (
                                      <span title="Total render time (sum of per-clip seconds)">
                                        <strong>{lambdaTotals.seconds.toFixed(2)}</strong> sec
                                      </span>
                                    )}
                                    {(showLtot || showStot) && showCtot && (
                                      <span style={{ color: '#94a3b8', margin: '0 8px' }}>·</span>
                                    )}
                                    {showCtot && (
                                      <span title="Total estimated cost (sum of per-clip estimates)">
                                        Est. <strong>${formatLambdaCostUSD(lambdaTotals.cost)}</strong>
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div
                                  className="task-lambda-inline"
                                  style={{ marginTop: hasLambdaTotals ? '8px' : 0 }}
                                >
                                  <strong>Lambda:</strong>
                                {Object.entries(task.remotion)
                                  .sort(([a], [b]) => Number(a) - Number(b))
                                  .map(([clipIdx, clip], ri) => {
                                    const isSuccess = clip.status === 'success';
                                    const isFailed = clip.status === 'failed';
                                    const fc = clip.failed_chunk_urls && clip.failed_chunk_urls.length > 0
                                      ? clip.failed_chunk_urls
                                      : [];
                                    return (
                                      <span
                                        key={clipIdx}
                                        style={{
                                          display: 'inline',
                                          marginLeft: ri === 0 ? '6px' : 0,
                                        }}
                                      >
                                        {ri > 0 ? <span style={{ color: '#94a3b8', margin: '0 8px' }}>|</span> : null}
                                        <span style={{ fontWeight: 600 }}>Clip {clipIdx}</span>
                                        {' '}
                                        <span
                                          style={{
                                            padding: '0 6px',
                                            borderRadius: '4px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            background: isSuccess ? '#dcfce7' : isFailed ? '#fee2e2' : '#f1f5f9',
                                            color: isSuccess ? '#166534' : isFailed ? '#991b1b' : '#475569',
                                          }}
                                        >
                                          {clip.status || 'unknown'}
                                        </span>
                                        {clip.render_id ? (
                                          <span style={{ color: '#64748b', fontFamily: 'monospace', marginLeft: '6px' }}>
                                            #{clip.render_id}
                                          </span>
                                        ) : null}
                                        {clip.summary ? (
                                          <span style={{ color: '#374151', marginLeft: '6px' }}>{clip.summary}</span>
                                        ) : null}
                                        {clip.progress_json_url ? (
                                          <>
                                            {' '}
                                            <a
                                              href={clip.progress_json_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              style={{ color: '#3b82f6', textDecoration: 'underline' }}
                                            >
                                              progress.json
                                            </a>
                                          </>
                                        ) : null}
                                        {fc.length > 0 ? (
                                          <span style={{ marginLeft: '6px' }}>
                                            <span style={{ color: '#991b1b', fontWeight: 600 }}>failed:</span>
                                            {fc.map((url, ui) => (
                                              <span key={ui}>
                                                {' '}
                                                <a
                                                  href={url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  onClick={(e) => e.stopPropagation()}
                                                  style={{ color: '#ef4444', textDecoration: 'underline', fontSize: '0.78rem' }}
                                                >
                                                  [{ui + 1}]
                                                </a>
                                              </span>
                                            ))}
                                          </span>
                                        ) : null}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                </>
                )}
              </div>
            ) : selectedCollection === 'users' && users ? (
              <div className="firestore-data">
                {/* Back button if navigated from task */}
                {navigationState.fromTask && (
                  <div style={{ marginBottom: '15px', padding: '12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ color: '#0369a1', fontSize: '0.9rem' }}>
                      <strong>Viewing user from task:</strong> {navigationState.fromTask.taskId}
                    </div>
                    <button
                      onClick={() => {
                        const taskId = navigationState.fromTask?.taskId;
                        const previousCollection = navigationState.fromTask?.collection || 'tasks';
                        // Restore previous state
                        setSelectedCollection(previousCollection);
                        clearFilters('users');
                        setNavigationState({
                          fromTask: null,
                          previousCollection: null,
                          previousTab: null,
                          fromUserJourney: null,
                          fromFileManagement: null,
                          fromFirestoreUsers: null,
                        });
                        // Expand and scroll back to task
                        if (taskId) {
                          setTimeout(() => {
                            // Expand the task
                            setCollapsedItems(prev => {
                              const newSet = new Set(prev.tasks);
                              newSet.delete(taskId);
                              return {
                                ...prev,
                                tasks: newSet
                              };
                            });
                            // Scroll back to task
                            setTimeout(() => {
                              const element = document.querySelector(`[data-task-id="${taskId}"]`);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 50);
                          }, 100);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                    >
                      ← Back to Task
                    </button>
                  </div>
                )}
                {navigationState.fromFirestoreUsers &&
                  !navigationState.fromTask &&
                  !navigationState.fromUserJourney && (
                  <div
                    style={{
                      marginBottom: '15px',
                      padding: '12px',
                      background: '#eff6ff',
                      borderRadius: '6px',
                      border: '1px solid #93c5fd',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ color: '#1d4ed8', fontSize: '0.9rem' }}>
                      <strong>Resume for user:</strong> {navigationState.fromFirestoreUsers.email}
                      {navigationState.fromFirestoreUsers.source === 'tasks' ? ' · Tasks' : ' · Files'}
                    </div>
                    <button
                      type="button"
                      onClick={handleResumeFromFirestoreUsersDrill}
                      style={{
                        padding: '6px 12px',
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                      }}
                    >
                      {navigationState.fromFirestoreUsers.source === 'tasks'
                        ? '← Back to Tasks'
                        : '← Back to File Management'}
                    </button>
                  </div>
                )}
                {navigationState.fromFileManagement &&
                  navigationState.fromFileManagement.collection === 'users' &&
                  !navigationState.fromTask &&
                  !navigationState.fromUserJourney &&
                  !navigationState.fromFirestoreUsers && (
                  <div
                    style={{
                      marginBottom: '15px',
                      padding: '12px',
                      background: '#ecfdf5',
                      borderRadius: '6px',
                      border: '1px solid #6ee7b7',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ color: '#047857', fontSize: '0.9rem' }}>
                      <strong>From File Management:</strong> {navigationState.fromFileManagement.email}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleBackToFileManagement('users')}
                      style={{
                        padding: '6px 12px',
                        background: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                      }}
                    >
                      ← Back to File Management
                    </button>
                  </div>
                )}
                {navigationState.fromUserJourney && !navigationState.fromTask && (
                  <div style={{ marginBottom: '15px', padding: '12px', background: '#faf5ff', borderRadius: '6px', border: '1px solid #e9d5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ color: '#6b21a8', fontSize: '0.9rem' }}>
                      <strong>From User Journey:</strong>{' '}
                      {navigationState.fromUserJourney.date}
                      {navigationState.fromUserJourney.sessionId
                        ? ` · session ${String(navigationState.fromUserJourney.sessionId).substring(0, 24)}…`
                        : ''}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleBackToUserJourney('users')}
                      style={{
                        padding: '6px 12px',
                        background: '#7c3aed',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                      }}
                    >
                      ← Back to User Journey
                    </button>
                  </div>
                )}
                <div className="filter-section">
                  <div className="filter-header">
                    <h3>Filters</h3>
                    <button 
                      onClick={() => {
                        clearFilters('users');
                        setNavigationState((prev) => ({
                          ...prev,
                          fromTask: null,
                          previousCollection: null,
                          previousTab: null,
                          fromFileManagement: null,
                          fromFirestoreUsers: null,
                        }));
                      }} 
                      className="btn-clear-filters"
                      title="Clear all filters"
                    >
                      Clear Filters
                    </button>
                  </div>
                  <div className="filter-controls">
                    <div className="filter-group">
                      <label htmlFor="filter-email">Email:</label>
                      <input
                        id="filter-email"
                        type="text"
                        placeholder="Search by email..."
                        value={filters.users.email}
                        onChange={(e) => updateFilter('users', 'email', e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-name">Name:</label>
                      <input
                        id="filter-name"
                        type="text"
                        placeholder="Search by name..."
                        value={filters.users.name}
                        onChange={(e) => updateFilter('users', 'name', e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-phone">Phone:</label>
                      <input
                        id="filter-phone"
                        type="text"
                        placeholder="Type null to find entries with no phone number"
                        value={filters.users.phone}
                        onChange={(e) => updateFilter('users', 'phone', e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-country">Country:</label>
                      <select
                        id="filter-country"
                        value={filters.users.country}
                        onChange={(e) => updateFilter('users', 'country', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All countries</option>
                        {availableUserCountryCodes.map(({ code, count }) => (
                          <option key={code} value={code}>
                            {`${code} (${count})`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-has-videos">Has Videos:</label>
                      <select
                        id="filter-has-videos"
                        value={filters.users.hasVideos === null ? '' : filters.users.hasVideos ? 'true' : 'false'}
                        onChange={(e) => updateFilter('users', 'hasVideos', e.target.value === '' ? null : e.target.value === 'true')}
                        className="filter-select"
                      >
                        <option value="">All Users</option>
                        <option value="true">Has Videos</option>
                        <option value="false">No Videos</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-has-phone">Has Phone:</label>
                      <select
                        id="filter-has-phone"
                        value={filters.users.hasPhone}
                        onChange={(e) => updateFilter('users', 'hasPhone', e.target.value)}
                        className="filter-select"
                      >
                        <option value="none">None</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-premium-filter">Premium:</label>
                      <select
                        id="filter-premium-filter"
                        value={
                          filters.users.premiumFilter === 'subscription'
                            ? 'active'
                            : filters.users.premiumFilter || 'all'
                        }
                        onChange={(e) => updateFilter('users', 'premiumFilter', e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">All users</option>
                        <option value="premium">Premium + Coupon</option>
                        <option value="any-subscription">Any Subscription</option>
                        <option value="coupon">Coupon</option>
                        <option value="active">active</option>
                        <option value="cancel-at-period-end">Active until cancelled</option>
                        <option value="halted">halted</option>
                        <option value="pending">pending</option>
                        <option value="paused">paused</option>
                        <option value="created">created</option>
                        <option value="cancelled">cancelled</option>
                        <option value="expired">expired</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-paid-count">Paid Count:</label>
                      <select
                        id="filter-paid-count"
                        value={filters.users.paidCount}
                        onChange={(e) => updateFilter('users', 'paidCount', e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">All</option>
                        <option value="1">1</option>
                        <option value="gt1">&gt;1</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="filter-streak">Streak:</label>
                      <select
                        id="filter-streak"
                        value={filters.users.streakFilter}
                        onChange={(e) => updateFilter('users', 'streakFilter', e.target.value)}
                        className="filter-select"
                      >
                        <option value="all">All</option>
                        <option value="1">1</option>
                        <option value="gt1">&gt;1</option>
                      </select>
                    </div>
                    <div className="filter-group filter-group-date-range">
                      <label htmlFor="filter-created-range">Created Date Range:</label>
                      <div className="filter-date-with-inline-today">
                        <DatePicker
                          id="filter-created-range"
                          selected={filters.users.createdRange[0]}
                          onChange={(dates) => {
                            const [start, end] = dates || [null, null];
                            setFilters(prev => ({
                              ...prev,
                              users: {
                                ...prev.users,
                                createdRange: [start, end]
                              }
                            }));
                          }}
                          startDate={filters.users.createdRange[0]}
                          endDate={filters.users.createdRange[1]}
                          selectsRange
                          isClearable
                          placeholderText="Select created date range"
                          className="filter-input"
                          dateFormat="yyyy-MM-dd"
                        />
                        <button
                          type="button"
                          className="filter-date-with-inline-today__btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            applyDatePresetToday('users', 'createdRange');
                          }}
                          title="Filter users created today (local date)"
                        >
                          Today
                        </button>
                      </div>
                    </div>
                    <div className="filter-group filter-group-date-range">
                      <label htmlFor="filter-last-active-range">Last Active Date Range:</label>
                      <div className="filter-date-with-inline-today">
                        <DatePicker
                          id="filter-last-active-range"
                          selected={filters.users.lastActiveRange[0]}
                          onChange={(dates) => {
                            const [start, end] = dates || [null, null];
                            setFilters(prev => ({
                              ...prev,
                              users: {
                                ...prev.users,
                                lastActiveRange: [start, end]
                              }
                            }));
                          }}
                          startDate={filters.users.lastActiveRange[0]}
                          endDate={filters.users.lastActiveRange[1]}
                          selectsRange
                          isClearable
                          placeholderText="Select last active date range"
                          className="filter-input"
                          dateFormat="yyyy-MM-dd"
                        />
                        <button
                          type="button"
                          className="filter-date-with-inline-today__btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            applyDatePresetToday('users', 'lastActiveRange');
                          }}
                          title="Filter users last active today (local date)"
                        >
                          Today
                        </button>
                      </div>
                    </div>
                    <div className="filter-group filter-group-checkbox">
                      <label
                        htmlFor="filter-users-reminders-only"
                        className="filter-checkbox-label"
                      >
                        <input
                          id="filter-users-reminders-only"
                          type="checkbox"
                          checked={filters.users.remindersOnly}
                          onChange={(e) =>
                            updateFilter('users', 'remindersOnly', e.target.checked)
                          }
                        />
                        <span>🔔 Reminders on</span>
                      </label>
                    </div>
                    <div className="filter-group filter-group-checkbox">
                      <label
                        htmlFor="filter-users-social-handle-only"
                        className="filter-checkbox-label"
                      >
                        <input
                          id="filter-users-social-handle-only"
                          type="checkbox"
                          checked={filters.users.socialHandleOnly}
                          onChange={(e) =>
                            updateFilter('users', 'socialHandleOnly', e.target.checked)
                          }
                        />
                        <span>📷 Social handle</span>
                      </label>
                    </div>
                    <div className="filter-group filter-group-checkbox">
                      <label
                        htmlFor="filter-users-connected-account-only"
                        className="filter-checkbox-label"
                      >
                        <input
                          id="filter-users-connected-account-only"
                          type="checkbox"
                          checked={filters.users.connectedAccountOnly}
                          onChange={(e) =>
                            updateFilter('users', 'connectedAccountOnly', e.target.checked)
                          }
                        />
                        <span>🔌 Connected account</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="data-summary">
                  <div className="summary-info">
                    <p className="summary-metric">
                      <span className="summary-metric-label">Total Users:</span>{' '}
                      <span className="summary-metric-value summary-metric-value--users">
                        {users.count || 0}
                      </span>
                    </p>
                    <p className="summary-metric">
                      <span className="summary-metric-label">Filtered:</span>{' '}
                      <span className="summary-metric-value summary-metric-value--filtered">
                        {getSortedUsers().length}
                      </span>
                    </p>
                    {pushStats && (
                      <p className="summary-metric" title="Last 7 days; scheduler from master_config">
                        <span className="summary-metric-label">Push (7d):</span>{' '}
                        <span className="summary-metric-value">
                          {pushStats.sent ?? 0} sent · {pushStats.clicked ?? 0} clicked
                          {pushStats.scheduler_enabled === false ? ' · scheduler off' : ''}
                        </span>
                      </p>
                    )}
                    {connectedAccountsLoaded && (
                      <p className="summary-metric" title="OAuth connected social accounts">
                        <span className="summary-metric-label">Connected:</span>{' '}
                        <span className="summary-metric-value">
                          {Object.keys(connectedAccountsByUser).length} users
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="sort-controls">
                    <button
                      type="button"
                      className="btn-refresh"
                      onClick={refreshFirestoreUsers}
                      disabled={loadingFirestore}
                    >
                      Refresh
                    </button>
                    <button 
                      onClick={toggleUserSortType} 
                      className="btn-sort-type"
                      title={`Sort by ${userSortType === 'created' ? 'last active' : 'created date'}`}
                    >
                      Sort by: {userSortType === 'created' ? 'Created' : 'Last Active'}
                    </button>
                    <button 
                      onClick={() => toggleSortOrder('users')} 
                      className="btn-sort"
                      title={sortLatestFirst.users ? 'Show oldest first' : 'Show latest first'}
                    >
                      {userSortType === 'created' 
                        ? (sortLatestFirst.users ? '⬇️ Latest Created' : '⬆️ Oldest Created')
                        : (sortLatestFirst.users ? '⬇️ Latest Active' : '⬆️ Oldest Active')
                      }
                    </button>
                  </div>
                </div>
                <div className="users-list">
                  {getSortedUsers().map((user) => {
                    const isCollapsed = collapsedItems.users.has(user.user_id);
                    return (
                      <div key={user.user_id} className="data-item" data-user-id={user.user_id}>
                        <div 
                          className="data-header clickable" 
                          onClick={() => toggleCollapse('users', user.user_id)}
                        >
                          <div className="header-left">
                            <span className="expand-icon">{isCollapsed ? '▶' : '▼'}</span>
                            <span className="user-id">{user.email || user.user_id}</span>
                            <div
                              className="firestore-user-row-actions"
                              onClick={(e) => e.stopPropagation()}
                              role="group"
                              aria-label="Open tasks or file management"
                            >
                              <button
                                type="button"
                                className="btn-firestore-users-row"
                                onClick={() => openTasksFromFirestoreUserRow(user)}
                                title="Open Tasks filtered by this user"
                              >
                                Tasks
                              </button>
                              <button
                                type="button"
                                className="btn-firestore-users-row btn-firestore-users-row--files"
                                onClick={() => openFileManagementFromFirestoreUserRow(user)}
                                title="Open File Management for this user"
                              >
                                Files
                              </button>
                              <button
                                type="button"
                                className="btn-firestore-users-row btn-firestore-users-row--journey"
                                onClick={() => openUserJourneyFromFirestoreUserRow(user)}
                                title="Open User Journey for this user (created date to today)"
                              >
                                Journey
                              </button>
                            </div>
                            <span className="user-date">{formatDate(user.created_at)}</span>
                            <span className="user-last-active">({formatTimeAgo(user.last_active || user.updated_at)})</span>
                            {user.phone_number && <span className="user-phone-badge" style={{ marginLeft: '8px', padding: '2px 8px', background: '#10b981', color: 'white', borderRadius: '12px', fontSize: '11px' }}>📱 Has Phone</span>}
                            {hasSocialHandle(user) && (
                              <span
                                style={{ marginLeft: '6px', fontSize: '13px' }}
                                title={formatSocialHandleTooltip(user)}
                                aria-label="Social handle on file"
                              >
                                📷
                              </span>
                            )}
                            {getUserConnectedPlatforms(user.user_id, connectedAccountsByUser).map((platform) => {
                              const meta = CONNECTED_PLATFORM_META[platform] || {
                                short: platform.slice(0, 2).toUpperCase(),
                                label: platform,
                              };
                              return (
                                <span
                                  key={`${user.user_id}-${platform}`}
                                  className={`user-connected-badge user-connected-badge--${platform}`}
                                  title={`${meta.label} connected (OAuth)`}
                                  aria-label={`${meta.label} connected`}
                                >
                                  🔌 {meta.short}
                                </span>
                              );
                            })}
                            {user.signup_country_code && (
                              <span
                                style={{
                                  marginLeft: '8px',
                                  padding: '2px 8px',
                                  background: '#2563eb',
                                  color: 'white',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  letterSpacing: '0.3px',
                                }}
                                title={`Signup country: ${user.signup_country_code}`}
                              >
                                🌍 {String(user.signup_country_code).toUpperCase()}
                              </span>
                            )}
                            {user.coupon_code_applied && (
                              <span
                                style={{
                                  marginLeft: '8px',
                                  padding: '2px 8px',
                                  background: isCouponTrialActive(user)
                                    ? '#111827'
                                    : isCouponTrialExpiredMetadata(user)
                                      ? '#6b7280'
                                      : '#374151',
                                  color: 'white',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                }}
                                title={
                                  isCouponTrialActive(user)
                                    ? `Coupon: ${user.coupon_code_applied} (trial active)`
                                    : isCouponTrialExpiredMetadata(user)
                                      ? `Coupon: ${user.coupon_code_applied} (trial ended — DB metadata stale). Run Controls → Coupon trial cleanup.`
                                      : `Coupon: ${user.coupon_code_applied} (trial ended)`
                                }
                              >
                                {isCouponTrialActive(user)
                                  ? '🎟 Coupon'
                                  : isCouponTrialExpiredMetadata(user)
                                    ? '🎟 Coupon *'
                                    : '🎟 Coupon (expired)'}
                              </span>
                            )}
                            {(() => {
                              const effectiveStatus = getEffectiveSubscriptionStatus(user);
                              const storedStatus = String(user.subscription_status || '').toLowerCase();
                              if (!effectiveStatus && !storedStatus) return null;
                              const displayStatus = effectiveStatus || storedStatus;
                              const staleInDb =
                                storedStatus &&
                                effectiveStatus &&
                                storedStatus !== effectiveStatus.toLowerCase();
                              return (
                                <span
                                  style={{
                                    marginLeft: '8px',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    textTransform: 'capitalize',
                                    ...getSubscriptionStatusBadgeStyle(displayStatus),
                                  }}
                                  title={
                                    staleInDb
                                      ? `Stored in Firestore: ${storedStatus}. Run Controls → Subscription status cleanup to update DB.`
                                      : undefined
                                  }
                                >
                                  {`Subscription: ${displayStatus}`}
                                  {staleInDb ? ' *' : ''}
                                </span>
                              );
                            })()}
                            {isCancelAtPeriodEndUser(user) && (
                              <span
                                style={{
                                  marginLeft: '8px',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  background: '#f59e0b',
                                  color: '#111827',
                                }}
                                title="Auto-renew cancelled; access until current period end"
                              >
                                Active until cancelled
                              </span>
                            )}
                            {getDisplayStreakCount(user) > 0 && (
                              <span
                                style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 600 }}
                                title={`Creator streak: ${getDisplayStreakCount(user)} day(s), last ${user.creator_streak_last_date || 'N/A'}`}
                              >
                                🔥 {getDisplayStreakCount(user)}
                              </span>
                            )}
                            {user.reminders_enabled && (
                              <span
                                style={{ marginLeft: '6px', fontSize: '13px' }}
                                title={`Push reminders: ${user.push_status || 'granted'}`}
                                aria-label="Push reminders enabled"
                              >
                                🔔
                              </span>
                            )}
                          </div>
                        </div>
                        {!isCollapsed && (
                          <div className="data-details">
                            <p><strong>User ID:</strong> {user.user_id}</p>
                            <p><strong>Name:</strong> {user.name || 'N/A'}</p>
                            <p><strong>Last Active:</strong> {formatDate(user.last_active || user.updated_at)}</p>
                            <p><strong>Signup Country:</strong> {user.signup_country_code ? String(user.signup_country_code).toUpperCase() : 'N/A'}</p>
                            {user.phone_number && <p><strong>Phone:</strong> {user.phone_number}</p>}
                            <p><strong>Instagram handle:</strong> {String(user.instagram_handle || '').trim() || 'N/A'}</p>
                            <p><strong>YouTube handle:</strong> {String(user.youtube_handle || '').trim() || 'N/A'}</p>
                            {hasConnectedAccount(user.user_id, connectedAccountsByUser) && (
                              <div className="user-stats" style={{ marginTop: '12px' }}>
                                <h5>Connected accounts (OAuth)</h5>
                                <div className="connected-accounts-detail-list">
                                  {getUserConnectedPlatforms(user.user_id, connectedAccountsByUser).map((platform) => {
                                    const account =
                                      connectedAccountsByUser[user.user_id]?.accounts?.[platform] || {};
                                    const meta = CONNECTED_PLATFORM_META[platform] || {
                                      short: platform,
                                      label: platform,
                                    };
                                    const handle = String(account.handle || '').trim();
                                    const display = String(account.display || meta.label).trim();
                                    return (
                                      <p key={`${user.user_id}-detail-${platform}`}>
                                        <strong>{meta.label}:</strong>{' '}
                                        {handle ? `@${handle.replace(/^@/, '')}` : display}
                                        {account.connected_at ? (
                                          <span style={{ opacity: 0.75 }}>
                                            {' '}
                                            · connected {formatDate(account.connected_at)}
                                          </span>
                                        ) : null}
                                      </p>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {user.social_analysis_submitted_at && (
                              <p>
                                <strong>Social submitted:</strong>{' '}
                                {formatDate(user.social_analysis_submitted_at)}
                              </p>
                            )}
                            <p>
                              <strong>Referral source:</strong>{' '}
                              {user.referral_source_url ? (
                                <a
                                  href={user.referral_source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ wordBreak: 'break-all', color: '#60a5fa' }}
                                >
                                  {user.referral_source_url}
                                </a>
                              ) : (
                                <span style={{ opacity: 0.7 }}>Not captured</span>
                              )}
                            </p>
                            <div className="user-stats" style={{ marginTop: '12px' }}>
                              <h5>Premium / Coupons</h5>
                              <div className="stats-grid">
                                <div className="stat-item">
                                  <span className="stat-label">Premium source:</span>
                                  <span className="stat-value">{user.premium_source || 'N/A'}</span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Coupon code:</span>
                                  <span className="stat-value">{user.coupon_code_applied || 'N/A'}</span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Coupon trial end:</span>
                                  <span className="stat-value">
                                    {user.coupon_trial_end_at_iso ? formatDate(user.coupon_trial_end_at_iso) : 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {(user.razorpay_subscription_id || user.subscription_status || getEffectiveSubscriptionStatus(user)) && (
                              <div className="user-stats" style={{ marginTop: '12px' }}>
                                <h5>Subscription</h5>
                                <div className="stats-grid">
                                  <div className="stat-item">
                                    <span className="stat-label">Status:</span>
                                    <span className="stat-value" style={{ textTransform: 'capitalize' }}>
                                      {getEffectiveSubscriptionStatus(user) || user.subscription_status || 'N/A'}
                                      {getEffectiveSubscriptionStatus(user) &&
                                      user.subscription_status &&
                                      getEffectiveSubscriptionStatus(user).toLowerCase() !==
                                        String(user.subscription_status).toLowerCase()
                                        ? ` (stored: ${user.subscription_status})`
                                        : ''}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Cancel at period end:</span>
                                    <span className="stat-value">
                                      {isCancelAtPeriodEndUser(user) ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                  {user.subscription_cancelled_at_iso && (
                                    <div className="stat-item">
                                      <span className="stat-label">Cancelled at:</span>
                                      <span className="stat-value">
                                        {formatDate(user.subscription_cancelled_at_iso)}
                                      </span>
                                    </div>
                                  )}
                                  <div className="stat-item">
                                    <span className="stat-label">Current Period End:</span>
                                    <span className="stat-value">
                                      {user.subscription_current_end_iso ? formatDate(user.subscription_current_end_iso) : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Subscription ID:</span>
                                    <span className="stat-value">
                                      {user.razorpay_subscription_id || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Last Payment ID:</span>
                                    <span className="stat-value">{user.last_payment_id || 'N/A'}</span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Last Billing Event At:</span>
                                    <span className="stat-value">
                                      {user.last_razorpay_event_at ? formatDate(user.last_razorpay_event_at) : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Current Period Start:</span>
                                    <span className="stat-value">
                                      {user.subscription_current_start_iso ? formatDate(user.subscription_current_start_iso) : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Next Charge At:</span>
                                    <span className="stat-value">
                                      {user.subscription_charge_at_iso ? formatDate(user.subscription_charge_at_iso) : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Paid Count:</span>
                                    <span className="stat-value">{user.subscription_paid_count ?? 'N/A'}</span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Remaining Count:</span>
                                    <span className="stat-value">{user.subscription_remaining_count ?? 'N/A'}</span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Last Payment Amount:</span>
                                    <span className="stat-value">
                                      {typeof user.last_payment_amount === 'number'
                                        ? `Rs ${(user.last_payment_amount / 100).toFixed(2)}`
                                        : 'N/A'}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Last Payment Status:</span>
                                    <span className="stat-value" style={{ textTransform: 'capitalize' }}>
                                      {user.last_payment_status || 'N/A'}
                                    </span>
                                  </div>
                                  <div className="stat-item">
                                    <span className="stat-label">Last Payment Method:</span>
                                    <span className="stat-value" style={{ textTransform: 'capitalize' }}>
                                      {user.last_payment_method || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="user-stats" style={{ marginTop: '12px' }}>
                              <h5>Creator Streak</h5>
                              <div className="stats-grid">
                                <div className="stat-item">
                                  <span className="stat-label">Streak count:</span>
                                  <span className="stat-value">
                                    {getDisplayStreakCount(user) > 0 ? getDisplayStreakCount(user) : 'N/A'}
                                  </span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Last check-in date:</span>
                                  <span className="stat-value">{user.creator_streak_last_date || 'N/A'}</span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Timezone:</span>
                                  <span className="stat-value">{user.creator_streak_timezone || 'N/A'}</span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Reminders enabled:</span>
                                  <span className="stat-value">
                                    {user.reminders_enabled === true
                                      ? 'Yes'
                                      : user.reminders_enabled === false
                                        ? 'No'
                                        : 'N/A'}
                                  </span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Push status:</span>
                                  <span className="stat-value" style={{ textTransform: 'capitalize' }}>
                                    {user.push_status || 'N/A'}
                                  </span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Push subscribed at:</span>
                                  <span className="stat-value">
                                    {user.push_subscribed_at ? formatDate(user.push_subscribed_at) : 'N/A'}
                                  </span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Push subscription:</span>
                                  <span
                                    className="stat-value"
                                    style={{ wordBreak: 'break-all', fontSize: '12px' }}
                                    title={hasPushSubscription(user) ? 'Web Push endpoint (truncated)' : ''}
                                  >
                                    {summarizePushSubscription(user)}
                                  </span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Last push sent (local date):</span>
                                  <span className="stat-value">{user.last_push_sent_date || 'N/A'}</span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Reminder hour (local):</span>
                                  <span className="stat-value">
                                    {user.notification_send_hour_local != null
                                      ? `${user.notification_send_hour_local}:00`
                                      : '19:00 (default)'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="user-stats">
                              <h5>Video Processing Stats</h5>
                              <div className="stats-grid">
                                <div className="stat-item">
                                  <span className="stat-label">Total Videos Uploaded:</span>
                                  <span className="stat-value">{user.total_videos_uploaded || 0}</span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Total Time Processed:</span>
                                  <span className="stat-value">
                                    {user.total_video_time_processed 
                                      ? `${formatTime(user.total_video_time_processed)}`
                                      : '0s'}
                                  </span>
                                </div>
                                <div className="stat-item">
                                  <span className="stat-label">Total Size Processed:</span>
                                  <span className="stat-value">
                                    {user.total_video_size_processed 
                                      ? formatFileSize(user.total_video_size_processed)
                                      : '0 B'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-state">No data available</div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="admin-content log-tab">
            <div className="section-header log-tab-header">
              <h2>Application Logs</h2>
              <div className="log-toolbar" role="toolbar" aria-label="Log viewer tools">
                <div className="log-toolbar-section log-toolbar-section--tasks">
                  <span className="log-toolbar-section-label">Tasks</span>
                  <button
                    type="button"
                    onClick={handleAnalyzeLogTasks}
                    className="btn-refresh log-toolbar-btn log-analyze-btn"
                    disabled={loadingLogs || !logFile?.content || showErrorLogsOnly}
                    title={
                      showErrorLogsOnly
                        ? 'Turn off Show Error Logs Only to analyze tasks in the full log'
                        : 'Detect processing sessions (Session started / background processing lines)'
                    }
                  >
                    Analyze
                  </button>
                  <select
                    className="log-task-select"
                    aria-label="Jump to a processing task in the log"
                    value={selectedLogTaskKey}
                    onChange={handleLogTaskSelect}
                    disabled={!logAnalyzedTasks.length || showErrorLogsOnly}
                    title={
                      showErrorLogsOnly
                        ? 'Turn off error-only mode to jump to tasks'
                        : 'Scroll to session start and highlight that task’s log range'
                    }
                  >
                    <option value="">
                      {logAnalyzedTasks.length
                        ? `Jump to… (${logAnalyzedTasks.length} found)`
                        : 'Run Analyze first'}
                    </option>
                    {logAnalyzedTasks.map((t, i) => {
                      const tid =
                        t.taskId.length > 14 ? `${t.taskId.slice(0, 8)}…` : t.taskId;
                      return (
                        <option key={t.key} value={t.key}>
                          {`${i + 1}. ${t.displayLabel} (${tid})`}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className={`log-task-copy-btn${logTaskCopyFlash ? ' log-task-copy-btn--ok' : ''}`}
                    onClick={handleCopySelectedLogTaskText}
                    disabled={
                      showErrorLogsOnly ||
                      !logAnalyzedTasks.length ||
                      !selectedLogTaskKey ||
                      !selectedLogTask
                    }
                    title={logTaskCopyFlash ? 'Copied' : 'Copy raw log lines for this task'}
                    aria-label={
                      logTaskCopyFlash
                        ? 'Copied to clipboard'
                        : 'Copy raw log lines for selected task'
                    }
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <rect
                        x="9"
                        y="9"
                        width="13"
                        height="13"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </div>
                <div className="log-toolbar-section log-toolbar-section--search">
                  <div className="log-search-panel">
                    <div className="log-search-row">
                      <input
                        id="log-viewer-search"
                        type="search"
                        className="log-search-input"
                        placeholder="Email, file name, task id…"
                        value={logViewerSearchInput}
                        onChange={(e) => setLogViewerSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          if (!logViewerSearchDebounced || logSearchMatchLineIndices.length === 0)
                            return;
                          e.preventDefault();
                          if (e.shiftKey) goLogSearchPrev();
                          else goLogSearchNext();
                        }}
                        autoComplete="off"
                        spellCheck={false}
                        aria-label="Search in log lines. Enter: next match, Shift+Enter: previous"
                      />
                      <div className="log-search-nav" role="group" aria-label="Search matches">
                        <button
                          type="button"
                          className="log-search-nav-btn"
                          onClick={goLogSearchPrev}
                          disabled={
                            !logViewerSearchDebounced || logSearchMatchLineIndices.length === 0
                          }
                          title="Previous match (Shift+Enter)"
                        >
                          ↑
                        </button>
                        <span className="log-search-nav-count" aria-live="polite">
                          {logViewerSearchDebounced && logSearchMatchLineIndices.length > 0
                            ? `${logSearchMatchCursor + 1}/${logSearchMatchLineIndices.length}`
                            : logViewerSearchDebounced
                              ? '0'
                              : '—'}
                        </span>
                        <button
                          type="button"
                          className="log-search-nav-btn"
                          onClick={goLogSearchNext}
                          disabled={
                            !logViewerSearchDebounced || logSearchMatchLineIndices.length === 0
                          }
                          title="Next match (Enter)"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {(!logFile || !logFile.filename) && (
                  <div className="log-toolbar-section log-toolbar-section--fetch">
                    <span className="log-toolbar-section-label">Load</span>
                    <div className="date-picker-container log-toolbar-date">
                      <label htmlFor="log-date-picker-top">Date</label>
                      <input
                        id="log-date-picker-top"
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="log-date-picker"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => loadLogs(selectedDate)}
                      className="btn-refresh log-toolbar-btn"
                      disabled={loadingLogs}
                    >
                      {loadingLogs ? 'Loading…' : 'Refresh'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {loadingLogs && !logFile ? (
              <div className="loading-state log-tab-state">Loading logs…</div>
            ) : !logFile || !logFile.filename ? (
              <div className="empty-state log-tab-state">
                No log file for <strong>{selectedDate}</strong>. Pick another date above or refresh.
              </div>
            ) : (
              <div className="log-viewer">
                <div className="log-header">
                  <div className="log-info">
                    <div className="log-filename-row">
                      <h3 className="log-filename">{logFile.filename}</h3>
                      {loadingLogs && (
                        <span
                          className="log-refresh-indicator"
                          aria-label="Refreshing log"
                          role="status"
                          title="Refreshing…"
                        >
                          <span className="log-refresh-spinner" aria-hidden="true" />
                        </span>
                      )}
                    </div>
                    <div className="log-meta log-meta--chips">
                      <span className="log-meta-chip">{logFile.date}</span>
                      <span className="log-meta-chip">{logFile.size_mb} MB</span>
                      {logFile.line_count != null && (
                        <span className="log-meta-chip">
                          {logFile.line_count.toLocaleString()} lines
                          {logFile.is_preview &&
                            ` · last ${(logFile.preview_line_count || 20000).toLocaleString()}`}
                        </span>
                      )}
                      <span className="log-meta-chip">
                        {new Date(logFile.modified).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="log-actions log-actions--with-toggle">
                    <div className="date-picker-container log-header-date-picker">
                      <label htmlFor="log-date-picker-header">Date</label>
                      <input
                        id="log-date-picker-header"
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="log-date-picker"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="log-actions-divider" aria-hidden="true" />
                    <div className="error-toggle-container log-header-error-toggle">
                      <label className="error-toggle-label" title="Show only lines containing ERROR">
                        <input
                          type="checkbox"
                          checked={showErrorLogsOnly}
                          onChange={(e) => setShowErrorLogsOnly(e.target.checked)}
                          className="error-toggle-input"
                        />
                        <span className="error-toggle-slider"></span>
                        <span className="error-toggle-text">Errors only</span>
                      </label>
                    </div>
                    <div className="log-actions-divider" aria-hidden="true" />
                    <button
                      type="button"
                      onClick={() => loadLogs(selectedDate)}
                      className="btn-refresh log-header-action-btn"
                      disabled={loadingLogs}
                    >
                      {loadingLogs ? 'Loading…' : 'Refresh'}
                    </button>
                    <button 
                      onClick={() => handleDownloadLog(logFile.filename)} 
                      className="btn-action btn-download log-header-action-btn" 
                      title="Download full log file"
                    >
                      Download
                    </button>
                  </div>
                </div>
                {logFile.error ? (
                  <div className="log-error">Error: {logFile.error}</div>
                ) : logFile.content && (
                  <div className="log-preview" ref={logContainerRef}>
                    {showErrorLogsOnly ? (
                      <>
                        <div className="log-lines-body">
                          {logLinesForViewer.map((line, idx) => {
                            if (!line.toUpperCase().includes('ERROR')) return null;
                            const q = logViewerSearchDebounced.toLowerCase();
                            if (q && !line.toLowerCase().includes(q)) return null;
                            const inTask =
                              selectedLogTask &&
                              idx >= selectedLogTask.startLine &&
                              idx < selectedLogTask.endLine;
                            const searchHit = Boolean(q && line.toLowerCase().includes(q));
                            const isCurrentSearch =
                              searchHit && logSearchCurrentLineIndex === idx;
                            return (
                              <div
                                key={idx}
                                id={`admin-log-line-${idx}`}
                                className={
                                  'log-line' +
                                  (inTask ? ' log-line--task-range' : '') +
                                  (searchHit ? ' log-line--search-hit' : '') +
                                  (isCurrentSearch ? ' log-line--search-current' : '')
                                }
                              >
                                {line || '\u00a0'}
                              </div>
                            );
                          })}
                        </div>
                        {(() => {
                          const visible = logLinesForViewer.some((line) => {
                            if (!line.toUpperCase().includes('ERROR')) return false;
                            const q = logViewerSearchDebounced.toLowerCase();
                            if (q && !line.toLowerCase().includes(q)) return false;
                            return true;
                          });
                          if (visible) return null;
                          const anyError = logLinesForViewer.some((line) =>
                            line.toUpperCase().includes('ERROR')
                          );
                          return (
                            <div className="log-no-errors">
                              {!anyError
                                ? 'No ERROR lines found in this log file preview.'
                                : 'No ERROR lines match your search.'}
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="log-lines-body">
                        {logLinesForViewer.map((line, idx) => {
                          const inTask =
                            selectedLogTask &&
                            idx >= selectedLogTask.startLine &&
                            idx < selectedLogTask.endLine;
                          const q = logViewerSearchDebounced.toLowerCase();
                          const searchHit = Boolean(q && line.toLowerCase().includes(q));
                          const isCurrentSearch =
                            searchHit && logSearchCurrentLineIndex === idx;
                          return (
                            <div
                              key={idx}
                              id={`admin-log-line-${idx}`}
                              className={
                                'log-line' +
                                (inTask ? ' log-line--task-range' : '') +
                                (searchHit ? ' log-line--search-hit' : '') +
                                (isCurrentSearch ? ' log-line--search-current' : '')
                              }
                            >
                              {line || '\u00a0'}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {logFile.is_preview && (
                      <div className="log-preview-notice">
                        Showing the latest {logFile.preview_line_count || 20000} lines. Download full log to see all {logFile.line_count?.toLocaleString()} lines.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'razorpay-webhooks' && (
          <div className="admin-content webhooks-tab">
            <div className="section-header">
              <h2>Razorpay webhook events</h2>
              <div className="webhooks-toolbar">
                <label className="webhooks-search-label">
                  <span className="webhooks-search-label-text">Search</span>
                  <input
                    type="search"
                    className="webhooks-search-input"
                    placeholder="Search loaded rows"
                    value={razorpayWebhookSearch}
                    onChange={(e) => setRazorpayWebhookSearch(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    aria-label="Search Razorpay webhook events in loaded list"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => loadRazorpayWebhookEvents({ append: false })}
                  className="btn-refresh"
                  disabled={loadingRazorpayWebhooks}
                >
                  {loadingRazorpayWebhooks && razorpayWebhookEvents.length === 0
                    ? 'Loading...'
                    : '🔄 Refresh'}
                </button>
                <button
                  type="button"
                  className="btn-small btn-danger"
                  disabled={
                    deletingRazorpayWebhooks || selectedRazorpayWebhookDocIds.length === 0
                  }
                  title={
                    selectedRazorpayWebhookDocIds.length === 0
                      ? 'Select rows to delete'
                      : 'Remove audit rows from Firestore. Razorpay may treat future retries as new events.'
                  }
                  onClick={() => {
                    const n = selectedRazorpayWebhookDocIds.length;
                    if (!n) return;
                    if (
                      !window.confirm(
                        `Delete ${n} webhook event record(s) from Firestore? This cannot be undone.\n\nRemoving processed-event rows means the same Razorpay event_id could be processed again if Razorpay retries the webhook.`
                      )
                    ) {
                      return;
                    }
                    deleteRazorpayWebhookEventsBatch(selectedRazorpayWebhookDocIds);
                  }}
                >
                  {deletingRazorpayWebhooks
                    ? 'Deleting…'
                    : `Delete selected (${selectedRazorpayWebhookDocIds.length})`}
                </button>
              </div>
            </div>
            {razorpayWebhooksError && (
              <div className="empty-state" style={{ color: '#b91c1c' }}>
                {razorpayWebhooksError}
              </div>
            )}
            {loadingRazorpayWebhooks && razorpayWebhookEvents.length === 0 ? (
              <div className="loading-state">Loading webhook events…</div>
            ) : !razorpayWebhookEvents.length && !razorpayWebhooksError ? (
              <div className="empty-state">No webhook events recorded yet.</div>
            ) : (
              <>
                <div className="webhooks-table-wrapper">
                  <table className="webhooks-table">
                    <thead>
                      <tr>
                        <th className="webhooks-col-select" scope="col">
                          <input
                            ref={razorpayWebhookSelectAllRef}
                            type="checkbox"
                            aria-label="Select all visible webhook rows"
                            onChange={() => {
                              const vis = filteredRazorpayVisibleDocIds;
                              if (!vis.length) return;
                              const allOn = vis.every((id) =>
                                selectedRazorpayWebhookDocIds.includes(id)
                              );
                              if (allOn) {
                                const visSet = new Set(vis);
                                setSelectedRazorpayWebhookDocIds((prev) =>
                                  prev.filter((id) => !visSet.has(id))
                                );
                              } else {
                                setSelectedRazorpayWebhookDocIds((prev) =>
                                  Array.from(new Set([...prev, ...vis]))
                                );
                              }
                            }}
                          />
                        </th>
                        <th className="webhooks-col-processed">Processed</th>
                        <th className="webhooks-col-type webhooks-col-type-header">
                          <div className="webhooks-event-filter" ref={webhooksFilterMenuRef}>
                            <button
                              type="button"
                              className="webhooks-event-filter-trigger"
                              onClick={() => setWebhookEventFilterOpen((o) => !o)}
                              aria-expanded={webhookEventFilterOpen}
                              aria-haspopup="listbox"
                            >
                              <span className="webhooks-event-filter-label">Event type</span>
                              <span
                                className={`webhooks-event-filter-chevron${
                                  webhookEventFilterOpen ? ' webhooks-event-filter-chevron--open' : ''
                                }`}
                                aria-hidden
                              >
                                ▾
                              </span>
                            </button>
                            {webhookEventFilterOpen && (
                              <ul className="webhooks-event-filter-menu" role="listbox">
                                <li role="none">
                                  <button
                                    type="button"
                                    role="option"
                                    className={
                                      webhookEventTypeFilter === 'all'
                                        ? 'webhooks-event-filter-option webhooks-event-filter-option--active'
                                        : 'webhooks-event-filter-option'
                                    }
                                    onClick={() => {
                                      setWebhookEventTypeFilter('all');
                                      setWebhookEventFilterOpen(false);
                                    }}
                                  >
                                    All types
                                  </button>
                                </li>
                                {webhookEventTypesInData.map((t) => (
                                  <li key={t} role="none">
                                    <button
                                      type="button"
                                      role="option"
                                      className={
                                        webhookEventTypeFilter === t
                                          ? 'webhooks-event-filter-option webhooks-event-filter-option--active'
                                          : 'webhooks-event-filter-option'
                                      }
                                      onClick={() => {
                                        setWebhookEventTypeFilter(t);
                                        setWebhookEventFilterOpen(false);
                                      }}
                                    >
                                      {t}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </th>
                        <th className="webhooks-col-received">Received at</th>
                        <th className="webhooks-col-email">Email</th>
                        <th className="webhooks-col-id">Event id</th>
                        <th className="webhooks-col-account">Account</th>
                        <th className="webhooks-col-sub">Subscription</th>
                        <th className="webhooks-col-user">User</th>
                        <th className="webhooks-col-order">Order</th>
                        <th className="webhooks-col-invoice">Invoice</th>
                        <th className="webhooks-col-acquirer">Acquirer</th>
                        <th className="webhooks-col-pay-err">Pay error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRazorpayWebhookEvents.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="webhooks-filter-empty">
                            No events match the current search or event type filter.{' '}
                            {(webhookEventTypeFilter !== 'all' || razorpayWebhookSearch.trim()) && (
                              <>
                                {webhookEventTypeFilter !== 'all' && (
                                  <button
                                    type="button"
                                    className="webhooks-filter-clear"
                                    onClick={() => setWebhookEventTypeFilter('all')}
                                  >
                                    Clear type filter
                                  </button>
                                )}
                                {razorpayWebhookSearch.trim() && (
                                  <button
                                    type="button"
                                    className="webhooks-filter-clear"
                                    onClick={() => setRazorpayWebhookSearch('')}
                                  >
                                    Clear search
                                  </button>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      ) : (
                        filteredRazorpayWebhookEvents.map((row) => {
                          const acq = summarizeWebhookAcquirerData(row.acquirer_data);
                          const errTitle =
                            row.error_description != null && String(row.error_description).trim() !== ''
                              ? String(row.error_description)
                              : undefined;
                          const errCode =
                            row.error_code != null && String(row.error_code).trim() !== ''
                              ? String(row.error_code)
                              : '—';
                          const rowDocId = row.doc_id || row.event_id ? String(row.doc_id || row.event_id) : '';
                          return (
                            <tr key={row.doc_id || row.event_id}>
                              <td className="webhooks-col-select">
                                <input
                                  type="checkbox"
                                  checked={Boolean(rowDocId && selectedRazorpayWebhookDocIds.includes(rowDocId))}
                                  disabled={!rowDocId || deletingRazorpayWebhooks}
                                  aria-label={`Select webhook event ${rowDocId || 'row'}`}
                                  onChange={() => {
                                    if (!rowDocId) return;
                                    setSelectedRazorpayWebhookDocIds((prev) =>
                                      prev.includes(rowDocId)
                                        ? prev.filter((id) => id !== rowDocId)
                                        : [...prev, rowDocId]
                                    );
                                  }}
                                />
                              </td>
                              <td className="webhooks-col-processed">
                                {row.processed_at_iso ? formatDate(row.processed_at_iso) : '—'}
                              </td>
                              <td className="webhooks-col-type">
                                <code
                                  className={webhookEventToneClass(row.event_type)}
                                  title={row.event_type || ''}
                                >
                                  {row.event_type || '—'}
                                </code>
                              </td>
                              <td className="webhooks-col-received">{row.received_at || '—'}</td>
                              <td className="webhooks-col-email">{row.email || '—'}</td>
                              <td className="webhooks-col-id">
                                <code title={row.event_id || row.doc_id}>
                                  {row.event_id || row.doc_id || '—'}
                                </code>
                              </td>
                              <td className="webhooks-col-account">
                                <code title={row.account_id || ''}>{row.account_id || '—'}</code>
                              </td>
                              <td className="webhooks-col-sub">
                                <code title={row.subscription_id || ''}>{row.subscription_id || '—'}</code>
                              </td>
                              <td className="webhooks-col-user">
                                <code className="webhooks-user-id-code" title={row.user_id || ''}>
                                  {row.user_id || '—'}
                                </code>
                              </td>
                              <td className="webhooks-col-order">
                                <code title={row.order_id || ''}>{row.order_id || '—'}</code>
                              </td>
                              <td className="webhooks-col-invoice">
                                <code title={row.invoice_id || ''}>{row.invoice_id || '—'}</code>
                              </td>
                              <td className="webhooks-col-acquirer">
                                <code className="webhooks-acquirer-json" title={acq.full || undefined}>
                                  {acq.short}
                                </code>
                              </td>
                              <td className="webhooks-col-pay-err" title={errTitle}>
                                {errCode}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {razorpayWebhooksHasMore && (
                  <div className="webhooks-load-more-wrap">
                    <button
                      type="button"
                      className="btn-action"
                      disabled={loadingRazorpayWebhooks}
                      onClick={() =>
                        loadRazorpayWebhookEvents({
                          append: true,
                          cursor: razorpayWebhooksNextCursor,
                        })
                      }
                    >
                      {loadingRazorpayWebhooks ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'survey' && (
          <div className="admin-content webhooks-tab survey-tab">
            <div className="section-header">
              <h2>Payment abandonment surveys</h2>
              <div className="webhooks-toolbar">
                <button
                  type="button"
                  onClick={() => loadPaymentAbandonmentSurveys({ append: false })}
                  className="btn-refresh"
                  disabled={loadingPaymentSurveys}
                >
                  {loadingPaymentSurveys && paymentSurveys.length === 0 ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            {paymentSurveysError && (
              <div className="empty-state" style={{ color: '#b91c1c' }}>
                {paymentSurveysError}
              </div>
            )}

            {loadingPaymentSurveys && paymentSurveys.length === 0 ? (
              <div className="loading-state">Loading survey submissions…</div>
            ) : !paymentSurveys.length && !paymentSurveysError ? (
              <div className="empty-state">No survey submissions recorded yet.</div>
            ) : (
              <>
                <div className="webhooks-table-wrapper">
                  <table className="webhooks-table survey-table">
                    <thead>
                      <tr>
                        <th className="survey-col-created">Created</th>
                        <th className="survey-col-client-ts">Client time</th>
                        <th className="survey-col-email">Email</th>
                        <th className="survey-col-uid">User ID</th>
                        <th className="survey-col-reasons">Reasons</th>
                        <th className="survey-col-other">Other</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentSurveys.map((row) => {
                        const reasons = Array.isArray(row.reasons) ? row.reasons.filter(Boolean) : [];
                        const reasonsTitle = reasons.join('\n');
                        return (
                          <tr key={row.doc_id || `${row.uid || 'uid'}-${row.created_at_iso || ''}`}>
                            <td className="survey-col-created">
                              {row.created_at_iso ? formatDate(row.created_at_iso) : '—'}
                            </td>
                            <td className="survey-col-client-ts">{row.client_timestamp || '—'}</td>
                            <td className="survey-col-email">{row.email || '—'}</td>
                            <td className="survey-col-uid">
                              <code title={row.uid || ''}>{row.uid || '—'}</code>
                            </td>
                            <td className="survey-col-reasons">
                              {reasons.length ? (
                                <span className="survey-reasons-list" title={reasonsTitle}>
                                  {reasons.join(', ')}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="survey-col-other">
                              {row.other ? <span title={row.other}>{row.other}</span> : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {paymentSurveysHasMore && (
                  <div className="webhooks-load-more-wrap">
                    <button
                      type="button"
                      className="btn-action"
                      disabled={loadingPaymentSurveys}
                      onClick={() =>
                        loadPaymentAbandonmentSurveys({
                          append: true,
                          cursor: paymentSurveysNextCursor,
                        })
                      }
                    >
                      {loadingPaymentSurveys ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'clip-feedback' && (
          <div className="admin-content webhooks-tab survey-tab">
            <div className="section-header">
              <h2>Clip feedback</h2>
              <div className="webhooks-toolbar">
                <button
                  type="button"
                  onClick={() => loadClipFeedback({ append: false })}
                  className="btn-refresh"
                  disabled={loadingClipFeedback}
                >
                  {loadingClipFeedback && clipFeedbackRows.length === 0 ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            {clipFeedbackError && (
              <div className="empty-state" style={{ color: '#b91c1c' }}>
                {clipFeedbackError}
              </div>
            )}

            {loadingClipFeedback && clipFeedbackRows.length === 0 ? (
              <div className="loading-state">Loading clip feedback…</div>
            ) : !clipFeedbackRows.length && !clipFeedbackError ? (
              <div className="empty-state">No clip feedback recorded yet.</div>
            ) : (
              <>
                <div className="webhooks-table-wrapper">
                  <table className="webhooks-table survey-table">
                    <thead>
                      <tr>
                        <th className="survey-col-created">Created</th>
                        <th className="survey-col-client-ts">Client time</th>
                        <th className="survey-col-email">Email</th>
                        <th className="survey-col-uid">User ID</th>
                        <th>Task ID</th>
                        <th>Stars</th>
                        <th>Action</th>
                        <th>Clip</th>
                        <th className="survey-col-other">Improvement</th>
                        <th>Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clipFeedbackRows.map((row) => (
                        <tr key={row.doc_id || `${row.task_id || 'task'}-${row.created_at_iso || ''}`}>
                          <td className="survey-col-created">{row.created_at_iso ? formatDate(row.created_at_iso) : '—'}</td>
                          <td className="survey-col-client-ts">{row.client_timestamp || '—'}</td>
                          <td className="survey-col-email">{row.email || '—'}</td>
                          <td className="survey-col-uid">
                            <code title={row.uid || ''}>{row.uid || '—'}</code>
                          </td>
                          <td><code title={row.task_id || ''}>{row.task_id || '—'}</code></td>
                          <td>{row.stars ? `${row.stars} / 5` : '—'}</td>
                          <td>{row.trigger_action || '—'}</td>
                          <td title={row.clip_filename || ''}>{row.clip_filename || '—'}</td>
                          <td className="survey-col-other">
                            {row.improvement_text ? <span title={row.improvement_text}>{row.improvement_text}</span> : '—'}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn-small"
                              disabled={!row.doc_id || deletingClipFeedbackId === row.doc_id}
                              onClick={() => deleteClipFeedback(row.doc_id)}
                              style={{
                                color: '#b91c1c',
                                borderColor: '#fecaca',
                                background: '#fff',
                              }}
                              title={row.doc_id ? 'Delete feedback' : 'Missing feedback id'}
                            >
                              {deletingClipFeedbackId === row.doc_id ? 'Deleting…' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {clipFeedbackHasMore && (
                  <div className="webhooks-load-more-wrap">
                    <button
                      type="button"
                      className="btn-action"
                      disabled={loadingClipFeedback}
                      onClick={() =>
                        loadClipFeedback({
                          append: true,
                          cursor: clipFeedbackNextCursor,
                        })
                      }
                    >
                      {loadingClipFeedback ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'user-journey' && (
          <div className="admin-content">
            {navigationState.fromFirestoreUsers &&
              navigationState.fromFirestoreUsers.source === 'journey' && (
              <div
                style={{
                  marginBottom: '15px',
                  padding: '12px',
                  background: '#eff6ff',
                  borderRadius: '6px',
                  border: '1px solid #93c5fd',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ color: '#1d4ed8', fontSize: '0.9rem' }}>
                  <strong>From Users:</strong> {navigationState.fromFirestoreUsers.email}
                </div>
                <button
                  type="button"
                  onClick={handleBackToFirestoreUsers}
                  style={{
                    padding: '6px 12px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                  }}
                >
                  ← Back to Users
                </button>
              </div>
            )}
            <div className="section-header">
              <h2>User Journey Analytics</h2>
              <div className="date-picker-container user-journey-range-controls">
                <label htmlFor="user-journey-min-events-filter">Min events:</label>
                <select
                  id="user-journey-min-events-filter"
                  className="log-date-picker user-journey-min-events-select"
                  value={String(userJourneyMinEventCount)}
                  onChange={(e) => setUserJourneyMinEventCount(Number(e.target.value))}
                  title="Show sessions with more than this many events"
                >
                  <option value="1">&gt; 1</option>
                  <option value="2">&gt; 2</option>
                  <option value="3">&gt; 3</option>
                  <option value="4">&gt; 4</option>
                  <option value="5">&gt; 5</option>
                </select>
                <label htmlFor="user-journey-date-from">From:</label>
                <input
                  id="user-journey-date-from"
                  type="date"
                  value={userJourneyDateFrom}
                  onChange={(e) => {
                    const nextFrom = e.target.value;
                    setUserJourneyDateFrom(nextFrom);
                    if (nextFrom && userJourneyDateTo && nextFrom > userJourneyDateTo) {
                      setUserJourneyDateTo(nextFrom);
                    }
                  }}
                  className="log-date-picker"
                  max={new Date().toISOString().split('T')[0]}
                />
                <label htmlFor="user-journey-date-to">To:</label>
                <input
                  id="user-journey-date-to"
                  type="date"
                  value={userJourneyDateTo}
                  onChange={(e) => {
                    const nextTo = e.target.value;
                    setUserJourneyDateTo(nextTo);
                    if (userJourneyDateFrom && nextTo && nextTo < userJourneyDateFrom) {
                      setUserJourneyDateFrom(nextTo);
                    }
                  }}
                  className="log-date-picker"
                  max={new Date().toISOString().split('T')[0]}
                />
                <button
                  type="button"
                  className="btn-small user-journey-preset-btn"
                  onClick={() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const ymd = toYmdLocal(today);
                    setUserJourneyDateFrom(ymd);
                    setUserJourneyDateTo(ymd);
                    loadUserJourneySessions(ymd, ymd);
                  }}
                  disabled={loadingUserJourney}
                  title="Set range to today"
                >
                  Today
                </button>
                <button
                  type="button"
                  className="btn-small user-journey-preset-btn"
                  onClick={() => {
                    const d = new Date();
                    d.setHours(0, 0, 0, 0);
                    d.setDate(d.getDate() - 1);
                    const ymd = toYmdLocal(d);
                    setUserJourneyDateFrom(ymd);
                    setUserJourneyDateTo(ymd);
                    loadUserJourneySessions(ymd, ymd);
                  }}
                  disabled={loadingUserJourney}
                  title="Set range to yesterday"
                >
                  Yesterday
                </button>
                <button
                  onClick={() => loadUserJourneySessions(userJourneyDateFrom, userJourneyDateTo)}
                  className="btn-refresh"
                  disabled={loadingUserJourney}
                >
                  {loadingUserJourney ? 'Loading...' : '🔄 Refresh'}
                </button>
                <button
                  type="button"
                  className="btn-small"
                  onClick={() => exportUserJourneyRange()}
                  disabled={
                    loadingUserJourney ||
                    exportingUserJourney ||
                    enumerateYmdDatesInclusive(userJourneyDateFrom, userJourneyDateTo).length === 0
                  }
                  title="Download combined JSON for all journey data in this date range (max 90 days)"
                >
                  {exportingUserJourney ? 'Exporting…' : '⬇ Export JSON'}
                </button>
              </div>
            </div>

            {/* Session filter: multiselect dropdown. Show sessions that CONTAIN any of the selected event types. */}
            {(() => {
              // Normalize to display names so we compare same values (backend may return enum keys)
              const toDisplayName = (name) => EVENT_ENUM_TO_DISPLAY[name] || name;
              const getSessionEventTypes = (s) =>
                (s.event_types || s.eventTypes || []).filter(Boolean).map(toDisplayName);
              const selected = userJourneySessionFilter || [];
              const emailScope = String(userJourneyEmailScope || '').trim().toLowerCase();
              const countryScope = String(userJourneyCountryScope || '').trim().toUpperCase();
              const landingScope = String(userJourneyLandingScope || '').trim().toLowerCase();
              const countryCounts = new Map();
              (userJourneySessions || []).forEach((s) => {
                const cc = String(s?.country_code || '').trim().toUpperCase();
                if (cc.length === 2 && /^[A-Z]{2}$/.test(cc)) {
                  countryCounts.set(cc, (countryCounts.get(cc) || 0) + 1);
                }
              });
              const availableCountryCodes = Array.from(countryCounts.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([code, count]) => ({ code, count }));
              const filteredByEvents =
                selected.length === 0
                  ? (userJourneySessions || [])
                  : (userJourneySessions || []).filter((s) => {
                      const sessionEvents = getSessionEventTypes(s);
                      return selected.some((ev) => sessionEvents.includes(ev));
                    });
              const filteredByCountry =
                !countryScope
                  ? filteredByEvents
                  : filteredByEvents.filter((s) =>
                      String(s?.country_code || '').trim().toUpperCase() === countryScope
                    );
              const filteredByLanding =
                !landingScope
                  ? filteredByCountry
                  : filteredByCountry.filter((s) =>
                      String(s?.landing_url || '')
                        .trim()
                        .toLowerCase()
                        .includes(landingScope)
                    );
              const filteredByEmail =
                !emailScope
                  ? filteredByLanding
                  : filteredByLanding.filter((s) =>
                      String(s?.email || '')
                        .trim()
                        .toLowerCase()
                        .includes(emailScope)
                    );
              const minEventThreshold = Number(userJourneyMinEventCount);
              const filteredSessions = filteredByEmail.filter(
                (s) => (s.event_count ?? 0) > minEventThreshold
              );
              const toggleFilterOption = (name) => {
                setUserJourneySessionFilter((prev) =>
                  prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
                );
              };
              const clearFilter = () => {
                setUserJourneySessionFilter([]);
                setUserJourneyFilterDropdownOpen(false);
              };
              return (
                <>
                  <div className="timeline-event-filter user-journey-session-filter">
                    <div className="filter-row">
                      <div className="user-journey-filter-inline-group">
                        <span className="filter-label">Filter sessions by event type:</span>
                        <div className="filter-multiselect-wrap">
                          <button
                            type="button"
                            className="filter-select filter-multiselect-trigger"
                            onClick={() => setUserJourneyFilterDropdownOpen((o) => !o)}
                            aria-expanded={userJourneyFilterDropdownOpen}
                            aria-haspopup="listbox"
                          >
                            {selected.length === 0 ? 'All event types' : `${selected.length} selected`}
                            <span className="filter-multiselect-arrow">{userJourneyFilterDropdownOpen ? ' ▲' : ' ▼'}</span>
                          </button>
                          {userJourneyFilterDropdownOpen && (
                            <>
                              <div
                                className="filter-multiselect-backdrop"
                                onClick={() => setUserJourneyFilterDropdownOpen(false)}
                                aria-hidden="true"
                              />
                              <div className="filter-multiselect-dropdown" role="listbox">
                                <div className="filter-multiselect-actions">
                                  <button type="button" className="filter-multiselect-clear" onClick={clearFilter}>
                                    Clear
                                  </button>
                                </div>
                                {USER_JOURNEY_EVENT_TYPES.map((name) => (
                                  <label key={name} className="filter-multiselect-option" role="option">
                                    <input
                                      type="checkbox"
                                      checked={selected.includes(name)}
                                      onChange={() => toggleFilterOption(name)}
                                    />
                                    <span>{name}</span>
                                  </label>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="user-journey-filter-inline-group user-journey-email-search-row">
                        <label className="filter-label" htmlFor="user-journey-country-filter">
                          Country:
                        </label>
                        <select
                          id="user-journey-country-filter"
                          className="filter-select user-journey-country-select"
                          value={userJourneyCountryScope}
                          onChange={(e) => setUserJourneyCountryScope(e.target.value)}
                        >
                          <option value="">All countries</option>
                          {availableCountryCodes.map(({ code, count }) => (
                            <option key={code} value={code}>
                              {`${code} (${count})`}
                            </option>
                          ))}
                        </select>
                        {userJourneyCountryScope && (
                          <button
                            type="button"
                            className="filter-multiselect-clear"
                            onClick={() => setUserJourneyCountryScope('')}
                            title="Clear country filter"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="user-journey-filter-inline-group user-journey-email-search-row">
                        <label className="filter-label" htmlFor="user-journey-landing-search">
                          Landing URL:
                        </label>
                        <div className="user-journey-input-wrap">
                          <input
                            id="user-journey-landing-search"
                            type="text"
                            className="filter-input user-journey-email-search-input user-journey-clearable-input"
                            placeholder="Raw search"
                            value={userJourneyLandingScope}
                            onChange={(e) => setUserJourneyLandingScope(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                          />
                          {userJourneyLandingScope.trim() && (
                            <button
                              type="button"
                              className="user-journey-inline-clear-btn"
                              onClick={() => setUserJourneyLandingScope('')}
                              title="Clear landing URL search"
                              aria-label="Clear landing URL search"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="user-journey-filter-inline-group user-journey-email-search-row">
                        <label className="filter-label" htmlFor="user-journey-email-search">
                          Email search:
                        </label>
                        <div className="user-journey-input-wrap">
                          <input
                            id="user-journey-email-search"
                            type="text"
                            className="filter-input user-journey-email-search-input user-journey-clearable-input"
                            placeholder="Filter sessions"
                            value={userJourneyEmailScope}
                            onChange={(e) => setUserJourneyEmailScope(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                          />
                          {userJourneyEmailScope.trim() && (
                            <button
                              type="button"
                              className="user-journey-inline-clear-btn"
                              onClick={() => setUserJourneyEmailScope('')}
                              title="Clear email search"
                              aria-label="Clear email search"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {minEventThreshold > 1 && (userJourneySessions || []).length > 0 && (
                      <span className="filter-hint">
                        Min events &gt; {minEventThreshold}: {filteredSessions.length} of {filteredByEmail.length} sessions
                      </span>
                    )}
                    {emailScope && (userJourneySessions || []).length > 0 && (
                      <span className="filter-hint">
                        Email search: {filteredByEmail.length} of {filteredByLanding.length} sessions
                        {selected.length > 0 ? ' (after event-type filter)' : ''}
                      </span>
                    )}
                    {countryScope && (userJourneySessions || []).length > 0 && (
                      <span className="filter-hint">
                        Country filter: {filteredByCountry.length} of {filteredByEvents.length} sessions ({countryScope})
                      </span>
                    )}
                    {landingScope && (userJourneySessions || []).length > 0 && (
                      <span className="filter-hint">
                        Landing URL search: {filteredByLanding.length} of {filteredByCountry.length} sessions
                      </span>
                    )}
                    {selected.length > 0 && (userJourneySessions || []).length > 0 && (
                      <span className="filter-hint">
                        Showing sessions that contain any of: {selected.join(', ')} — {filteredByEvents.length} of {userJourneySessions.length} sessions
                      </span>
                    )}
                  </div>
                  <div className="user-journey-container">
                    {/* Left side - Session List */}
                    <div className="user-journey-sessions">
                      <div className="sessions-header-row">
                        <h3>Sessions ({filteredSessions.length})</h3>
                        <div className="sessions-header-actions">
                          <button
                            type="button"
                            className="btn-small"
                            disabled={filteredSessions.length === 0}
                            onClick={() => {
                              const allKeys = new Set(
                                filteredSessions.map((s) => s.session_key || `${s.source_date || ''}::${s.session_id}`)
                              );
                              setSelectedJourneySessionKeys(allKeys);
                            }}
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="btn-small"
                            disabled={selectedJourneySessionKeys.size === 0}
                            onClick={() => setSelectedJourneySessionKeys(new Set())}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="btn-small btn-danger"
                            disabled={selectedJourneySessionKeys.size === 0 || batchDeletingJourneySessions}
                            onClick={() => deleteSelectedUserJourneySessions(filteredSessions)}
                            title="Delete selected sessions"
                          >
                            {batchDeletingJourneySessions ? 'Deleting…' : `Delete selected (${selectedJourneySessionKeys.size})`}
                          </button>
                        </div>
                      </div>
                      {loadingUserJourney ? (
                        <div className="loading-state">Loading sessions...</div>
                      ) : filteredSessions.length === 0 ? (
                        <div className="empty-state">
                          {userJourneySessions.length === 0 ? 'No sessions found in this date range' : 'No sessions match the filter'}
                        </div>
                      ) : (
                        <div className="sessions-list">
                          {filteredSessions.map((session) => {
                            const isDeleting = deletingSessionId === session.session_id;
                            const sessionKey = session.session_key || `${session.source_date || ''}::${session.session_id}`;
                            const isChecked = selectedJourneySessionKeys.has(sessionKey);
                            return (
                              <div
                                key={sessionKey}
                                className={`session-item ${selectedSessionId === session.session_id && selectedSessionDate === (session.source_date || null) ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedSessionId(session.session_id);
                                  setSelectedSessionDate(session.source_date || null);
                                }}
                              >
                                <div className="session-select-wrap" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const nextChecked = e.target.checked;
                                      setSelectedJourneySessionKeys((prev) => {
                                        const next = new Set(prev);
                                        if (nextChecked) next.add(sessionKey);
                                        else next.delete(sessionKey);
                                        return next;
                                      });
                                    }}
                                    aria-label="Select session for batch delete"
                                  />
                                </div>
                                <div className="session-item-main">
                                  <div className="session-header">
                                    <span className="session-id">
                                      {session.email && session.email.trim()
                                        ? session.email
                                        : session.session_id.substring(0, 20) + '...'}
                                    </span>
                                  </div>
                                  <div className="session-time">
                                    {session.first_event_time && new Date(session.first_event_time).toLocaleString()}
                                  </div>
                                  <div className="session-time">
                                    File date: {session.source_date || 'N/A'}
                                  </div>
                                  {(session.country_code || session.landing_url) && (
                                    <div className="session-time">
                                      {session.country_code ? `Country: ${String(session.country_code).toUpperCase()}` : 'Country: N/A'}
                                      {session.landing_url ? ` · Landing: ${String(session.landing_url).slice(0, 120)}` : ''}
                                    </div>
                                  )}
                                </div>
                                <div className="session-item-actions">
                                  <span className="session-count">{session.event_count} events</span>
                                  {session.email && session.email.trim() && (
                                    <button
                                      type="button"
                                      className="session-user-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openUsersForJourneySession(session);
                                      }}
                                      title={`Open Users tab filtered by ${session.email.trim()}`}
                                    >
                                      User
                                    </button>
                                  )}
                                  {sessionHasGenerateShortsClick(session) && (
                                    <button
                                      type="button"
                                      className="session-tasks-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openTasksForJourneySession(session);
                                      }}
                                      title={
                                        session.email && session.email.trim()
                                          ? 'Open Tasks tab: this email + date range from journey file day and first/last event'
                                          : 'Open Tasks tab: date range from journey file day and first/last event (no email on session)'
                                      }
                                    >
                                      Tasks
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="session-delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteUserJourneySession(session.session_id, session.source_date);
                                    }}
                                    disabled={isDeleting}
                                    title="Delete this session"
                                  >
                                    {isDeleting ? '…' : '🗑'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

              {/* Right side - Timeline */}
              <div className="user-journey-timeline">
                {!selectedSessionId ? (
                  <div className="empty-state">Select a session to view timeline</div>
                ) : loadingTimeline ? (
                  <div className="loading-state">Loading timeline...</div>
                ) : userJourneyTimeline ? (
                  <div className="timeline-content">
                    {(() => {
                      const SESSION_ATTR_NAMES = new Set(['Session Attribution', 'JOURNEY_SESSION_ATTRIBUTION']);
                      const TIMELINE_HIDDEN_EVENT_NAMES = new Set([
                        'Session Attribution',
                        'JOURNEY_SESSION_ATTRIBUTION',
                        'Visitor Country',
                        'VISITOR_COUNTRY',
                      ]);
                      /** Merged into single "Landing URL" row (path + query). */
                      const ATTR_MERGED_INTO_LANDING_URL = new Set([
                        'utm_source',
                        'utm_medium',
                        'utm_campaign',
                        'utm_term',
                        'utm_content',
                        'gclid',
                        'fbclid',
                        'ref',
                        'referral',
                        'landing_path',
                        'landing_search',
                        'landing_url',
                      ]);
                      const ATTR_QUERY_PARAM_KEYS = [
                        'utm_source',
                        'utm_medium',
                        'utm_campaign',
                        'utm_term',
                        'utm_content',
                        'gclid',
                        'fbclid',
                        'ref',
                        'referral',
                      ];

                      const buildSessionLandingUrlDisplay = (ev) => {
                        if (!ev) {
                          return '';
                        }
                        const full = ev.landing_url != null ? String(ev.landing_url).trim() : '';
                        if (full) {
                          return full;
                        }
                        const path = (ev.landing_path && String(ev.landing_path).trim()) || '/';
                        const searchRaw = ev.landing_search != null ? String(ev.landing_search).trim() : '';
                        if (searchRaw) {
                          const qs = searchRaw.startsWith('?') ? searchRaw : `?${searchRaw}`;
                          return path + qs;
                        }
                        const params = new URLSearchParams();
                        for (const k of ATTR_QUERY_PARAM_KEYS) {
                          const v = ev[k];
                          if (v != null && String(v).trim() !== '') {
                            params.set(k, String(v).trim());
                          }
                        }
                        const q = params.toString();
                        return q ? `${path}?${q}` : path;
                      };

                      const rawEvents = userJourneyTimeline.events || [];
                      const attrEvent = rawEvents.find((e) => e && SESSION_ATTR_NAMES.has(e.name));
                      const visitorCountryEvent = rawEvents.find(
                        (e) => e && (e.name === 'Visitor Country' || e.name === 'VISITOR_COUNTRY')
                      );
                      const visitorCountryCodeRaw =
                        visitorCountryEvent && visitorCountryEvent.country_code != null
                          ? String(visitorCountryEvent.country_code).trim().toUpperCase()
                          : '';
                      const visitorCountryCode =
                        visitorCountryCodeRaw.length === 2 && /^[A-Z]{2}$/.test(visitorCountryCodeRaw)
                          ? visitorCountryCodeRaw
                          : null;
                      const timelineEvents = rawEvents.filter(
                        (e) => e && !TIMELINE_HIDDEN_EVENT_NAMES.has(e.name)
                      );

                      const attributionRows = [];
                      if (attrEvent) {
                        const landingUrlDisplay = buildSessionLandingUrlDisplay(attrEvent);
                        if (landingUrlDisplay) {
                          attributionRows.push({
                            key: 'landing_url',
                            label: 'Landing URL',
                            value: landingUrlDisplay,
                            isUrl: true,
                          });
                        }
                        const refUrl =
                          attrEvent.referrer_url != null ? String(attrEvent.referrer_url).trim() : '';
                        if (refUrl) {
                          attributionRows.push({
                            key: 'referrer_url',
                            label: 'Referrer',
                            value: refUrl,
                            isUrl: true,
                          });
                        }
                        for (const k of Object.keys(attrEvent)) {
                          if (k === 'type' || k === 'name' || k === 'timestamp') {
                            continue;
                          }
                          if (ATTR_MERGED_INTO_LANDING_URL.has(k) || k === 'landing_url') {
                            continue;
                          }
                          const v = attrEvent[k];
                          if (v != null && String(v).trim() !== '') {
                            attributionRows.push({
                              key: k,
                              label: k,
                              value: String(v).trim(),
                              isUrl: /^https?:\/\//i.test(String(v).trim()),
                            });
                          }
                        }
                        if (attributionRows.length === 0) {
                          attributionRows.push({
                            key: 'note',
                            label: 'Note',
                            value: 'No UTM, referrer, or landing params stored on this event',
                            isUrl: false,
                          });
                        }
                      }
                      return (
                        <>
                    <div className="timeline-header">
                      <div className="timeline-header-top">
                      <div className="timeline-header-title-row">
                        <h3>Session Timeline</h3>
                        {visitorCountryCode ? (
                          <span
                            className="timeline-country-pill"
                            title="Visitor country (geo hint from /api/visitor-country)"
                          >
                            {visitorCountryCode}
                          </span>
                        ) : null}
                      </div>
                      <div className="timeline-meta timeline-meta-compact">
                        <span className="timeline-meta-pill">
                          {userJourneyTimeline.email && String(userJourneyTimeline.email).trim()
                            ? `Email: ${userJourneyTimeline.email}` 
                            : `Session ID: ${userJourneyTimeline.session_id ? userJourneyTimeline.session_id.substring(0, 30) + '...' : 'N/A'}`}
                        </span>
                        <span className="timeline-meta-pill">Date: {selectedSessionDate || userJourneyTimeline.date}</span>
                        <span className="timeline-meta-pill">Events: {userJourneyTimeline.event_count}</span>
                      </div>
                      </div>
                      {attributionRows.length > 0 && (
                        <div className="timeline-session-attribution">
                          <dl className="timeline-session-attribution-grid">
                            {attributionRows.map((row) => (
                              <React.Fragment key={row.key}>
                                <dt>{row.label}</dt>
                                <dd
                                  className={row.isUrl ? 'timeline-session-attribution-url' : undefined}
                                  title={row.value}
                                >
                                  {row.isUrl && /^https?:\/\//i.test(row.value) ? (
                                    <a
                                      href={row.value}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="timeline-session-attribution-link"
                                    >
                                      {row.value}
                                    </a>
                                  ) : (
                                    row.value
                                  )}
                                </dd>
                              </React.Fragment>
                            ))}
                          </dl>
                        </div>
                      )}
                    </div>
                    <div className="timeline-events">
                      {timelineEvents.map((event, visibleIndex) => {
                        const rawIdx = rawEvents.indexOf(event);
                        const prevRaw = rawIdx > 0 ? rawEvents[rawIdx - 1] : null;
                        const previousTime = prevRaw ? new Date(prevRaw.timestamp).getTime() : null;
                        const currentTime = new Date(event.timestamp).getTime();
                        const timeDiff = previousTime ? currentTime - previousTime : null;
                        const eventDate = new Date(event.timestamp);
                        const eventDateTime = Number.isNaN(eventDate.getTime())
                          ? 'Invalid time'
                          : eventDate.toLocaleString([], {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            });
                        const isGreenActionName = new Set([
                          'SUBSCRIBE_NOW_CLICKED',
                          'Subscribe Now Clicked',
                          'CANCEL_SUBSCRIPTION_WHATSAPP_CLICKED',
                          'Cancel Subscription (WhatsApp) Clicked',
                          'DOWNLOAD',
                          'Download',
                          'SHARE_VIDEO',
                          'Share Video',
                          'UPLOAD_BUTTON',
                          'Upload_Button',
                          'UPLOAD_ANOTHER_VIDEO_CLICKED',
                          'Upload Another Video Clicked',
                          'VIDEO_SELECTED_TO_UPLOAD',
                          'Video selected to upload',
                          'VIDEO_URL_ADDED',
                          'Video Url Added',
                        ]).has(event.name);
                        const formatTimeDiff = (diffMs) => {
                          const seconds = Math.floor(diffMs / 1000);
                          const minutes = Math.floor(seconds / 60);
                          const hours = Math.floor(minutes / 60);
                          if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
                          if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
                          return `${seconds}s`;
                        };
                        const trimTimelineMeta = (v) =>
                          v != null && String(v).trim() !== '' ? String(v).trim() : null;
                        const eventSlug = trimTimelineMeta(event.slug);
                        const eventRegion = trimTimelineMeta(event.region);
                        const eventPriceText = trimTimelineMeta(event.price_text);
                        const eventFaqQuestion = trimTimelineMeta(event.faq_question);
                        const eventStreakRange = formatStreakOverlayRange(event);
                        const timelineRowTitle = [
                          event.name,
                          eventStreakRange,
                          eventSlug,
                          eventRegion,
                          eventPriceText,
                          eventFaqQuestion,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <div key={`${rawIdx}-${event.timestamp}`} className="timeline-event-wrapper">
                            {visibleIndex > 0 && timeDiff !== null && (
                              <div className="timeline-connector">
                                <div className="timeline-line"></div>
                                <div className="timeline-diff">{formatTimeDiff(timeDiff)}</div>
                              </div>
                            )}
                            <div className="timeline-event">
                              <span
                                className={`event-type-badge ${
                                  isGreenActionName
                                    ? 'event-type-badge-success'
                                    : (event.type === 'button' ? 'event-type-badge-button' : 'event-type-badge-event')
                                }`}
                              >
                                {event.type === 'button' ? 'Button' : 'Event'}
                              </span>
                              <div
                                className={`event-name-row ${isGreenActionName ? 'event-name-row-success' : ''}`}
                                title={timelineRowTitle}
                              >
                                <span className={`event-name ${isGreenActionName ? 'event-name-success' : ''}`}>
                                  {event.name}
                                </span>
                                {eventSlug ? (
                                  <span className="event-slug-chip" title={`slug: ${eventSlug}`}>
                                    {eventSlug}
                                  </span>
                                ) : null}
                                {eventRegion ? (
                                  <span className="event-region-chip" title={`region: ${eventRegion}`}>
                                    {eventRegion}
                                  </span>
                                ) : null}
                                {eventPriceText ? (
                                  <span className="event-price-chip" title={`price: ${eventPriceText}`}>
                                    {eventPriceText}
                                  </span>
                                ) : null}
                                {eventFaqQuestion ? (
                                  <span className="event-faq-chip" title={`FAQ: ${eventFaqQuestion}`}>
                                    {eventFaqQuestion}
                                  </span>
                                ) : null}
                                {eventStreakRange ? (
                                  <span
                                    className="event-streak-chip"
                                    title={`streak: ${eventStreakRange}`}
                                  >
                                    {eventStreakRange}
                                  </span>
                                ) : null}
                              </div>
                              <span className="event-datetime" title={event.timestamp}>{eventDateTime}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="empty-state">Failed to load timeline</div>
                )}
              </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'youtube-cookies' && (
          <div className="admin-content">
            <div className="section-header">
              <h2>YouTube Cookie Management</h2>
              <button onClick={loadCookies} className="btn-refresh" disabled={loadingCookies}>
                {loadingCookies ? 'Loading...' : '🔄 Refresh'}
              </button>
            </div>

            {/* Add New Cookie */}
            <div className="cookie-section">
              <h3>Add New Cookie</h3>
              <div className="cookie-form">
                <input
                  type="text"
                  placeholder="Cookie name (e.g., account1)"
                  value={newCookieName}
                  onChange={(e) => setNewCookieName(e.target.value)}
                  className="cookie-input"
                />
                <textarea
                  placeholder="Paste cookie content here (Netscape format)"
                  value={newCookieContent}
                  onChange={(e) => setNewCookieContent(e.target.value)}
                  className="cookie-textarea"
                  rows="10"
                />
                <button onClick={addCookie} className="btn-primary">
                  ➕ Add Cookie
                </button>
              </div>
            </div>

            {/* Cookie List */}
            <div className="cookie-section">
              <h3>Cookies ({cookies.length})</h3>
              <div className="cookie-stats">
                <span>Active: {cookies.filter(c => c.status === 'active').length}</span>
                <span>Expired: {cookies.filter(c => c.status === 'expired').length}</span>
                <span>Failed: {cookies.filter(c => c.status === 'failed').length}</span>
              </div>
              
              {loadingCookies ? (
                <div className="loading-state">Loading cookies...</div>
              ) : cookies.length === 0 ? (
                <div className="empty-state">No cookies found. Add your first cookie above.</div>
              ) : (
                <div className="cookies-list">
                  {cookies.map((cookie) => (
                    <div key={cookie.name} className={`cookie-item ${cookie.status}`}>
                      <div className="cookie-header">
                        <div className="cookie-info">
                          <h4>{cookie.name}</h4>
                          <span className={`status-badge ${cookie.status}`}>
                            {cookie.status}
                          </span>
                        </div>
                        <div className="cookie-actions">
                          <button
                            onClick={() => checkCookie(cookie.name)}
                            className="btn-small"
                            title="Check if cookie is valid"
                          >
                            ✓ Check
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const freshToken = await getFreshTokenRef.current();
                                const response = await fetch(`/api/admin/youtube/cookies/${encodeURIComponent(cookie.name)}`, {
                                  headers: {
                                    'Authorization': `Bearer ${freshToken}`,
                                    'Content-Type': 'application/json',
                                  },
                                });
                                const data = await response.json();
                                if (response.ok) {
                                  setEditingCookie({ name: cookie.name, content: data.content });
                                } else {
                                  alert(`Error loading cookie: ${data.error}`);
                                }
                              } catch (err) {
                                alert(`Error: ${err.message}`);
                              }
                            }}
                            className="btn-small"
                            title="Edit cookie"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => deleteCookie(cookie.name)}
                            className="btn-small btn-danger"
                            title="Delete cookie"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                      <div className="cookie-details">
                        <p><strong>Created:</strong> {cookie.created_at || 'Unknown'}</p>
                        <p><strong>Times Used:</strong> {cookie.times_used || 0}</p>
                        <p><strong>Last Used:</strong> {cookie.last_used || 'Never'}</p>
                        <p><strong>Last Checked:</strong> {cookie.last_checked || 'Never'}</p>
                        {cookie.expires_at && (
                          <p><strong>Expires At:</strong> {
                            (() => {
                              try {
                                const date = new Date(cookie.expires_at);
                                return isNaN(date.getTime()) ? cookie.expires_at : date.toLocaleString();
                              } catch {
                                return cookie.expires_at;
                              }
                            })()
                          }</p>
                        )}
                        {cookie.status === 'expired' && cookie.valid_for && (
                          <p><strong>Valid For:</strong> {cookie.valid_for}</p>
                        )}
                        {cookie.failure_count > 0 && (
                          <p><strong>Failures:</strong> {cookie.failure_count}</p>
                        )}
                        {cookie.last_error && (
                          <p className="error-text"><strong>Last Error:</strong> {cookie.last_error}</p>
                        )}
                        <p><strong>Size:</strong> {(cookie.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Edit Cookie Modal */}
            {editingCookie && (
              <div className="modal-overlay" onClick={() => setEditingCookie(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <h3>Edit Cookie: {editingCookie.name}</h3>
                  <textarea
                    value={editingCookie.content}
                    onChange={(e) => setEditingCookie({ ...editingCookie, content: e.target.value })}
                    className="cookie-textarea"
                    rows="15"
                  />
                  <div className="modal-actions">
                    <button
                      onClick={() => updateCookie(editingCookie.name, editingCookie.content)}
                      className="btn-primary"
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={() => setEditingCookie(null)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'marketing' && (
          <div className="admin-content">
            <div className="section-header">
              <h2 style={{ fontSize: '18px', margin: 0 }}>Marketing Dashboard</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }} className="marketing-header-actions">
                <div style={{ display: 'flex', border: '1px solid #ccc', borderRadius: '6px', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (marketingChannel !== 'whatsapp') {
                        setSelectedUserIds(new Set());
                        setMarketingChannel('whatsapp');
                      }
                    }}
                    style={{
                      fontSize: '12px',
                      padding: '6px 12px',
                      border: 'none',
                      cursor: 'pointer',
                      background: marketingChannel === 'whatsapp' ? '#2563eb' : '#f3f4f6',
                      color: marketingChannel === 'whatsapp' ? '#fff' : '#374151',
                    }}
                  >
                    WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (marketingChannel !== 'web_push') {
                        setSelectedUserIds(new Set());
                        setMarketingChannel('web_push');
                      }
                    }}
                    style={{
                      fontSize: '12px',
                      padding: '6px 12px',
                      border: 'none',
                      cursor: 'pointer',
                      background: marketingChannel === 'web_push' ? '#2563eb' : '#f3f4f6',
                      color: marketingChannel === 'web_push' ? '#fff' : '#374151',
                    }}
                  >
                    Web Push
                  </button>
                </div>
                <button 
                  onClick={() => loadMarketingUsers(marketingChannel, { forceRefresh: true })}
                  className="btn-refresh" 
                  disabled={loadingMarketingUsers}
                  style={{ fontSize: '12px', padding: '6px 12px', height: 'auto', minHeight: '28px' }}
                  title="Re-fetch eligible users for this channel only (not all users)"
                >
                  {loadingMarketingUsers ? 'Loading...' : '🔄 Refresh'}
                </button>
                <button 
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateForm(
                      marketingChannel === 'web_push' ? emptyWebPushTemplateForm() : emptyWhatsappTemplateForm()
                    );
                    setShowTemplateModal(true);
                  }}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '6px 12px', height: 'auto', minHeight: '28px' }}
                >
                  ➕ {marketingChannel === 'web_push' ? 'Web Push Template' : 'WhatsApp Template'}
                </button>
              </div>
            </div>

            {/* Filters Section */}
            <div className="cookie-section">
              <h3 style={{ marginBottom: '10px', fontSize: '16px' }}>Filters</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                {/* Last Active Filter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500' }}>Last Active</label>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <select
                      value={marketingFilters.last_active.operator}
                      onChange={(e) => setMarketingFilters(prev => ({
                        ...prev,
                        last_active: { ...prev.last_active, operator: e.target.value, days: e.target.value === 'none' ? '' : prev.last_active.days }
                      }))}
                      style={{ width: '100px', fontSize: '12px', padding: '4px' }}
                    >
                      <option value="none">None</option>
                      <option value=">">More than</option>
                      <option value="<">Less than</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Days"
                      value={marketingFilters.last_active.days}
                      onChange={(e) => setMarketingFilters(prev => ({
                        ...prev,
                        last_active: { ...prev.last_active, days: e.target.value }
                      }))}
                      disabled={marketingFilters.last_active.operator === 'none'}
                      style={{ width: '60px', fontSize: '12px', padding: '4px' }}
                      min="0"
                    />
                    <span style={{ fontSize: '11px', color: '#666' }}>days</span>
                  </div>
                </div>

                {/* Last Marketing Message Filter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500' }}>Last Marketing</label>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <select
                      value={marketingFilters.last_marketing.operator}
                      onChange={(e) => setMarketingFilters(prev => ({
                        ...prev,
                        last_marketing: { ...prev.last_marketing, operator: e.target.value, days: e.target.value === 'none' ? '' : prev.last_marketing.days }
                      }))}
                      style={{ width: '100px', fontSize: '12px', padding: '4px' }}
                    >
                      <option value="none">None</option>
                      <option value=">">More than</option>
                      <option value="<">Less than</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Days"
                      value={marketingFilters.last_marketing.days}
                      onChange={(e) => setMarketingFilters(prev => ({
                        ...prev,
                        last_marketing: { ...prev.last_marketing, days: e.target.value }
                      }))}
                      disabled={marketingFilters.last_marketing.operator === 'none'}
                      style={{ width: '60px', fontSize: '12px', padding: '4px' }}
                      min="0"
                    />
                    <span style={{ fontSize: '11px', color: '#666' }}>days</span>
                  </div>
                </div>

                {/* Last Template Filter - Multi-select */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500' }}>Last Template</label>
                  <select
                    multiple
                    value={marketingFilters.last_template}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setMarketingFilters(prev => ({
                        ...prev,
                        last_template: selected
                      }));
                    }}
                    style={{ width: '100%', fontSize: '12px', padding: '4px', minHeight: '60px' }}
                    size={3}
                  >
                    <option value="all">All</option>
                    {allTemplateNames.map(template => (
                      <option key={template} value={template}>{template}</option>
                    ))}
                  </select>
                </div>

                {/* Message Count Filter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500' }}>Total Messages</label>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <select
                      value={marketingFilters.message_count.operator}
                      onChange={(e) => setMarketingFilters(prev => ({
                        ...prev,
                        message_count: { ...prev.message_count, operator: e.target.value, value: e.target.value === 'none' ? '' : prev.message_count.value }
                      }))}
                      style={{ width: '100px', fontSize: '12px', padding: '4px' }}
                    >
                      <option value="none">None</option>
                      <option value=">">More than</option>
                      <option value="<">Less than</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Count"
                      value={marketingFilters.message_count.value}
                      onChange={(e) => setMarketingFilters(prev => ({
                        ...prev,
                        message_count: { ...prev.message_count, value: e.target.value }
                      }))}
                      disabled={marketingFilters.message_count.operator === 'none'}
                      style={{ width: '70px', fontSize: '12px', padding: '4px' }}
                      min="0"
                    />
                  </div>
                </div>

                {/* Name Filter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500' }}>Name</label>
                  <input
                    type="text"
                    placeholder="Search name..."
                    value={marketingFilters.name}
                    onChange={(e) => setMarketingFilters(prev => ({
                      ...prev,
                      name: e.target.value
                    }))}
                    style={{ width: '100%', fontSize: '12px', padding: '4px' }}
                  />
                </div>

                {/* Email Filter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500' }}>Email</label>
                  <input
                    type="text"
                    placeholder="Search email..."
                    value={marketingFilters.email}
                    onChange={(e) => setMarketingFilters(prev => ({
                      ...prev,
                      email: e.target.value
                    }))}
                    style={{ width: '100%', fontSize: '12px', padding: '4px' }}
                  />
                </div>

                {/* Premium filter (same options as Firestore → Users) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label htmlFor="marketing-filter-premium" style={{ fontSize: '12px', fontWeight: '500' }}>Premium</label>
                  <select
                    id="marketing-filter-premium"
                    value={
                      marketingFilters.premiumFilter === 'subscription'
                        ? 'active'
                        : marketingFilters.premiumFilter || 'all'
                    }
                    onChange={(e) => setMarketingFilters(prev => ({
                      ...prev,
                      premiumFilter: e.target.value
                    }))}
                    style={{ width: '100%', fontSize: '12px', padding: '4px' }}
                  >
                    <option value="all">All users</option>
                    <option value="premium">Premium + Coupon</option>
                    <option value="any-subscription">Any Subscription</option>
                    <option value="coupon">Coupon</option>
                    <option value="active">active</option>
                    <option value="cancel-at-period-end">Active until cancelled</option>
                    <option value="halted">halted</option>
                    <option value="pending">pending</option>
                    <option value="paused">paused</option>
                    <option value="created">created</option>
                    <option value="cancelled">cancelled</option>
                    <option value="expired">expired</option>
                  </select>
                </div>

                {/* Created Date Range Filter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '500' }}>Created Date Range</label>
                  <DatePicker
                    selected={marketingFilters.created_range[0]}
                    onChange={(dates) => {
                      const [start, end] = dates || [null, null];
                      setMarketingFilters(prev => ({
                        ...prev,
                        created_range: [start, end]
                      }));
                    }}
                    startDate={marketingFilters.created_range[0]}
                    endDate={marketingFilters.created_range[1]}
                    selectsRange
                    isClearable
                    placeholderText="Select date range"
                    className="filter-input"
                    dateFormat="yyyy-MM-dd"
                    style={{ width: '100%', fontSize: '12px', padding: '4px' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                <button 
                  onClick={() => applyMarketingFilters()} 
                  className="btn-primary" 
                  style={{ 
                    fontSize: '12px', 
                    padding: '6px 12px',
                    height: 'auto',
                    minHeight: '28px'
                  }}
                >
                  Apply Filters
                </button>
                <button 
                  onClick={() => {
                    clearMarketingFilters();
                    // Auto-apply after clearing (filters are cleared, so show all users)
                    setTimeout(() => applyMarketingFilters(), 100);
                  }}
                  className="btn-small"
                  style={{ 
                    fontSize: '12px', 
                    padding: '6px 12px',
                    height: 'auto',
                    minHeight: '28px'
                  }}
                >
                  🔄 Clear Filters
                </button>
              </div>
            </div>

            {/* User List Section */}
            <div className="cookie-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ fontSize: '16px', margin: 0 }}>Users ({marketingUsers.length}) - Selected: {selectedUserIds.size}</h3>
                <button 
                  onClick={selectAllUsers} 
                  className="btn-small"
                  style={{ fontSize: '12px', padding: '6px 12px', height: 'auto', minHeight: '28px' }}
                >
                  {selectedUserIds.size === marketingUsers.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              {selectedUserIds.size > 0 && (
                <div style={{ marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: '12px' }}>Template:</label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    style={{ flex: 1, maxWidth: '300px', fontSize: '12px', padding: '4px' }}
                  >
                    <option value="">Select template...</option>
                    {marketingTemplates.map(t => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.template_name}
                        {marketingChannel === 'whatsapp' && t.send_first_name ? ' (with name)' : ''}
                        {marketingChannel === 'web_push' && t.push_title ? ` — ${t.push_title}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      if (!selectedTemplateId) {
                        alert('Please select a template first');
                        return;
                      }
                      if (selectedUserIds.size === 0) {
                        alert('Please select at least one user');
                        return;
                      }
                      const channelLabel = marketingChannel === 'web_push' ? 'Web Push' : 'WhatsApp';
                      if (confirm(`Send ${channelLabel} to ${selectedUserIds.size} selected user(s)?`)) {
                        sendMarketingMessages(selectedTemplateId);
                      }
                    }}
                    disabled={!selectedTemplateId || sendingMessages || selectedUserIds.size === 0}
                    className="btn-primary"
                    style={{ 
                      fontSize: '12px', 
                      padding: '6px 12px', 
                      height: 'auto', 
                      minHeight: '28px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {sendingMessages ? 'Sending...' : '📤 Send'}
                  </button>
                </div>
              )}
              
              {loadingMarketingUsers ? (
                <div className="loading-state">Loading users...</div>
              ) : marketingUsers.length === 0 ? (
                <div className="empty-state">No users found. Adjust filters or refresh.</div>
              ) : (
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px' }}>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.size === getSortedMarketingUsers().length && getSortedMarketingUsers().length > 0}
                            onChange={selectAllUsers}
                            style={{ width: '14px', height: '14px' }}
                          />
                        </th>
                        <th
                          style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleMarketingSort('subscription_status')}
                        >
                          Subscription {sortConfig.field === 'subscription_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleMarketingSort('email')}
                        >
                          Email {sortConfig.field === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleMarketingSort('name')}
                        >
                          Name {sortConfig.field === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        {marketingChannel === 'whatsapp' ? (
                          <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px' }}>Phone</th>
                        ) : (
                          <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px' }}>Push</th>
                        )}
                        <th 
                          style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleMarketingSort('last_active')}
                        >
                          Last Active {sortConfig.field === 'last_active' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleMarketingSort(
                            marketingChannel === 'web_push' ? 'web_push_marketing_count' : 'message_template_sent_count'
                          )}
                        >
                          Msgs {sortConfig.field === (marketingChannel === 'web_push' ? 'web_push_marketing_count' : 'message_template_sent_count') && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', fontSize: '12px' }}>Last Template</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedMarketingUsers().map(user => {
                        const daysAgo = calculateDaysAgo(user.last_active);
                        return (
                          <tr key={user.user_id} style={{ backgroundColor: selectedUserIds.has(user.user_id) ? '#e3f2fd' : 'white' }}>
                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                              <input
                                type="checkbox"
                                checked={selectedUserIds.has(user.user_id)}
                                onChange={() => toggleUserSelection(user.user_id)}
                                style={{ width: '14px', height: '14px' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {user.subscription_status ? (
                                <span
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    textTransform: 'capitalize',
                                    ...getSubscriptionStatusBadgeStyle(user.subscription_status),
                                  }}
                                >
                                  {user.subscription_status}
                                </span>
                              ) : user.plan_tier === 'premium' ? (
                                <span
                                  title="Premium (coupon/trial — no Razorpay subscription_status)"
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    textTransform: 'capitalize',
                                    background: '#10b981',
                                    color: 'white',
                                  }}
                                >
                                  premium
                                </span>
                              ) : (
                                <span
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    textTransform: 'capitalize',
                                    color: '#6b7280',
                                  }}
                                >
                                  {user.plan_tier === 'free' ? 'free' : '—'}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px' }}>{user.email}</td>
                            <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px' }}>{user.name || 'N/A'}</td>
                            {marketingChannel === 'whatsapp' ? (
                              <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px' }}>{user.phone_number || 'N/A'}</td>
                            ) : (
                              <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px' }}>
                                {hasPushSubscription(user) ? 'Yes' : 'No'}
                              </td>
                            )}
                            <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px' }}>
                              {user.last_active ? (
                                <span>
                                  {daysAgo !== null ? `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago` : new Date(user.last_active).toLocaleString()}
                                </span>
                              ) : 'Never'}
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px' }}>
                              {marketingChannel === 'web_push'
                                ? (user.web_push_marketing_count || 0)
                                : (user.message_template_sent_count || 0)}
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #ddd', fontSize: '12px' }}>
                              {(() => {
                                const sent = marketingChannel === 'web_push'
                                  ? (user.web_push_marketing_sent || [])
                                  : (user.whatsapp_template_sent || []);
                                return sent.length > 0 ? sent[sent.length - 1] : 'None';
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Marketing Templates Section */}
            <div className="cookie-section">
              <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>
                {marketingChannel === 'web_push' ? 'Web Push' : 'WhatsApp'} Templates
              </h3>
              {loadingTemplates ? (
                <div className="loading-state">Loading templates...</div>
              ) : marketingTemplates.length === 0 ? (
                <div className="empty-state">No templates found. Create your first template above.</div>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {marketingTemplates.map(template => (
                    <div key={template.template_id} style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '5px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h4 style={{ fontSize: '14px', margin: '0 0 5px 0' }}>{template.template_name}</h4>
                          {marketingChannel === 'web_push' ? (
                            <>
                              <p style={{ fontSize: '12px', margin: '2px 0' }}>Title: <strong>{template.push_title}</strong></p>
                              <p style={{ fontSize: '12px', margin: '2px 0' }}>Body: {template.push_body}</p>
                              <p style={{ fontSize: '12px', margin: '2px 0' }}>URL: {template.push_url || '/'}</p>
                            </>
                          ) : (
                            <>
                              <p style={{ fontSize: '12px', margin: '2px 0' }}>WhatsApp: <strong>{template.whatsapp_template_name}</strong></p>
                              <p style={{ fontSize: '12px', margin: '2px 0' }}>First Name: {template.send_first_name ? 'Yes' : 'No'}</p>
                            </>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={() => {
                              setEditingTemplate(template);
                              if (marketingChannel === 'web_push') {
                                setTemplateForm({
                                  channel: 'web_push',
                                  template_name: template.template_name,
                                  push_title: template.push_title || '',
                                  push_body: template.push_body || '',
                                  push_url: template.push_url || '/',
                                  push_campaign: template.push_campaign || 'marketing',
                                });
                              } else {
                                setTemplateForm({
                                  channel: 'whatsapp',
                                  template_name: template.template_name,
                                  whatsapp_template_name: template.whatsapp_template_name,
                                  send_first_name: template.send_first_name,
                                });
                              }
                              setShowTemplateModal(true);
                            }}
                            className="btn-small"
                            style={{ fontSize: '12px', padding: '4px 8px', height: 'auto', minHeight: '24px' }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => deleteMarketingTemplate(template.template_id)}
                            className="btn-small btn-danger"
                            style={{ fontSize: '12px', padding: '4px 8px', height: 'auto', minHeight: '24px' }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Template Modal */}
            {showTemplateModal && (
              <div className="modal-overlay" onClick={() => {
                setShowTemplateModal(false);
                setEditingTemplate(null);
                setTemplateForm(
                  marketingChannel === 'web_push' ? emptyWebPushTemplateForm() : emptyWhatsappTemplateForm()
                );
              }}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <h3>
                    {editingTemplate ? 'Edit' : 'Create'}{' '}
                    {marketingChannel === 'web_push' ? 'Web Push' : 'WhatsApp'} Template
                  </h3>
                  <div style={{ marginTop: '20px' }}>
                    <label>
                      Template Name (internal label for tracking)
                      <input
                        type="text"
                        value={templateForm.template_name}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, template_name: e.target.value }))}
                        placeholder="e.g., Holiday promo"
                        style={{ width: '100%', marginTop: '5px', padding: '8px' }}
                      />
                    </label>
                    {marketingChannel === 'web_push' ? (
                      <>
                        <label style={{ marginTop: '15px', display: 'block' }}>
                          Notification title
                          <input
                            type="text"
                            value={templateForm.push_title || ''}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, push_title: e.target.value }))}
                            placeholder="e.g., Your clips are ready"
                            style={{ width: '100%', marginTop: '5px', padding: '8px' }}
                          />
                        </label>
                        <label style={{ marginTop: '15px', display: 'block' }}>
                          Notification body
                          <textarea
                            value={templateForm.push_body || ''}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, push_body: e.target.value }))}
                            placeholder="Tap to view your shorts"
                            rows={3}
                            style={{ width: '100%', marginTop: '5px', padding: '8px' }}
                          />
                        </label>
                        <label style={{ marginTop: '15px', display: 'block' }}>
                          Click URL (path)
                          <input
                            type="text"
                            value={templateForm.push_url || '/'}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, push_url: e.target.value }))}
                            placeholder="/"
                            style={{ width: '100%', marginTop: '5px', padding: '8px' }}
                          />
                        </label>
                        <label style={{ marginTop: '15px', display: 'block' }}>
                          UTM campaign
                          <input
                            type="text"
                            value={templateForm.push_campaign || 'marketing'}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, push_campaign: e.target.value }))}
                            placeholder="marketing"
                            style={{ width: '100%', marginTop: '5px', padding: '8px' }}
                          />
                        </label>
                      </>
                    ) : (
                      <>
                        <label style={{ marginTop: '15px', display: 'block' }}>
                          WhatsApp Template Name (Chatwoot / Meta)
                          <input
                            type="text"
                            value={templateForm.whatsapp_template_name || ''}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, whatsapp_template_name: e.target.value }))}
                            placeholder="e.g., holiday_greeting"
                            style={{ width: '100%', marginTop: '5px', padding: '8px' }}
                          />
                        </label>
                        <label style={{ marginTop: '15px', display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!templateForm.send_first_name}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, send_first_name: e.target.checked }))}
                            style={{ marginRight: '10px' }}
                          />
                          Send user&apos;s first name as parameter
                        </label>
                      </>
                    )}
                  </div>
                  <div className="modal-actions" style={{ marginTop: '20px' }}>
                    <button onClick={saveMarketingTemplate} className="btn-primary">
                      💾 Save
                    </button>
                    <button
                      onClick={() => {
                        setShowTemplateModal(false);
                        setEditingTemplate(null);
                        setTemplateForm(
                          marketingChannel === 'web_push' ? emptyWebPushTemplateForm() : emptyWhatsappTemplateForm()
                        );
                      }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'scheduled-posts' && (
          <div className="admin-content scheduled-posts-tab">
            <div className="scheduled-posts-header">
              <div className="scheduled-posts-header__intro">
                <h2>Scheduled posts</h2>
                <p className="scheduled-posts-header__hint">
                  Social publish queue in Firestore. Latest 25 load first — use Load all remaining for the full list.
                </p>
              </div>
              <div className="scheduled-posts-header__actions">
                {scheduledPostsHasMore && (
                  <button
                    type="button"
                    className="btn-refresh"
                    onClick={loadScheduledPostsRemaining}
                    disabled={loadingScheduledPostsRemaining || loadingScheduledPosts}
                  >
                    {loadingScheduledPostsRemaining ? 'Loading…' : 'Load all remaining'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => refreshScheduledPosts()}
                  className="btn-refresh"
                  disabled={loadingScheduledPosts || loadingScheduledPostsRemaining}
                >
                  {loadingScheduledPosts ? 'Loading…' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            <div className="scheduled-posts-filters">
              <label className="scheduled-posts-filter">
                <span className="scheduled-posts-filter__label">Status</span>
                <select
                  className="scheduled-posts-filter__control"
                  value={scheduledPostsStatusFilter}
                  onChange={(e) => setScheduledPostsStatusFilter(e.target.value)}
                  disabled={loadingScheduledPosts && scheduledPosts.length === 0}
                  aria-label="Filter by status"
                >
                  <option value="">All statuses</option>
                  {SCHEDULED_POST_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              <label className="scheduled-posts-filter">
                <span className="scheduled-posts-filter__label">Platform</span>
                <select
                  className="scheduled-posts-filter__control"
                  value={scheduledPostsPlatformFilter}
                  onChange={(e) => setScheduledPostsPlatformFilter(e.target.value)}
                  disabled={loadingScheduledPosts && scheduledPosts.length === 0}
                  aria-label="Filter by platform"
                >
                  <option value="">All platforms</option>
                  {SCHEDULED_POST_PLATFORMS.map((platform) => (
                    <option key={platform} value={platform}>
                      {platform}
                    </option>
                  ))}
                </select>
              </label>
              <label className="scheduled-posts-filter scheduled-posts-filter--search">
                <span className="scheduled-posts-filter__label">Search</span>
                <input
                  type="search"
                  className="scheduled-posts-filter__control"
                  placeholder="User, clip, title, error…"
                  value={scheduledPostsSearch}
                  onChange={(e) => setScheduledPostsSearch(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={loadingScheduledPosts && scheduledPosts.length === 0}
                  aria-label="Search scheduled posts"
                />
              </label>
            </div>

            {scheduledPostsError && (
              <div className="scheduled-posts-empty scheduled-posts-empty--error" role="alert">
                {scheduledPostsError}
              </div>
            )}

            {loadingScheduledPosts && scheduledPosts.length === 0 && !scheduledPostsError && (
              <div className="scheduled-posts-empty">Loading scheduled posts…</div>
            )}

            {!loadingScheduledPosts && scheduledPosts.length === 0 && !scheduledPostsError && (
              <div className="scheduled-posts-empty">
                No scheduled posts loaded. Click Refresh to fetch from Firestore.
              </div>
            )}

            {!loadingScheduledPosts && scheduledPosts.length > 0 && filteredScheduledPosts.length === 0 && (
              <div className="scheduled-posts-empty">No posts match the current filters.</div>
            )}

            {filteredScheduledPosts.length > 0 && (
              <div className="scheduled-posts-panel">
                <div className="scheduled-posts-panel__bar" aria-live="polite">
                  <span>
                    Showing <strong>{filteredScheduledPosts.length}</strong>
                    {scheduledPostsHasActiveFilters ? ` of ${scheduledPosts.length}` : ''} posts
                    {scheduledPostsHasMore && (
                      <span className="scheduled-posts-panel__partial"> · latest 25 loaded</span>
                    )}
                  </span>
                  <div className="scheduled-posts-panel__bar-actions">
                    <button
                      type="button"
                      className="btn-small scheduled-post-batch-delete"
                      disabled={batchDeletingScheduledPosts || selectedScheduledPostIds.size === 0}
                      onClick={handleDeleteSelectedScheduledPosts}
                    >
                      {batchDeletingScheduledPosts
                        ? 'Deleting…'
                        : `Delete selected (${selectedScheduledPostIds.size})`}
                    </button>
                  </div>
                </div>
                <div className="scheduled-posts-table-scroll">
                  <table className="scheduled-posts-table">
                    <thead>
                      <tr>
                        <th className="sp-col-select">
                          <input
                            type="checkbox"
                            aria-label="Select all visible scheduled posts"
                            checked={
                              filteredScheduledPosts.length > 0
                              && filteredScheduledPosts.every((post) => selectedScheduledPostIds.has(post.id))
                            }
                            ref={(el) => {
                              if (!el) return;
                              const someSelected = filteredScheduledPosts.some(
                                (post) => selectedScheduledPostIds.has(post.id),
                              );
                              const allSelected = filteredScheduledPosts.length > 0
                                && filteredScheduledPosts.every((post) => selectedScheduledPostIds.has(post.id));
                              el.indeterminate = someSelected && !allSelected;
                            }}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedScheduledPostIds((prev) => {
                                const next = new Set(prev);
                                filteredScheduledPosts.forEach((post) => {
                                  if (!post.id) return;
                                  if (checked) next.add(post.id);
                                  else next.delete(post.id);
                                });
                                return next;
                              });
                            }}
                          />
                        </th>
                        <th className="sp-col-when">Scheduled</th>
                        <th className="sp-col-status">Status</th>
                        <th className="sp-col-platform">Platform</th>
                        <th className="sp-col-user">User</th>
                        <th className="sp-col-clip">Clip</th>
                        <th className="sp-col-title">Title</th>
                        <th className="sp-col-error">Error</th>
                        <th className="sp-col-mode">Mode</th>
                        <th className="sp-col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredScheduledPosts.map((post, index) => {
                        const platformUrl = getScheduledPostPlatformUrl(post);
                        const title = post.metadata?.title || post.clip_filename || '—';
                        const clipLabel = post.file_stem && post.clip_filename
                          ? `${post.file_stem} / ${post.clip_filename}`
                          : (post.clip_filename || post.file_stem || '—');
                        const rowKey = post.id || `${post.user_id || 'user'}-${post.scheduled_at_utc || index}`;
                        return (
                          <tr key={rowKey}>
                            <td className="sp-col-select">
                              <input
                                type="checkbox"
                                aria-label={`Select scheduled post ${title}`}
                                checked={Boolean(post.id && selectedScheduledPostIds.has(post.id))}
                                disabled={!post.id}
                                onChange={() => toggleScheduledPostSelection(post.id)}
                              />
                            </td>
                            <td className="sp-col-when">
                              {post.scheduled_at_utc ? formatDate(post.scheduled_at_utc) : '—'}
                            </td>
                            <td className="sp-col-status">
                              <span className={`scheduled-post-status scheduled-post-status--${post.status || 'pending'}`}>
                                {post.status || 'pending'}
                              </span>
                            </td>
                            <td className="sp-col-platform">{post.platform || 'youtube'}</td>
                            <td className="sp-col-user">
                              <span className="scheduled-post-user-email">{post.user_email || '—'}</span>
                              <code className="scheduled-post-user-id" title={post.user_id || ''}>
                                {post.user_id || '—'}
                              </code>
                            </td>
                            <td className="sp-col-clip" title={clipLabel}>{clipLabel}</td>
                            <td className="sp-col-title" title={title}>{title}</td>
                            <td className="sp-col-error">
                              {post.error ? (
                                <span className="scheduled-post-error">{post.error}</span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="sp-col-mode">{post.mode || '—'}</td>
                            <td className="sp-col-actions">
                              <div className="scheduled-post-actions">
                                {platformUrl ? (
                                  <a
                                    href={platformUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="scheduled-post-link"
                                  >
                                    Open
                                  </a>
                                ) : null}
                                {post.id ? (
                                  <button
                                    type="button"
                                    className="btn-small scheduled-post-delete"
                                    disabled={deletingScheduledPostId === post.id || batchDeletingScheduledPosts}
                                    onClick={() => deleteScheduledPost(post.id)}
                                  >
                                    {deletingScheduledPostId === post.id ? 'Deleting…' : 'Delete'}
                                  </button>
                                ) : null}
                                {!platformUrl && !post.id ? '—' : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'task-queue' && (
          <div className="admin-content webhooks-tab task-queue-tab">
            <div className="section-header">
              <h2>Task queue</h2>
              <div className="webhooks-toolbar">
                <label className="webhooks-search-label">
                  <span className="webhooks-search-label-text">Search</span>
                  <input
                    type="search"
                    className="webhooks-search-input"
                    placeholder="Filter by task id, user, filename, type…"
                    value={taskQueueSearch}
                    onChange={(e) => setTaskQueueSearch(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={!queueData || loadingQueue}
                    aria-label="Filter task queue rows"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => loadQueue()}
                  className="btn-refresh"
                  disabled={loadingQueue}
                >
                  {loadingQueue ? 'Loading…' : '🔄 Refresh'}
                </button>
              </div>
            </div>
            <p className="task-queue-hint">
              One task runs at a time. Rows drop off after success, failure, or exception. The queue survives server restarts.
            </p>
            {!queueData && !loadingQueue && (
              <div className="empty-state">Click Refresh to load the task queue.</div>
            )}
            {loadingQueue && !queueData && <div className="loading-state">Loading task queue…</div>}
            {queueData && (
              <>
                <div className="task-queue-stats" aria-live="polite">
                  <span>
                    <strong>Queued</strong> {queueData.count ?? 0}{' '}
                    {queueData.count === 1 ? 'job' : 'jobs'}
                  </span>
                  {queueData.current_task_id ? (
                    <span className="task-queue-stats-current">
                      <strong>Processing</strong>{' '}
                      <code className="task-queue-stats-id" title={queueData.current_task_id}>
                        {queueData.current_task_id}
                      </code>
                    </span>
                  ) : (
                    <span className="task-queue-stats-idle">No job running</span>
                  )}
                  {taskQueueSearch.trim() && (
                    <span className="task-queue-stats-filter">
                      Showing <strong>{filteredTaskQueueItems.length}</strong> of {(queueData.items || []).length}
                    </span>
                  )}
                </div>
                <div className="webhooks-table-wrapper">
                  <table className="webhooks-table task-queue-table">
                    <thead>
                      <tr>
                        <th className="task-queue-col-pos">#</th>
                        <th className="task-queue-col-task">Task ID</th>
                        <th className="task-queue-col-user">User ID</th>
                        <th className="task-queue-col-email">Email</th>
                        <th className="task-queue-col-type">Type</th>
                        <th className="task-queue-col-file">Filename</th>
                        <th className="task-queue-col-added">Added at</th>
                        <th className="task-queue-col-status">Status</th>
                        <th className="task-queue-col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(queueData.items || []).length === 0 ? (
                        <tr>
                          <td colSpan={9} className="webhooks-filter-empty">
                            Task queue is empty.
                          </td>
                        </tr>
                      ) : filteredTaskQueueItems.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="webhooks-filter-empty">
                            No rows match your search.{' '}
                            <button
                              type="button"
                              className="webhooks-filter-clear"
                              onClick={() => setTaskQueueSearch('')}
                            >
                              Clear search
                            </button>
                          </td>
                        </tr>
                      ) : (
                        filteredTaskQueueItems.map((item, idx) => (
                          <tr
                            key={item.task_id || idx}
                            className={
                              queueData.current_task_id === item.task_id
                                ? 'task-queue-row--active'
                                : undefined
                            }
                          >
                            <td className="task-queue-col-pos">{idx + 1}</td>
                            <td className="task-queue-col-task">
                              <code className="task-queue-mono" title={item.task_id}>
                                {item.task_id}
                              </code>
                            </td>
                            <td className="task-queue-col-user">
                              <code className="task-queue-mono" title={item.user_id}>
                                {item.user_id || '—'}
                              </code>
                            </td>
                            <td className="task-queue-col-email">
                              <span className="task-queue-filename" title={item.user_email || ''}>
                                {item.user_email || '—'}
                              </span>
                            </td>
                            <td className="task-queue-col-type">
                              <code className="task-queue-type-pill">{item.type || '—'}</code>
                            </td>
                            <td className="task-queue-col-file">
                              <span className="task-queue-filename" title={item.filename}>
                                {item.filename || '—'}
                              </span>
                            </td>
                            <td className="task-queue-col-added">{item.added_at || '—'}</td>
                            <td className="task-queue-col-status">
                              {queueData.current_task_id === item.task_id ? (
                                <span className="task-queue-badge task-queue-badge--processing">Processing</span>
                              ) : (
                                <span className="task-queue-badge task-queue-badge--waiting">Waiting</span>
                              )}
                            </td>
                            <td className="task-queue-col-actions">
                              <button
                                type="button"
                                onClick={() => removeFromQueue(item.task_id)}
                                className="task-queue-remove-btn"
                                title="Remove from task queue (cancels job)"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <div className="admin-actions">
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReelsAdminPage;
